/**
 * Interlock API — the ONE centralized data module for the /interlock frontend shell.
 *
 * The Interlock backend (DEVELOPMENT-PLAN.md §6.4) is being built in a PARALLEL parcel,
 * so its exact response shapes may still shift. To keep the shell resilient:
 *
 *   1. Every endpoint has a typed fetch function here.
 *   2. Every response is passed through an *adapter* (also here) that maps the raw
 *      API shape → the view-model the pages actually render. When the real API lands,
 *      the integration coordinator reconciles by editing ONLY the adapters — pages don't
 *      change.
 *   3. Each fetch returns `{ data, fromFixture }`. On any fetch/parse failure the page
 *      falls back to the design-canvas fixture (exported below) and renders a visible
 *      "fixture data" badge so nobody mistakes stub data for live data.
 *
 * Adapters are deliberately defensive: they read several plausible field names for each
 * value (e.g. `refId ?? ref ?? id`) so a minor rename upstream doesn't blank the UI.
 *
 * Project scope: v1 is single-project — PROJECT_ID is a constant ('PRO-302').
 */

import { API_BASE_URL } from './config';
import { withActor, getActorId } from '../utils/interlockActor';
// RS3 (round 10 resilience) — reuse the SAME "session expired" surfacing
// AuthContext already uses on /api/auth/me 401, instead of inventing a second one
// here (or worse, letting a 401 fall into the fixture catch-all like every other
// error).
import { redirectToLogin } from '../contexts/AuthContext';

// Interlock's active project. Historically a hard 'PRO-302' constant (single
// project); now a runtime value driven by the project switcher (multi-project,
// Nick 2026-07-19, decision D1 = flat URLs + switcher).
//
// `export let` (not const) is deliberate: ES-module named exports are LIVE
// BINDINGS, so every consumer that imports `PROJECT_ID` — and every
// `${PROJECT_ID}` / `= PROJECT_ID` default-arg inside this file — automatically
// reads the current value at call/render time once setActiveProjectId reassigns
// it. No consumer captures it at module-init (audited), so this is safe.
//
// Independent of the legacy app's global ProjectContext (its own storage key),
// and DEFAULTS TO 'PRO-302' — so on a single-project deployment the behaviour is
// exactly as before. A switch persists + full-reloads (see ProjectSwitcher), so
// the whole tree re-initialises cleanly with the new project — no stale-cache or
// partial-rescope risk.
const INTERLOCK_PROJECT_KEY = 'interlockProjectId';
export let PROJECT_ID =
  (typeof localStorage !== 'undefined' && localStorage.getItem(INTERLOCK_PROJECT_KEY)) || 'PRO-302';

/** The current Interlock project id (function form for non-live-binding callers). */
export function getActiveProjectId(): string {
  return PROJECT_ID;
}

/** Set + persist the active Interlock project. Callers that need the whole tree
 *  to re-scope should reload after this (ProjectSwitcher does). */
export function setActiveProjectId(id: string): void {
  PROJECT_ID = id;
  if (typeof localStorage !== 'undefined') localStorage.setItem(INTERLOCK_PROJECT_KEY, id);
}

/** Direct-download URL for the rendered ICD Word document (icd-word-export). The
 *  browser streams the .docx and takes its filename from the response's
 *  Content-Disposition. withActor() carries the dev identity hatch; in prod the SSO
 *  cookie rides same-origin. */
export function projectIcdDocxUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/projects/${projectId}/export/icd.docx`)}`;
}

/** Direct-download URL for the rendered SRS Word document. B2 wires the export
 *  button on /interlock/requirements to this path; the route itself is parcel D3's
 *  (GET /api/projects/:id/export/srs.docx — fixed contract, same shape as the ICD
 *  export above). Until D3 merges, the anchor 404s. See {@link projectIcdDocxUrl}. */
export function projectSrsDocxUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/projects/${projectId}/export/srs.docx`)}`;
}

/** Direct-download URL for the rendered RTM Word document (doc round-trip P4 —
 *  GET /api/projects/:id/export/rtm.docx, same shape as the SRS/ICD exports).
 *  The Traceability view on /interlock/requirements wires its export button to
 *  this path. See {@link projectIcdDocxUrl}. */
export function projectRtmDocxUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/projects/${projectId}/export/rtm.docx`)}`;
}

/** Direct-download URL for the rendered FAT/SAT Test Plan Word document (parcel D5,
 *  GET /api/projects/:id/export/fatsat.docx — same contract shape as the SRS/ICD
 *  exports above). Button lives on /interlock/tests. */
export function projectFatSatDocxUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/projects/${projectId}/export/fatsat.docx`)}`;
}

/** Direct-download URL for the rendered Interface Register Word document
 *  (GET /api/projects/:id/export/interface-register.docx — same contract
 *  shape as the SRS/RTM/ICD exports above). Button lives on the Interface
 *  Matrix list view, beside "Export ICD". */
export function projectInterfaceRegisterDocxUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/projects/${projectId}/export/interface-register.docx`)}`;
}

/** Direct-download URL for the rendered FMEA Worksheet Word document
 *  (GET /api/projects/:id/export/fmea-worksheet.docx). Button lives on
 *  /interlock/fmea. */
export function projectFmeaWorksheetDocxUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/projects/${projectId}/export/fmea-worksheet.docx`)}`;
}

/** Direct-download URL for the rendered Risk Register Word document
 *  (GET /api/projects/:id/export/risk-register.docx — the SR-DC-005
 *  deliverable). Button lives on /interlock/risks (Risk Assessment page). */
export function projectRiskRegisterDocxUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/projects/${projectId}/export/risk-register.docx`)}`;
}

/** Direct-download URL for a component's Design Dossier Word document
 *  (GET /api/components/:id/export/dossier.docx — 302 008 015x, the FULL
 *  internal engineering rollup). Button lives on the component spec page. */
export function componentDossierDocxUrl(componentId: string): string {
  return `${API_BASE_URL}${withActor(
    `/components/${encodeURIComponent(componentId)}/export/dossier.docx`
  )}`;
}

/** Direct-download URL for a component's Product Datasheet Word document
 *  (GET /api/components/:id/export/datasheet.docx — 302 011 001x). The
 *  datasheet is a FILTERED PROJECTION of the dossier above, generated from the
 *  same serializer, never authored separately. No freeze/gate state exists on a
 *  component dossier yet, so the generated document stamps itself UNGATED
 *  (banner, page header, footer Rev cell, section 5 and the filename) rather
 *  than the button being blocked. */
export function componentDatasheetDocxUrl(componentId: string): string {
  return `${API_BASE_URL}${withActor(
    `/components/${encodeURIComponent(componentId)}/export/datasheet.docx`
  )}`;
}

// ── Excel exports (spreadsheet deliverables) ────────────────────────────────
// Mounted at /api/exports/excel/* (backed by excelExport.service.ts).
// Bug fix: these used to be called with NO project scope at all, so the
// backend returned data across every project in the system (confirmed
// cross-project data leak). They now require ?projectId=, matching the
// scoping the Docx/JSON exports enforce via their :id path param.

/** Direct-download URL for the FMEA worksheet as an Excel workbook
 *  (GET /api/exports/excel/fmea?projectId=...). Button lives on /interlock/fmea. */
export function excelFmeaExportUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/exports/excel/fmea?projectId=${encodeURIComponent(projectId)}`)}`;
}

/** Direct-download URL for the risk matrix as an Excel workbook
 *  (GET /api/exports/excel/risk-matrix?projectId=...). Button lives on /interlock/risks. */
export function excelRiskMatrixExportUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/exports/excel/risk-matrix?projectId=${encodeURIComponent(projectId)}`)}`;
}

/** Direct-download URL for the RPN Pareto as an Excel workbook
 *  (GET /api/exports/excel/pareto?projectId=...). Button lives on /interlock/fmea. */
export function excelParetoExportUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/exports/excel/pareto?projectId=${encodeURIComponent(projectId)}`)}`;
}

/** Direct-download URL for the component hierarchy as an Excel workbook
 *  (GET /api/exports/excel/components?projectId=...). No dedicated page yet — reserved. */
export function excelComponentsExportUrl(projectId: string = PROJECT_ID): string {
  return `${API_BASE_URL}${withActor(`/exports/excel/components?projectId=${encodeURIComponent(projectId)}`)}`;
}

// ── FAT/SAT plan (doc round-trip P5) ────────────────────────────────────────
// The /interlock/tests page renders the SAME canonical envelope the Word
// export renders (GET …/export/fatsat.json), so page and document can never
// drift. Shape mirrors the backend's FatSatExport (fatsatDocx.service.ts).

export interface FatSatTestRow {
  testId: string;
  title: string;
  method: string;
  criteria: string;
  reqCell: string;
  requirements: { id: string; requirementId: string }[];
  executedAt: string | null;
  executedBy: string | null;
  witnessedBy: string | null;
}

export interface FatSatPlan {
  projectId: string;
  projectName: string | null;
  generatedAt: string;
  fat: FatSatTestRow[];
  sat: FatSatTestRow[];
}

export async function fetchFatSatPlan(projectId: string = PROJECT_ID): Promise<FatSatPlan> {
  const res = await fetch(
    `${API_BASE_URL}${withActor(`/projects/${projectId}/export/fatsat.json`)}`,
    { headers: { 'Content-Type': 'application/json' }, credentials: 'include' }
  );
  if (!res.ok) throw new Error(`FAT/SAT plan fetch failed (${res.status})`);
  return (await res.json()) as FatSatPlan;
}

/** Direct-download URL for one interface's rendered Word datasheet. `idOrRefId` may
 *  be a refId like EXT-09 (the backend resolves it). See {@link projectIcdDocxUrl}. */
export function interfaceDatasheetDocxUrl(idOrRefId: string): string {
  return `${API_BASE_URL}${withActor(`/interfaces/${encodeURIComponent(idOrRefId)}/export/datasheet.docx`)}`;
}

// ============================================================================
// View-models — the shapes the pages render. Stable contract; adapters target these.
// ============================================================================

export type InterfaceRegister = 'int' | 'ext';
export type InterfaceStatus = 'open' | 'std' | 'undef' | 'closed';

// R2-A2b (settle-control parcel) — the RAW backend enum (interlockInterface.routes.ts's
// StatusEnum / Prisma InterfaceStatus), distinct from the lowercase view-model
// `InterfaceStatus` above (open/std/undef/closed). The PATCH body must carry one of
// THESE values, not the view-model one.
export type BackendInterfaceStatus = 'OPEN' | 'STD_CLOSED' | 'IDENTIFIED' | 'CLOSED';

// Deterministic, lossless (normStatus below is the inverse) — lets the edit modal seed
// its status select from the already-normalized `InterfaceDetail.status` without a
// second raw field on the wire.
const STATUS_TO_BACKEND: Record<InterfaceStatus, BackendInterfaceStatus> = {
  open: 'OPEN',
  std: 'STD_CLOSED',
  undef: 'IDENTIFIED',
  closed: 'CLOSED',
};

export function interfaceStatusToBackend(status: InterfaceStatus): BackendInterfaceStatus {
  return STATUS_TO_BACKEND[status] ?? 'OPEN';
}

// Ordered options for the settle-control dropdown, human label sourced from the
// SAME map every other page uses (B7 — see INTERFACE_STATUS_LABEL below).
export const BACKEND_STATUS_OPTIONS: { value: BackendInterfaceStatus; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IDENTIFIED', label: 'Identified' },
  { value: 'STD_CLOSED', label: 'Std-closed' },
  { value: 'CLOSED', label: 'Closed' },
];

export interface InterfaceRow {
  refId: string; // 'EXT-01'
  // Phase 3 (blast radius) — the interface's DB id, needed to call
  // GET /api/blast-radius?kind=INTERFACE&id=<this>. The raw list response always
  // carries it; optional here because callers that only have a refId (Matrix's
  // MatrixCellInterface, System View's connection-derived refs) resolve it via
  // {@link resolveInterfaceIdByRef} instead of assuming this field is populated.
  id?: string;
  register: InterfaceRegister;
  title: string; // 'Ring chassis ↔ Pull chamber'
  type: string; // 'Geometry'
  owners: string[]; // ['QC'] — owner initials, drive the avatar chips (from ownerText — display/informational)
  // R25 (handover F4) — the GOVERNING owner FK (authz), distinct from `owners`
  // above which is free-text (`ownerText`, e.g. the external party "QCells"). null
  // = no internal owner assigned. Drives the register's "unassigned" marker/filter
  // so a half-owned register (13 of 23 unowned in PRO-302) isn't hidden behind an
  // ownerText that looks owned. Optional so fixture rows (which carry no real FK)
  // stay valid — undefined and null both read as "unassigned" (`!row.ownerUserId`).
  ownerUserId?: string | null;
  status: InterfaceStatus;
  neededBy: string; // gate label, e.g. 'Design release'
  gatedDr?: string; // 'DR-01' — gated on a data request
  openTrades?: number; // count of open trades on this interface
  // S3-fix — trades that have converged (or been ruled) but not yet CLOSED.
  // Distinct from openTrades: a CONVERGED trade drops OUT of openTrades the
  // instant it converges, well before "Close & lock value" flips the
  // interface's own status — without this, the register had no signal at all
  // for that in-between state. Optional: an older backend that hasn't shipped
  // the field omits it and the chip simply doesn't render.
  convergedTrades?: number;
  // S3-fix — true when every currently-open trade on this interface is missing
  // a PARTY on one side (so none of them can actually be negotiated). Used to
  // keep 'stalled' honest: a freshly-created trade with no counterparty isn't
  // "in progress" just because openTrades > 0.
  openTradesBlockedOnCounterparty?: boolean;
  riskRef?: string; // 'RSK-03'
  elevated?: boolean; // highest-consequence flag
  // Campaign-2 Theme-2 Wave-3 — a live checker push-back / party signature
  // withdrawal on one of this interface's active trades (shares the backend's
  // computeHasRejection, the SAME derivation Trade Detail's `handshakes[].rejection`
  // uses). Optional — an older backend that hasn't shipped the field yet omits
  // it, and `Boolean(undefined)` reads as false, so the chip simply doesn't render.
  hasRejection?: boolean;
  // R2-C2 (r2b parcel) — a trade on this interface is ESCALATED (awaiting an
  // arbiter's ruling), the SAME derivation systemView.service.ts / interface.
  // service.ts's `listInterfaces` use (`trades.some(t => t.state === 'ESCALATED')`).
  // Optional for the same reason as `hasRejection` above — an older backend omits
  // it and `Boolean(undefined)` reads as false, so the chip/filter simply hides.
  escalated?: boolean;
  // R3 W3 (X10 — register search widening): the raw fields a newcomer's colloquial
  // search term is more likely to hit than the terse refId/title/type. The backend
  // list already returns `crosses`/`spec` on every Interface row; these were being
  // silently dropped by the adapter. `elementA`/`elementB` are the side component
  // names — usually folded into `title`, but kept here explicitly so search still
  // works even when `title` came from a raw API field that didn't include them.
  crosses?: string;
  spec?: string;
  elementA?: string;
  elementB?: string;
  // Ontology O3 — the side COMPONENT IDS (from the raw sideA/sideB includes the
  // list endpoint already returns). The register uses them to resolve each
  // side's containment ("Rotator Motor · in Rotator") client-side against the
  // components list, so re-homed interfaces stay findable. Optional: fixture
  // rows carry no real component ids.
  sideAId?: string;
  sideBId?: string;
  // Loop-3 P6 (dogfood eng #5) — a note (even pinned) was invisible until
  // NotesPanel was opened on the interface detail page; nothing on the
  // register hinted one existed. Fed by a batched `note.groupBy` in
  // interface.service.ts's listInterfaces (no N+1 — one query for the whole
  // register, not one GET /api/notes/stats/... per row). Optional: an older
  // backend that hasn't shipped the field omits it and the badge simply
  // doesn't render (0 reads the same as undefined).
  noteCount?: number;
}

/** INTERLOCK systemClass wave — same 3 values as SystemViewSystemClass, kept
 * as its own alias here since MatrixElement predates the System View type. */
export type SystemClassBand = 'SYSTEM' | 'SUPPORT' | 'EXTERNAL';

/**
 * Directional-N² keying (Nick, 2026-07-23): the internal Nexxis SYSTEM×SYSTEM
 * block is a TRUE directional matrix — row = SOURCE (interface.sideA), column =
 * SINK (interface.sideB), so a pair occupies TWO cells (one per direction). An
 * element is "row-eligible" (can be a row = a SOURCE axis) iff it's a Nexxis
 * SYSTEM part: `systemClass === 'SYSTEM' && org !== 'qc'`. QCells SYSTEM parts,
 * SUPPORT and EXTERNAL are column-only, so any interface touching one of those
 * is one-way and its cell is placed SYSTEM-side-first, irrespective of the
 * authored side order.
 */
export const isRowEligibleEl = (e?: { systemClass: SystemClassBand; org: 'nx' | 'qc' }): boolean =>
  !!e && e.systemClass === 'SYSTEM' && e.org !== 'qc';

export interface MatrixElement {
  name: string; // 'Ring chassis' (may contain \n for wrapping)
  org: 'nx' | 'qc';
  // INTERLOCK systemClass wave — real Component.systemClass (SYSTEM | SUPPORT |
  // EXTERNAL), NOT the old name-matching `org` guess. Rows are SYSTEM-only;
  // columns band into three groups keyed on this field. `org` is kept
  // alongside for now (legend/back-compat callers) but systemClass is the
  // field driving row/column/banding logic from this wave on.
  systemClass: SystemClassBand;
  index: number; // 0..13
  // Curated part thumbnail URL (from the backend matrix endpoint) or null — the
  // N2 matrix headers show it beside the label, same image as the system view.
  thumbnailUrl?: string | null;
  // Ontology O3 — containment context from the enriched backend matrix
  // endpoint. All optional: the design fixture (Shape A) and any pre-O3
  // backend omit them, and the majors aggregation then degrades to a no-op
  // (see aggregateMatrixToMajors).
  id?: string; // component id (live Shape B only)
  parentName?: string; // immediate parent's name — the register-style ' · in X' sub-label
  rollupId?: string; // family axis id: nearest self-or-ancestor MAJOR, else self
  rollupName?: string;
}

// Model A Phase 1 — the 8 crossing disciplines carried on each interface's
// `crossings[]` (see @fmeastudio/shared `CrossingDiscipline`; duplicated here
// as a plain adapter type so the matrix view-model has no runtime dep on the
// shared DTO, per this file's local-adapter convention).
export type CrossingDiscipline =
  | 'MECH'
  | 'ELEC'
  | 'DATA'
  | 'FLUID'
  | 'SEALING'
  | 'THERMAL'
  | 'HUMAN'
  | 'OPTICAL';

// Crossing status (adapter mirror of @fmeastudio/shared `CrossingStatus`).
// DESCOPED = a channel deliberately cancelled/removed from scope — rendered as
// killed (struck+greyed), excluded from open/settled tallies.
export type CrossingStatus = 'OPEN' | 'HANDSHAKED' | 'DESCOPED';

/** Model A Phase 1 — one demand/supply/agreed line item on an interface (the
 * matrix cell payload + interface detail now return `crossings[]`). Frontend
 * adapter mirror of the backend read DTO. */
export interface MatrixCellCrossing {
  id: string;
  refId: string; // e.g. "EXT-01.1"
  discipline: CrossingDiscipline;
  demandText: string | null;
  supplyText: string | null;
  agreedText: string | null;
  status: CrossingStatus;
  sortOrder: number;
  // provenance — 'INJECTED' backfilled crossings render red+bold until reviewed
  reviewState: 'INJECTED' | 'REVIEWED';
  hasTrade: boolean;
  tradeState: string | null;
  // Model A handshake — the attached trade's refId (null when hasTrade is false),
  // so "View negotiation →" deep-links straight to the trade/handshake page.
  tradeRefId: string | null;
}

export interface MatrixCellInterface {
  refId: string;
  register: InterfaceRegister;
  status: InterfaceStatus;
  trade?: boolean; // has an open trade (contested)
  elevated?: boolean;
  gated?: boolean;
  // Model A Phase 1 — the interface's crossings (one per demand/supply/agreed
  // line). Populated cells hold exactly ONE interface under Model A, so this
  // is that interface's full crossing set — drives the cell coverage-strip
  // dots and the quick-view's discipline sections.
  crossings?: MatrixCellCrossing[];
  // Ontology O3 — the TRUE endpoints ('Rotator Motor', not the 'Rotator'
  // family axis it aggregates under at majors level). Set by the live-shape
  // adapter; the quick-view shows them as a sub-label whenever they differ
  // from the cell's axis names.
  sideAName?: string;
  sideBName?: string;
}

export interface MatrixModel {
  elements: MatrixElement[]; // 14, ordered — Nexxis (0-8) then QCells (9-13)
  // Directional-N² key `${source}-${sink}`: internal SYSTEM×SYSTEM cells key by
  // authored side order (source=sideA → sink=sideB), so a pair has TWO cells
  // (A→B and B→A). system↔external cells key SYSTEM-side-first (one-way).
  cells: Record<string, MatrixCellInterface[]>;
}

// R4 W1B — 'countersign' is the trade-level CHECKER countersign turn (the new
// backend parcel's POST /trades/:id/checkers handshake), rendered in its own home
// group distinct from an ordinary party 'handshake'. NOT to be confused with the
// pre-existing W2-A workshop-session review COUNTERSIGN turns, which stay 'review'
// (see normalizeWaitingKind's entityType disambiguation below).
// Waiting-on-me design fix — 'coverage-gap' / 'evidence-gap' / 'gate-fail' are
// ENGINEER-only debt SUMMARIES (getEngineerDebtItems in projectHealth.service.ts):
// live-computed counts (uncovered requirements, VERIFIED-without-evidence
// requirements, failing Design Release predicates), not per-entity turns. They
// fix a false "all clear" — an engineer's court used to be blind to this debt
// entirely. PM behaviour is unaffected (backend gates these on role).
// 'fmea-risk' — PM+ENGINEER-visible (getFmeaRiskItems in
// projectHealth.service.ts): unmitigated CRITICAL/HIGH FMEA failure modes,
// per-entity (one row per failure mode, unlike the debt summaries above).
// Read-only surfacing of existing FMEA-lane data — never creates or reads an
// ExternalRisk row; the two-lane FMEA/Risk-Register split is untouched.
export type WaitingKind =
  | 'handshake'
  | 'trade'
  | 'data-request'
  | 'review'
  | 'score'
  | 'ruling'
  | 'countersign'
  | 'coverage-gap'
  | 'evidence-gap'
  | 'gate-fail'
  | 'fmea-risk'
  // 'external-risk' — PM/MANAGER/ADMIN-visible (getExternalRiskItems in
  // projectHealth.service.ts): unowned CRITICAL Risk-Assessment-register rows,
  // per-entity. Read-only surfacing so an orphaned Critical program risk can't
  // sit un-triaged behind a clean queue (L8 audit).
  | 'external-risk';

export interface WaitingItem {
  id: string;
  kind: WaitingKind;
  refId: string; // 'EXT-09-T1' or 'EXT-02'
  title: string;
  subtitle: string;
  whoseTurn?: string; // 'your turn'
  href: string; // deep link within /interlock
  externalParty?: string; // set on waitingOnExternal rows — the counterparty name
  // V4 — set on `kind: 'score'` rows once the backend decorates RiskReviewCampaign
  // turns (getWaitingOn); read defensively, the deep link falls back to refId.
  campaignId?: string;
  // Waiting-on-me pagination/sort fix — ISO timestamp, when the backend has
  // one (every turn-ledger + debt-summary row does). Powers the home page's
  // "oldest/newest" sort; optional because design-era fixture rows may not
  // carry one, in which case that row just sorts as if it were "now".
  createdAt?: string;
}

/** GET /api/me/waiting-on real shape: { waitingOnMe, waitingOnExternal, counts }. */
export interface WaitingOnData {
  mine: WaitingItem[];
  external: WaitingItem[];
}

/** One row of `getProjectHealth().oldestUnresolvedTurns` (projectHealth.service.ts). */
export interface HealthTurnRow {
  kind: string; // 'HANDSHAKE' | 'ACKNOWLEDGE' | 'CHASE_DR' | 'SCORE' | ...
  trade: { refId: string; title: string } | null;
  externalParty: string | null;
  // L2 addition (interlock/fix-l2): forUserId/forUserName — see the ONE permitted
  // backend touch in projectHealth.service.ts (additive fields only).
  forUserId?: string | null;
  forUserName?: string | null;
  ageDays: number;
  createdAt: string;
}

/**
 * GET /api/projects/:id/health real shape (verified live 2026-07-11 against the
 * sv-gate-pg gate DB): `{ projectId, interfacesByStatus, tradesByState,
 * unconvergedTrades, openDataRequests, oldestUnresolvedTurns }` — NOT the
 * `{interfaceCount,open,gated,withOpenTrades,elevated}` shape this adapter used to
 * assume (that never matched anything live; every `pick()` silently fell through
 * to 0 and the health strip would have rendered a confident, wrong "0 of everything").
 */
export interface ProjectHealth {
  projectId: string;
  interfacesByStatus: Record<string, number>;
  tradesByState: Record<string, number>;
  openDataRequestCount: number;
  oldestUnresolvedTurns: HealthTurnRow[];
}

/**
 * Campaign-2 Theme-2 Wave-5 (audit finding #5) — one row of the project-wide
 * escalations register: `GET /api/projects/:id/escalations` →
 * `{ escalations: EscalationRow[] }` (trade.service.ts listEscalatedTrades). An
 * escalation is ALWAYS awaiting a ruling by construction (the list is ESCALATED
 * trades only — a ruled trade moves to RESOLVED_BY_RULING and drops out), so
 * `awaitingRuling` is carried through rather than re-derived client-side.
 */
export interface EscalationRow {
  id: string;
  refId: string; // trade refId, e.g. 'EXT-09-T1'
  title: string;
  interfaceId: string;
  interfaceRefId: string | null; // 'EXT-09'
  interfaceTitle: string | null; // 'Ring chassis ↔ Pull chamber'
  reason: string | null;
  escalatedByUserId: string | null;
  escalatedByName: string | null;
  escalatedAt: string; // ISO
  ageMs: number;
  awaitingRuling: boolean;
}

export interface Fetched<T> {
  data: T;
  fromFixture: boolean;
  /** RS5 (round 10 resilience) — set when `data` is the HONEST-empty/failure shape
   * (not fixture, not a real result) because the request genuinely failed in an
   * environment where fixture fallback is disabled (PROD). Lets a page distinguish
   * "the project really has none of these yet" from "couldn't load — try again"
   * without ever having to render fixture data to tell the difference. Undefined/
   * false on every pre-existing call site (a real success, or a DEV fixture). */
  error?: boolean;
}

// ============================================================================
// Low-level fetch — returns parsed JSON or throws.
// ============================================================================

async function getJson<T>(path: string): Promise<T> {
  // API_BASE_URL already ends with '/api'; callers pass the sub-path after that.
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    // RS3 — carry the HTTP status (esp. 401) the same way requestJson does, so
    // callers can tell an expired session apart from a generic 5xx instead of
    // treating every non-2xx the same.
    let code: string | undefined;
    let body: unknown;
    try {
      body = await res.json();
      code = (body as Record<string, unknown> | undefined)?.code as string | undefined;
    } catch {
      /* non-JSON error body */
    }
    throw new InterlockApiError(`Interlock API ${path} → ${res.status}`, res.status, code, body);
  }
  return (await res.json()) as T;
}

// Unwrap the common `{ data: T }` envelope (see api/client.ts) or take the raw body.
function unwrap<T = unknown>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

function asArray(body: unknown): unknown[] {
  const d = unwrap(body);
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object') {
    // tolerate { interfaces: [...] } / { items: [...] } / { rows: [...] }
    for (const key of ['interfaces', 'items', 'rows', 'results']) {
      const v = (d as Record<string, unknown>)[key];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

const pick = <T = unknown>(o: Record<string, unknown>, ...keys: string[]): T | undefined => {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k] as T;
  }
  return undefined;
};

// ============================================================================
// Adapters — raw API shape → view-model. Reconcile HERE when the real API lands.
// ============================================================================

/** GET /api/projects/:id/interfaces → InterfaceRow[] */
export function adaptInterfaces(body: unknown): InterfaceRow[] {
  return asArray(body).map((raw) => {
    const o = raw as Record<string, unknown>;
    const register = (pick<string>(o, 'register', 'reg') ?? 'int').toLowerCase();
    const sideName = (side: unknown): string | undefined =>
      side && typeof side === 'object'
        ? ((side as Record<string, unknown>).componentName as string | undefined)
        : undefined;
    const elementA = pick<string>(o, 'elementA', 'aName', 'a') ?? sideName(o.sideA);
    const elementB = pick<string>(o, 'elementB', 'bName', 'b') ?? sideName(o.sideB);
    const title =
      pick<string>(o, 'title', 'ab', 'name') ??
      (elementA && elementB ? `${elementA} ↔ ${elementB}` : '—');
    const ownersRaw = pick<unknown>(o, 'owners', 'own', 'ownerInitials');
    let owners: string[] = Array.isArray(ownersRaw)
      ? ownersRaw.map((w) =>
          typeof w === 'string' ? w : String((w as Record<string, unknown>)?.initials ?? w)
        )
      : [];
    if (!owners.length) {
      // Live API shape: ownerText is a display string like "Shijin/Alex" or "QCells".
      const ownerText = pick<string>(o, 'ownerText');
      if (ownerText)
        owners = ownerText
          .split(/[/,&]+/)
          .map((s) => s.trim())
          .filter(Boolean);
    }
    const trades = pick<number>(o, 'openTrades', 'openTradeCount', 'trades');
    const convergedTrades = pick<number>(o, 'convergedTrades');
    return {
      refId: pick<string>(o, 'refId', 'ref', 'id') ?? '—',
      id: pick<string>(o, 'id'),
      register: register === 'ext' ? 'ext' : 'int',
      title,
      type: pick<string>(o, 'type', 'kind') ?? '—',
      owners,
      ownerUserId: pick<string>(o, 'ownerUserId') ?? null, // R25 (handover F4) — governing owner FK; null = unassigned
      status: normStatus(pick<string>(o, 'status', 'st')),
      neededBy: pick<string>(o, 'neededBy', 'neededByGate', 'gate') ?? '—',
      gatedDr: pick<string>(o, 'gatedDr', 'gatedDataRequest', 'gated'),
      openTrades: typeof trades === 'number' ? trades : undefined,
      convergedTrades: typeof convergedTrades === 'number' ? convergedTrades : undefined,
      openTradesBlockedOnCounterparty: Boolean(pick<boolean>(o, 'openTradesBlockedOnCounterparty')),
      riskRef: pick<string>(o, 'riskRef', 'topRiskRef', 'risk'),
      elevated: Boolean(pick<boolean>(o, 'elevated')),
      hasRejection: Boolean(pick<boolean>(o, 'hasRejection')),
      escalated: Boolean(pick<boolean>(o, 'escalated')),
      crosses: pick<string>(o, 'crosses'),
      spec: pick<string>(o, 'spec'),
      elementA,
      elementB,
      // Ontology O3 — side component ids for the register's containment sub-labels.
      sideAId:
        pick<string>(o, 'sideAId') ??
        (o.sideA && typeof o.sideA === 'object'
          ? ((o.sideA as Record<string, unknown>).id as string | undefined)
          : undefined),
      sideBId:
        pick<string>(o, 'sideBId') ??
        (o.sideB && typeof o.sideB === 'object'
          ? ((o.sideB as Record<string, unknown>).id as string | undefined)
          : undefined),
      // Loop-3 P6 — note-existence badge count (see InterfaceRow.noteCount).
      noteCount: pick<number>(o, 'noteCount'),
    };
  });
}

function normStatus(s: string | undefined): InterfaceStatus {
  const v = (s ?? '').toLowerCase();
  if (v.startsWith('std')) return 'std';
  if (v === 'undef' || v === 'identified') return 'undef';
  if (v === 'closed') return 'closed';
  return 'open';
}

// B7 — the ONE shared InterfaceStatus → human label map. Every page/component that
// renders an interface's status MUST go through this instead of printing the raw
// view-model value (e.g. the literal string "undef") — see ComponentDrawer B7 fix.
const INTERFACE_STATUS_LABEL: Record<InterfaceStatus, string> = {
  open: 'Open',
  std: 'Std-closed',
  undef: 'Identified',
  closed: 'Closed',
};

export function interfaceStatusLabel(status: InterfaceStatus): string {
  return INTERFACE_STATUS_LABEL[status] ?? status;
}

/**
 * Canonical ICD Rev-2 element order + org (Nexxis block 0-8, QCells block 9-13).
 * The backend matrix endpoint returns elements as bare {id, name} with no org or
 * ordering — this is ICD presentation metadata, sourced from the backfill dataset.
 */
const ICD_ELEMENT_ORDER: { match: string; org: 'nx' | 'qc' }[] = [
  { match: 'cleaning carriage', org: 'nx' },
  { match: 'rotator', org: 'nx' },
  { match: 'ring chassis', org: 'nx' },
  { match: 'tether', org: 'nx' },
  { match: 'juicebox', org: 'nx' },
  { match: 'operator', org: 'nx' },
  { match: 'launch tube', org: 'nx' },
  { match: 'simulation rig', org: 'nx' },
  { match: 'camera', org: 'nx' },
  { match: 'pull chamber', org: 'qc' },
  { match: 'seed chuck', org: 'qc' },
  { match: 'bottom features', org: 'qc' },
  { match: 'site facilities', org: 'qc' },
  { match: 'qcells operations', org: 'qc' },
];

function icdSlot(name: string): number {
  const n = name.toLowerCase();
  const i = ICD_ELEMENT_ORDER.findIndex((e) => n.startsWith(e.match));
  return i === -1 ? ICD_ELEMENT_ORDER.length : i;
}

/**
 * INTERLOCK systemClass wave — name-matching FALLBACK only, used when a
 * live matrix element has no `systemClass` field of its own (older backend,
 * or the design fixture). The real backend (getMatrix) now sends the actual
 * Component.systemClass, which always wins when present — see
 * `resolveSystemClass` below. NOT the same split as ICD_ELEMENT_ORDER's org
 * (Nexxis/QCells DESIGN ownership): Operator is Nexxis-owned but EXTERNAL in
 * systemClass terms (a person, not shipped hardware); the Simulation rig is
 * Nexxis-owned but SUPPORT (FAT test tooling, never shipped).
 */
const ICD_SYSTEM_CLASS_ORDER: { match: string; band: SystemClassBand }[] = [
  { match: 'cleaning carriage', band: 'SYSTEM' },
  { match: 'rotator', band: 'SYSTEM' },
  { match: 'ring chassis', band: 'SYSTEM' },
  { match: 'tether', band: 'SYSTEM' },
  { match: 'juicebox', band: 'SYSTEM' },
  { match: 'launch tube', band: 'SYSTEM' },
  { match: 'camera', band: 'SYSTEM' },
  { match: 'operator', band: 'EXTERNAL' },
  { match: 'simulation rig', band: 'SUPPORT' },
  { match: 'simulation test rig', band: 'SUPPORT' },
  { match: 'pull chamber', band: 'EXTERNAL' },
  { match: 'seed chuck', band: 'EXTERNAL' },
  { match: 'bottom features', band: 'EXTERNAL' },
  { match: 'site facilities', band: 'EXTERNAL' },
  { match: 'qcells operations', band: 'EXTERNAL' },
  { match: 'qcells boundary', band: 'EXTERNAL' },
];

function systemClassBandForName(name: string): SystemClassBand | undefined {
  const n = name.toLowerCase();
  return ICD_SYSTEM_CLASS_ORDER.find((e) => n.startsWith(e.match))?.band;
}

/** Resolve one raw matrix-element's systemClass: prefer the real API field
 * (getMatrix now sends it), else fall back to name → rollup name → parent
 * name matching against the classification list above, else SYSTEM. */
function resolveSystemClass(e: Record<string, unknown>): SystemClassBand {
  const raw = pick<string>(e, 'systemClass');
  if (raw) {
    const v = raw.toUpperCase();
    if (v === 'SYSTEM' || v === 'SUPPORT' || v === 'EXTERNAL') return v;
  }
  return (
    systemClassBandForName(pick<string>(e, 'name', 'label') ?? '') ??
    systemClassBandForName(pick<string>(e, 'rollupName') ?? '') ??
    systemClassBandForName(pick<string>(e, 'parentName') ?? '') ??
    'SYSTEM'
  );
}

/**
 * Resolve a component/node name to its ICD org ('nx' | 'qc') by matching against the
 * canonical ICD Rev-2 element list. Used by System View to paint the QCells client
 * boundary tint + fence, since the system-view API has no org field of its own.
 * Falls back to 'nx' when the name doesn't match any known ICD element (e.g. PART-level
 * nodes nested under a matched SUBSYSTEM should use the parent's org instead — callers
 * should prefer the ancestor chain when available).
 */
export function orgForName(name: string): 'nx' | 'qc' {
  const slot = icdSlot(name);
  return ICD_ELEMENT_ORDER[slot]?.org ?? 'nx';
}

/** Same as {@link orgForName} but returns `undefined` when the name doesn't match any
 * known ICD element, so callers (e.g. System View) can inherit the org from an ancestor
 * node instead of defaulting to 'nx'. */
export function orgForNameOrNull(name: string): 'nx' | 'qc' | undefined {
  const slot = icdSlot(name);
  return ICD_ELEMENT_ORDER[slot]?.org;
}

// Model A Phase 1 — map the read-API `crossings[]` (raw JSON rows) onto the
// adapter shape. Reads nullable text fields directly (pick() skips nulls, but
// demand/supply/agreed are legitimately null on an OPEN crossing). Shared by
// adaptMatrix (cell payload) and adaptInterfaceDetail (interface-detail payload)
// — both return the SAME crossing shape.
const CROSSING_DISCIPLINES = new Set<CrossingDiscipline>([
  'MECH',
  'ELEC',
  'DATA',
  'FLUID',
  'SEALING',
  'THERMAL',
  'HUMAN',
  'OPTICAL',
]);
function adaptCrossingStatus(raw: string | undefined): CrossingStatus {
  return raw === 'HANDSHAKED' || raw === 'DESCOPED' ? raw : 'OPEN';
}

function adaptCrossingRows(raw: unknown): MatrixCellCrossing[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((r) => {
    const x = r as Record<string, unknown>;
    const disc = String(pick<string>(x, 'discipline') ?? 'MECH') as CrossingDiscipline;
    return {
      id: String(pick<string>(x, 'id') ?? ''),
      refId: String(pick<string>(x, 'refId') ?? ''),
      discipline: CROSSING_DISCIPLINES.has(disc) ? disc : 'MECH',
      demandText: (x.demandText ?? null) as string | null,
      supplyText: (x.supplyText ?? null) as string | null,
      agreedText: (x.agreedText ?? null) as string | null,
      status: adaptCrossingStatus(pick<string>(x, 'status')),
      sortOrder: Number(pick<number>(x, 'sortOrder') ?? 0),
      reviewState: pick<string>(x, 'reviewState') === 'INJECTED' ? 'INJECTED' : 'REVIEWED',
      hasTrade: Boolean(pick<boolean>(x, 'hasTrade')),
      tradeState: (x.tradeState ?? null) as string | null,
      tradeRefId: (x.tradeRefId ?? null) as string | null,
    };
  });
}

/** GET /api/projects/:id/interfaces/matrix → MatrixModel */
export function adaptMatrix(body: unknown): MatrixModel {
  const d = unwrap<Record<string, unknown>>(body);
  const rawEls = (Array.isArray(d?.elements) ? d.elements : []) as Record<string, unknown>[];

  // Reorder into canonical ICD order (Nexxis block, then QCells block).
  //
  // Slot resolution walks the containment context when the element's own name
  // isn't a canonical ICD element (O1 sub-components like 'Traction Wheels /
  // Pads'): name → roll-up major → immediate parent. Without the fallback, an
  // unmatched sub-component sorted AFTER the QCells block while still counting
  // as Nexxis — and since the page derives the quadrant boundary from the
  // Nexxis COUNT, that dragged the boundary one column right and put Pull
  // chamber inside the Nexxis quadrant in the all-components view. The sort
  // also puts org blocks first so nx/qc are contiguous even if an org hint
  // ever disagrees with the slot (count-based boundary stays truthful).
  const ordered = rawEls
    .map((e, i) => {
      const nameSlot = icdSlot(pick<string>(e, 'name', 'label') ?? '');
      const rollupSlot = icdSlot(pick<string>(e, 'rollupName') ?? '');
      const slot =
        nameSlot < ICD_ELEMENT_ORDER.length
          ? nameSlot
          : rollupSlot < ICD_ELEMENT_ORDER.length
            ? rollupSlot
            : icdSlot(pick<string>(e, 'parentName') ?? '');
      const orgHint = pick<string>(e, 'org', 'owner');
      const org: 'nx' | 'qc' = orgHint
        ? orgHint.toLowerCase().startsWith('q')
          ? 'qc'
          : 'nx'
        : (ICD_ELEMENT_ORDER[slot]?.org ?? 'nx');
      const systemClass = resolveSystemClass(e);
      return { e, apiIndex: i, slot, org, systemClass };
    })
    .sort(
      (a, b) =>
        (a.org === 'qc' ? 1 : 0) - (b.org === 'qc' ? 1 : 0) ||
        a.slot - b.slot ||
        a.apiIndex - b.apiIndex
    );
  const idToIndex = new Map<string, number>();
  const elements: MatrixElement[] = ordered.map(({ e, org, systemClass }, i) => {
    const id = pick<string>(e, 'id');
    if (id) idToIndex.set(id, i);
    return {
      name: pick<string>(e, 'name', 'label') ?? `El ${i}`,
      org,
      systemClass,
      index: i,
      // Ontology O3 — containment context (absent on fixture Shape A rows).
      id,
      parentName: pick<string>(e, 'parentName'),
      rollupId: pick<string>(e, 'rollupId'),
      rollupName: pick<string>(e, 'rollupName'),
      thumbnailUrl: pick<string>(e, 'thumbnailUrl') ?? null,
    };
  });

  // Model A Phase 1 — module-level adaptCrossingRows() maps the read-API
  // `crossings[]` (shared with adaptInterfaceDetail).
  const adaptCrossings = adaptCrossingRows;

  const cells: Record<string, MatrixCellInterface[]> = {};
  const rawCells = (Array.isArray(d?.cells) ? d.cells : []) as Record<string, unknown>[];
  // Directional-N² keying — order the two endpoints (aIdx = authored sideA =
  // SOURCE, bIdx = authored sideB = SINK) into [firstIdx, secondIdx] = the cell
  // key `first-second`:
  //   • both row-eligible (internal SYSTEM×SYSTEM)  → preserve source→sink, so
  //     sideA=Rotator/sideB=Chassis lands at (Rotator, Chassis) and the reverse
  //     record lands at the MIRROR cell (Chassis, Rotator).
  //   • exactly one row-eligible (system↔external/QCells/support) → SYSTEM side
  //     first, so the single one-way cell always lands at (system-row, ext-col)
  //     regardless of which side was authored as A.
  //   • neither row-eligible → never rendered (no row exists); key as authored.
  const orderEndpoints = (aIdx: number, bIdx: number): [number, number] => {
    const aRow = isRowEligibleEl(elements[aIdx]);
    const bRow = isRowEligibleEl(elements[bIdx]);
    if (aRow && bRow) return [aIdx, bIdx]; // internal: source → sink
    if (bRow && !aRow) return [bIdx, aIdx]; // external authored ext→system: system first
    return [aIdx, bIdx]; // system→external, or neither (unrendered)
  };
  const pushCell = (aIdx: number, bIdx: number, iface: MatrixCellInterface) => {
    const [firstIdx, secondIdx] = orderEndpoints(aIdx, bIdx);
    const key = `${firstIdx}-${secondIdx}`;
    // Pair sideAName/sideBName to the KEY order (first, second). Downstream the
    // quick-view labels its a/b endpoints from key order and the majors rollup
    // re-derives direction from these, so they must track the key, not the raw
    // authored order. Shape-A design fixtures carry no side names — leave as-is.
    const cell =
      !iface.sideAName && !iface.sideBName
        ? iface
        : { ...iface, sideAName: elements[firstIdx]?.name, sideBName: elements[secondIdx]?.name };
    (cells[key] ??= []).push(cell);
  };
  for (const c of rawCells) {
    // Shape A (design-era): { r, c, interfaces: [...] }
    const r = pick<number>(c, 'r', 'row');
    const col = pick<number>(c, 'c', 'col');
    if (r !== undefined && col !== undefined) {
      const list = (Array.isArray(c.interfaces) ? c.interfaces : []) as Record<string, unknown>[];
      for (const f of list) {
        pushCell(r, col, {
          refId: pick<string>(f, 'refId', 'ref', 'id') ?? '—',
          register:
            (pick<string>(f, 'register', 'reg') ?? 'int').toLowerCase() === 'ext' ? 'ext' : 'int',
          status: normStatus(pick<string>(f, 'status', 'st')),
          trade: Boolean(pick<boolean>(f, 'trade', 'contested')),
          elevated: Boolean(pick<boolean>(f, 'elevated')),
          gated: Boolean(pick<unknown>(f, 'gated', 'gatedDr')),
          crossings: adaptCrossings(f.crossings),
        });
      }
      continue;
    }
    // Shape B (live API): one flat row per interface with sideAId/sideBId component ids.
    const aId = pick<string>(c, 'sideAId');
    const bId = pick<string>(c, 'sideBId');
    if (!aId || !bId) continue;
    const ai = idToIndex.get(aId);
    const bi = idToIndex.get(bId);
    if (ai === undefined || bi === undefined || ai === bi) continue;
    pushCell(ai, bi, {
      refId: pick<string>(c, 'refId') ?? '—',
      register: (pick<string>(c, 'register') ?? 'int').toLowerCase() === 'ext' ? 'ext' : 'int',
      status: normStatus(pick<string>(c, 'status')),
      trade: Boolean(pick<boolean>(c, 'hot')), // hot = has NEGOTIATING/ESCALATED trade
      elevated: Boolean(pick<boolean>(c, 'elevated')),
      gated: Boolean(pick<unknown>(c, 'gated', 'gatedDr')),
      // Ontology O3 — TRUE endpoints, kept regardless of what axis the
      // interface later aggregates under (quick-view sub-labels).
      sideAName: elements[ai]?.name,
      sideBName: elements[bi]?.name,
      crossings: adaptCrossings(c.crossings),
    });
  }
  return { elements, cells };
}

/**
 * Ontology O3 — roll the flat matrix model up to the "majors" level.
 *
 * Axis rule (mirrors the backend's rollup fields): every element joins the
 * axis of its NEAREST self-or-ancestor curated major (Component.
 * isMajorComponent); an element with no major in its chain keeps its own
 * axis. A cell then bundles ALL interfaces terminating anywhere in each
 * side's family (self ∪ descendants) — e.g. EXT-09 (Traction Wheels ↔ Pull
 * chamber) lands in the Ring Chassis × Pull chamber cell next to EXT-01.
 * Each bundled interface keeps its TRUE endpoints in sideAName/sideBName.
 *
 * Interfaces whose two sides roll up to the SAME family (intra-family, e.g.
 * a future Rotator ↔ Rotator Motor interface) have no off-diagonal cell at
 * this level; they're counted in `hiddenIntraFamily` so the page can say so
 * honestly (the "all components" level shows them).
 *
 * Degrades to a no-op (same model back, hiddenIntraFamily 0) when the model
 * carries no roll-up context — fixture data or a pre-O3 backend.
 */
export function aggregateMatrixToMajors(model: MatrixModel): {
  model: MatrixModel;
  hiddenIntraFamily: number;
} {
  const els = model.elements;
  if (!els.some((e) => e.rollupId && e.id)) return { model, hiddenIntraFamily: 0 };

  // Distinct roll-up axes, in the same canonical ICD order adaptMatrix uses.
  // Each axis inherits its org from the first member element — adaptMatrix has
  // already resolved element orgs through the containment chain, so this stays
  // correct even when the axis (major) name matches no canonical ICD element.
  const axisByRollupId = new Map<
    string,
    {
      id: string;
      name: string;
      org: 'nx' | 'qc';
      systemClass: SystemClassBand;
      thumbnailUrl?: string | null;
    }
  >();
  for (const e of els) {
    const rid = e.rollupId ?? e.id ?? e.name;
    const axisName = e.rollupName ?? e.name;
    if (!axisByRollupId.has(rid))
      axisByRollupId.set(rid, {
        id: rid,
        name: axisName,
        org: e.org,
        // INTERLOCK systemClass wave — same inheritance rule as org: the axis
        // takes its systemClass from the first member (the family's own
        // component, since a MAJOR is always self-or-ancestor of its members).
        systemClass: e.systemClass,
        thumbnailUrl: e.thumbnailUrl ?? null,
      });
    // Prefer the MAJOR's own thumbnail (the member whose name is the axis name)
    // over the first member's, so e.g. a "Ring Chassis" axis shows the chassis
    // image, not a sub-component's.
    const axis = axisByRollupId.get(rid)!;
    if (e.name === axisName && e.thumbnailUrl) axis.thumbnailUrl = e.thumbnailUrl;
  }
  // Same org-block-first sort as adaptMatrix: the page's quadrant boundary is
  // count-derived, so nx/qc contiguity is the invariant an axis order must keep.
  const orderedAxes = [...axisByRollupId.values()]
    .map((a, i) => ({ ...a, apiIndex: i, slot: icdSlot(a.name) }))
    .sort(
      (a, b) =>
        (a.org === 'qc' ? 1 : 0) - (b.org === 'qc' ? 1 : 0) ||
        a.slot - b.slot ||
        a.name.localeCompare(b.name) ||
        a.apiIndex - b.apiIndex
    );
  const axisIndexByRollupId = new Map<string, number>();
  const elements: MatrixElement[] = orderedAxes.map((a, i) => {
    axisIndexByRollupId.set(a.id, i);
    return {
      name: a.name,
      org: a.org,
      systemClass: a.systemClass,
      index: i,
      id: a.id,
      rollupId: a.id,
      rollupName: a.name,
      thumbnailUrl: a.thumbnailUrl ?? null,
    };
  });

  // Element index (flat model) -> axis index (majors model).
  const axisIndexByElIndex = new Map<number, number>();
  for (const e of els) {
    const rid = e.rollupId ?? e.id ?? e.name;
    const ai = axisIndexByRollupId.get(rid);
    if (ai !== undefined) axisIndexByElIndex.set(e.index, ai);
  }

  const cells: Record<string, MatrixCellInterface[]> = {};
  let hiddenIntraFamily = 0;
  for (const [key, list] of Object.entries(model.cells)) {
    const [r, c] = key.split('-').map(Number);
    const ar = axisIndexByElIndex.get(r);
    const ac = axisIndexByElIndex.get(c);
    if (ar === undefined || ac === undefined) continue;
    for (const f of list) {
      if (ar === ac) {
        hiddenIntraFamily += 1; // both sides in one family — no off-diagonal cell here
        continue;
      }
      // Directional at family level too — the flat key is already source-first
      // (internal) / system-first (external), so carry that ordering across the
      // rollup rather than collapsing to min/max. Both families row-eligible →
      // keep (source-family → sink-family); otherwise place the SYSTEM family
      // first so a system↔external pair stays a single one-way (system-row,
      // ext-col) cell.
      const rFamRow = isRowEligibleEl(elements[ar]);
      const cFamRow = isRowEligibleEl(elements[ac]);
      const [firstFam, secondFam] =
        rFamRow && cFamRow
          ? [ar, ac] // internal: source-family → sink-family
          : cFamRow && !rFamRow
            ? [ac, ar] // external authored ext→system: system family first
            : [ar, ac];
      const aggKey = `${firstFam}-${secondFam}`;
      // True endpoints for the quick-view sub-label — fall back to the flat
      // model's axis names. f.sideAName tracks the flat r side, f.sideBName the
      // flat c side; re-pair to the aggregate KEY order (firstFam, secondFam).
      const nameR = f.sideAName ?? els[r]?.name;
      const nameC = f.sideBName ?? els[c]?.name;
      (cells[aggKey] ??= []).push({
        ...f,
        sideAName: firstFam === ar ? nameR : nameC,
        sideBName: firstFam === ar ? nameC : nameR,
      });
    }
  }
  // Keep bundle order stable (refId) so the count chips read consistently.
  for (const list of Object.values(cells)) {
    list.sort((a, b) => a.refId.localeCompare(b.refId));
  }

  return { model: { elements, cells }, hiddenIntraFamily };
}

/**
 * R4 W1B: two different features both mint a turn whose raw kind is
 * 'countersign'/'COUNTERSIGN' — W2-A's workshop-session review AND the new
 * trade-level checker countersign (POST /trades/:id/checkers). They're only
 * distinguishable by which entity the turn decorates: a workshop 'countersign'
 * carries `entityType: 'FmeaSession'`, a trade one carries `entityType:
 * 'TradeRecord'`. Route TradeRecord to the new 'countersign' home group (deep
 * link → the trade page); everything else keeps going to 'review' (→
 * /interlock/fmea since F4 retired /interlock/workshop) as before, so this is
 * additive and can't regress W2-A.
 */
function normalizeWaitingKind(kind: string, entityType?: string): WaitingKind {
  if (
    (
      [
        'handshake',
        'trade',
        'data-request',
        'review',
        'score',
        'ruling',
        'coverage-gap',
        'evidence-gap',
        'gate-fail',
        'fmea-risk',
        'external-risk',
      ] as string[]
    ).includes(kind)
  ) {
    return kind as WaitingKind;
  }
  // tolerate campaign-flavoured kinds the backend may emit ('campaign', 'sheet', 'vote')
  if (['campaign', 'sheet', 'vote', 'scoring'].includes(kind)) return 'score';
  if (kind === 'countersign') {
    return (entityType ?? '').toLowerCase().includes('trade') ? 'countersign' : 'review';
  }
  return 'trade';
}

/**
 * Adapt ONE turn-ledger row (either the design-era fixture shape — flat refId/title/
 * subtitle — or the live shape `{ kind, entityType, entityId, externalParty, entity:
 * {refId,title,state,interfaceRefId,...} }`) into a WaitingItem. `external` rows come
 * from `waitingOnExternal` and never carry `whoseTurn` (it's not this user's turn).
 */
function adaptWaitingItem(raw: unknown, i: number, external: boolean): WaitingItem {
  const o = raw as Record<string, unknown>;
  const entity = pick<Record<string, unknown>>(o, 'entity');
  const kindRaw = (pick<string>(o, 'kind', 'type') ?? 'trade').toLowerCase();
  const entityType = pick<string>(o, 'entityType');
  const kind = normalizeWaitingKind(kindRaw, entityType);
  // V4 — decoration MAY be absent for SCORE rows until the backend (parallel W1-A
  // parcel) decorates RiskReviewCampaign turns. Never fall back to a synthetic
  // "item-N" refId/title — that's not a real identifier and reads as a bug.
  const decoratedRefId =
    pick<string>(o, 'refId', 'ref') ??
    (entity &&
      (pick<string>(entity, 'refId') ??
        // W2-A's FmeaSession decoration carries `sessionId`, not `refId` — a real
        // identifier, just not called that. Prefer it over the synthetic fallback.
        pick<string>(entity, 'sessionId')));
  // X9c — a workshop-session `sessionId` IS a real identifier but a raw cuid
  // (`cmrg65ghh001qkks…`), not a human-friendly ref like `INT-06-T1`. Rendered in
  // the home list's fixed-width `.ilk-home-item-ref` chip it overflowed and
  // visually overlapped "Workshop session review" (audit X9,
  // 07-alex-home-workshop-review-label-overlap.png). It isn't even used for the
  // href (deepLinkFor('review', …) always goes to /interlock/fmea) — so for
  // 'review' rows, never surface it as the display ref; the title already says
  // what it is.
  // Debt-summary rows (coverage-gap/evidence-gap/gate-fail) have no per-entity
  // ref — they're a live count, not a thing with an id — so they get the same
  // no-synthetic-ref treatment as 'review'/'score'.
  const isDebtKind = kind === 'coverage-gap' || kind === 'evidence-gap' || kind === 'gate-fail';
  const refId =
    kind === 'review' || isDebtKind
      ? ''
      : (decoratedRefId ?? (kind === 'score' ? '' : `item-${i}`));
  const decoratedTitle =
    pick<string>(o, 'title', 'name') ?? (entity && pick<string>(entity, 'title'));
  const title =
    decoratedTitle ??
    (kind === 'score' ? 'Scoring campaign' : kind === 'review' ? 'Workshop session review' : refId);
  const campaignId =
    pick<string>(o, 'campaignId') ?? (entity && pick<string>(entity, 'campaignId')) ?? undefined;
  const entityState = entity ? pick<string>(entity, 'state') : undefined;
  const externalParty = pick<string>(o, 'externalParty') ?? undefined;
  const subtitle =
    pick<string>(o, 'subtitle', 'summary', 'detail') ??
    (external && externalParty ? `Waiting on ${externalParty}` : (entityState ?? ''));
  return {
    id: pick<string>(o, 'id') ?? `wait-${i}`,
    kind,
    refId,
    title,
    subtitle,
    whoseTurn: external ? undefined : (pick<string>(o, 'whoseTurn', 'turn') ?? 'your turn'),
    href: pick<string>(o, 'href') ?? deepLinkFor(kind, campaignId ?? refId),
    externalParty,
    campaignId,
    createdAt: pick<string>(o, 'createdAt'),
  };
}

/** GET /api/me/waiting-on → WaitingItem[] (generic fallback path for fixtures/tests). */
export function adaptWaiting(body: unknown): WaitingItem[] {
  return asArray(body).map((raw, i) => adaptWaitingItem(raw, i, false));
}

/**
 * V2 slash-safe link helpers — refIds like `REC/INT-10-T1` contain a literal `/`
 * that breaks the `/interlock/trades/:refId` route match if left unencoded (the
 * browser treats it as an extra path segment). ALWAYS build trade/campaign links
 * through these two helpers rather than templating the refId in directly —
 * `useParams()` / `decodeURIComponent()` on the receiving page undo the encoding.
 */
export function tradeHref(refId: string): string {
  return `/interlock/trades/${encodeURIComponent(refId)}`;
}

export function campaignHref(idOrRef: string): string {
  return `/interlock/campaigns/${encodeURIComponent(idOrRef)}`;
}

function deepLinkFor(kind: WaitingKind, refId: string): string {
  if (kind === 'trade' || kind === 'handshake' || kind === 'ruling' || kind === 'countersign')
    return tradeHref(refId);
  if (kind === 'score') return campaignHref(refId);
  // Workshop-session COUNTERSIGN turns have no per-session route. F4 (owner's
  // retirement call): /interlock/workshop now redirects to /interlock/fmea, so
  // link there directly. KNOWN GAP — the sweep-session review UI (WorkshopPage)
  // is no longer routable; if session countersign review is needed again it
  // must be re-surfaced inside the FMEA shell (see App.tsx route comment).
  if (kind === 'review') return '/interlock/fmea';
  // Debt-summary rows normally carry their own `href` from the backend (picked
  // up before this fallback ever runs) — these are just a sane landing spot if
  // that's ever absent (e.g. a hand-written fixture).
  if (kind === 'coverage-gap' || kind === 'evidence-gap') return '/interlock/requirements';
  if (kind === 'gate-fail') return '/interlock/gates';
  // fmea-risk rows always carry their own `href` from the backend
  // (makeFmeaRiskItem — scoped to the failure mode's workshop view); this is
  // just a sane landing spot if that's ever absent.
  if (kind === 'fmea-risk') return '/interlock/fmea';
  // external-risk rows carry their own `href` (makeExternalRiskItem — the RSK-NN
  // detail or the register list); the register is the sane fallback.
  if (kind === 'external-risk') return '/interlock/risks';
  return `/interlock/interfaces/${encodeURIComponent(refId)}`;
}

/** GET /api/projects/:id/health → ProjectHealth */
export function adaptHealth(body: unknown): ProjectHealth {
  const o = unwrap<Record<string, unknown>>(body) ?? {};
  const turnsRaw = Array.isArray(o.oldestUnresolvedTurns) ? o.oldestUnresolvedTurns : [];
  const oldestUnresolvedTurns: HealthTurnRow[] = turnsRaw.map((raw) => {
    const t = raw as Record<string, unknown>;
    const tradeRaw = pick<Record<string, unknown>>(t, 'trade');
    return {
      kind: pick<string>(t, 'kind') ?? '—',
      trade: tradeRaw
        ? {
            refId: pick<string>(tradeRaw, 'refId') ?? '—',
            title: pick<string>(tradeRaw, 'title') ?? '',
          }
        : null,
      externalParty: pick<string>(t, 'externalParty') ?? null,
      forUserId: pick<string>(t, 'forUserId') ?? null,
      forUserName: pick<string>(t, 'forUserName') ?? null,
      ageDays: pick<number>(t, 'ageDays') ?? 0,
      createdAt: pick<string>(t, 'createdAt') ?? '',
    };
  });
  return {
    projectId: pick<string>(o, 'projectId') ?? PROJECT_ID,
    interfacesByStatus: pick<Record<string, number>>(o, 'interfacesByStatus') ?? {},
    tradesByState: pick<Record<string, number>>(o, 'tradesByState') ?? {},
    openDataRequestCount: Array.isArray(o.openDataRequests) ? o.openDataRequests.length : 0,
    oldestUnresolvedTurns,
  };
}

/** GET /api/projects/:id/escalations → EscalationRow[] (real shape: `{ escalations: [...] }`). */
export function adaptEscalations(body: unknown): EscalationRow[] {
  const o = unwrap<Record<string, unknown>>(body) ?? {};
  const raw = Array.isArray(o) ? o : Array.isArray(o.escalations) ? o.escalations : [];
  return (raw as unknown[]).map((r) => {
    const t = r as Record<string, unknown>;
    return {
      id: pick<string>(t, 'id') ?? '—',
      refId: pick<string>(t, 'refId') ?? '—',
      title: pick<string>(t, 'title') ?? '',
      interfaceId: pick<string>(t, 'interfaceId') ?? '',
      interfaceRefId: pick<string>(t, 'interfaceRefId') ?? null,
      interfaceTitle: pick<string>(t, 'interfaceTitle') ?? null,
      reason: pick<string>(t, 'reason') ?? null,
      escalatedByUserId: pick<string>(t, 'escalatedByUserId') ?? null,
      escalatedByName: pick<string>(t, 'escalatedByName') ?? null,
      escalatedAt: pick<string>(t, 'escalatedAt') ?? '',
      ageMs: pick<number>(t, 'ageMs') ?? 0,
      awaitingRuling: pick<boolean>(t, 'awaitingRuling') ?? true,
    };
  });
}

// ============================================================================
// Public fetch functions — each returns Fetched<T> and NEVER throws (except a
// 401, which is deliberately re-thrown — see handleAuthExpiry below).
//
// RS3/RS4/RS5 (round 10 resilience) — the "false calm" this whole block used to
// have: ANY failure (including a 500, and even a genuinely EMPTY 200) fell back
// to realistic-looking fixture data, in every environment. Per the project's
// design intent ("Detail is live-only; fixtures stay on the campaign LIST page
// only"), these dashboards were never supposed to show fixture data in prod at
// all. The fixed contract:
//   - a 401 NEVER becomes fixture, in ANY env — it's a session expiry, so it's
//     surfaced via the same SSO redirect AuthContext already uses (see
//     handleAuthExpiry).
//   - a genuinely empty 200 is a REAL state (a fresh/small project), not a
//     failure — it returns the real empty shape with fromFixture:false, same as
//     the Campaigns list already does correctly.
//   - any other failure (network/5xx/parse) falls back to the DEV fixture only
//     in `import.meta.env.DEV` (so local dev without a backend still demos);
//     in PROD it returns the honest-empty shape with `error: true` so the page
//     can render "couldn't load", never a confident wall of fake rows.
// ============================================================================

/** RS3 — a 401 must never be swallowed into "just another failure": redirect to
 * the SSO login (once) and re-throw so it never resolves into fixture/empty data
 * that a page might render as if the session were still good. */
let redirectingToLogin = false;
function handleAuthExpiry(e: unknown): void {
  if (e instanceof InterlockApiError && e.status === 401) {
    if (!redirectingToLogin) {
      redirectingToLogin = true;
      redirectToLogin();
    }
    throw e;
  }
}

/** RS5 — the shared DEV-fixture / PROD-honest-failure branch every fetchX below
 * uses in its catch block. `devFixture` is the existing design-canvas fixture
 * (DEV only, so local dev without a backend still demos); `prodEmpty` is the
 * real empty shape for that view-model (so PROD renders an honest "couldn't
 * load" / empty state instead of fake data). Always call {@link handleAuthExpiry}
 * first so a 401 never reaches here. */
function fixtureOrHonestFail<T>(e: unknown, devFixture: T, prodEmpty: T): Fetched<T> {
  handleAuthExpiry(e);
  if (import.meta.env.DEV) return { data: devFixture, fromFixture: true };
  return { data: prodEmpty, fromFixture: false, error: true };
}

export async function fetchInterfaces(): Promise<Fetched<InterfaceRow[]>> {
  try {
    const body = await getJson(`/projects/${PROJECT_ID}/interfaces`);
    const data = adaptInterfaces(body);
    // RS4 — an empty register is a real (good) state, not a fixture fallback.
    return { data, fromFixture: false };
  } catch (e) {
    return fixtureOrHonestFail(e, FIXTURE_INTERFACES, []);
  }
}

/**
 * Phase 3 (blast radius) — resolve an interface refId (e.g. 'INT-02') to its DB
 * id, for surfaces that only carry refIds (the Matrix's `MatrixCellInterface`,
 * System View's connection-derived refs) and need the id to call
 * `fetchBlastRadius({kind:'INTERFACE', id})`. Reuses the same live register
 * fetch as everything else — no separate endpoint — with a tiny in-memory
 * cache so a lens engaged repeatedly on the same page doesn't re-fetch the
 * whole register per click. Returns undefined on a fixture-backed response
 * (no real ids) or an unknown refId.
 */
let _ifaceIdByRefCache: Map<string, string> | null = null;
export async function resolveInterfaceIdByRef(refId: string): Promise<string | undefined> {
  if (!_ifaceIdByRefCache) {
    const { data, fromFixture } = await fetchInterfaces();
    if (fromFixture) return undefined;
    _ifaceIdByRefCache = new Map(data.filter((r) => r.id).map((r) => [r.refId, r.id!]));
  }
  return _ifaceIdByRefCache.get(refId);
}

export async function fetchMatrix(): Promise<Fetched<MatrixModel>> {
  try {
    const body = await getJson(`/projects/${PROJECT_ID}/interfaces/matrix`);
    const data = adaptMatrix(body);
    // RS4 — a zero-element matrix is a real (fresh-project) state.
    return { data, fromFixture: false };
  } catch (e) {
    return fixtureOrHonestFail(e, FIXTURE_MATRIX, { elements: [], cells: {} });
  }
}

export async function fetchInterface(refId: string): Promise<Fetched<InterfaceRow | undefined>> {
  try {
    const body = await getJson(`/interfaces/${refId}`);
    const data = adaptInterfaces(Array.isArray(unwrap(body)) ? body : [unwrap(body)]);
    return { data: data[0], fromFixture: false };
  } catch (e) {
    return fixtureOrHonestFail(
      e,
      FIXTURE_INTERFACES.find((i) => i.refId === refId),
      undefined
    );
  }
}

export async function fetchWaitingOn(): Promise<Fetched<WaitingOnData>> {
  try {
    // Dev actor override so the ledger shows the switched user's turns; an empty
    // ledger is a real (good) state, not a fixture fallback.
    //
    // Real shape (projectHealth.service.ts getWaitingOn): `{ waitingOnMe: [...],
    // waitingOnExternal: [...], counts }` — NOT one of asArray's generic unwrap
    // keys ('interfaces'/'items'/'rows'/'results'), so read the two lists by their
    // real names explicitly rather than widening that shared helper.
    const body = await getJson<Record<string, unknown>>(withActor(`/me/waiting-on`));
    const o = unwrap<Record<string, unknown>>(body) ?? {};
    const mineRaw = Array.isArray(o.waitingOnMe) ? o.waitingOnMe : [];
    const externalRaw = Array.isArray(o.waitingOnExternal) ? o.waitingOnExternal : [];
    return {
      data: {
        mine: mineRaw.map((r, i) => adaptWaitingItem(r, i, false)),
        external: externalRaw.map((r, i) => adaptWaitingItem(r, i, true)),
      },
      fromFixture: false,
    };
  } catch (e) {
    return fixtureOrHonestFail(
      e,
      { mine: FIXTURE_WAITING, external: FIXTURE_WAITING_EXTERNAL },
      { mine: [], external: [] }
    );
  }
}

export async function fetchHealth(): Promise<Fetched<ProjectHealth>> {
  try {
    const body = await getJson(`/projects/${PROJECT_ID}/health`);
    return { data: adaptHealth(body), fromFixture: false };
  } catch (e) {
    return fixtureOrHonestFail(e, FIXTURE_HEALTH, {
      projectId: PROJECT_ID,
      interfacesByStatus: {},
      tradesByState: {},
      openDataRequestCount: 0,
      oldestUnresolvedTurns: [],
    });
  }
}

/**
 * GET /api/projects/:id/escalations (Campaign-2 Theme-2 Wave-5, audit finding #5) —
 * every trade currently ESCALATED, project-wide. An empty list is the GOOD state
 * (nothing waiting on an arbiter right now), not a failure — same RS4 idiom as
 * fetchInterfaces/fetchHealth: real empty → fromFixture:false, only a genuine
 * fetch/parse failure falls back to fixture (DEV) / honest-empty+error (PROD).
 */
export async function fetchEscalations(): Promise<Fetched<EscalationRow[]>> {
  try {
    const body = await getJson(`/projects/${PROJECT_ID}/escalations`);
    return { data: adaptEscalations(body), fromFixture: false };
  } catch (e) {
    return fixtureOrHonestFail(e, FIXTURE_ESCALATIONS, []);
  }
}

// ============================================================================
// Trade Detail (F2) — the hero screen. Live-only (no fixture): the page renders
// a clear "trade not found / backend offline" state rather than faking a trade.
// ============================================================================

export type TradeState =
  | 'OPEN'
  | 'NEGOTIATING'
  | 'CONVERGED'
  | 'CLOSED'
  | 'ESCALATED'
  | 'RESOLVED_BY_RULING';

export interface TradeIndexRow {
  id: string;
  refId: string;
  title: string;
  state: TradeState;
  interfaceId: string;
  interfaceRefId?: string;
}

export interface HandshakeRow {
  id: string;
  userId: string;
  side: 'A' | 'B';
  role: 'PARTY' | 'CHECKER';
  state: 'PENDING' | 'AGREED' | 'DECLINED';
  /** trail-milestones — when this party/checker was assigned (the "joined"
   * milestone timestamp). Read defensively: absent on older backends. */
  createdAt?: string;
  /** trail-milestones — last state change; the best available real timestamp
   * for an AGREED handshake's "signed" milestone. Read defensively. */
  updatedAt?: string;
  // Populated only while this handshake is PENDING and the user's most recent
  // action was a rejection (cleared by a re-sign). Read defensively — absent
  // on backends before this parcel.
  rejection?: { kind: 'CHECKER_PUSHBACK' | 'PARTY_WITHDRAWAL'; at: string; note?: string } | null;
}

export interface StakeholderRow {
  id: string;
  userId: string;
  domain: string;
  reason: string | null;
}

export interface FeedLinkRow {
  id: string;
  feedsKind: 'INTERFACE' | 'TRADE' | 'NOTE';
  feedsId: string | null;
  feedsText: string | null;
}

/**
 * Handshake artefact (MVP) — a File/Image attachment bound to a negotiation-log
 * entry, served by the existing /api/attachments pipeline. Compact metadata only;
 * the bytes stream from {@link artefactDownloadUrl}. Meeting-min extracts are NOT
 * artefacts (they're COMMENT entries with a placeholder `sourceRef`).
 */
export interface ArtefactRef {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  description: string | null;
  uploadedAt: string;
}

/**
 * Trade-level artefact (gallery) — an {@link ArtefactRef} plus the owning
 * negotiation-log `entryId` (jump-to-comment target) and a best-effort
 * `uploadedByUserId` (the author of the entry it's bound to; Attachment rows carry
 * no uploader). Served by GET /api/trades/:id/artefacts — EVERY artefact on the
 * trade, not just the loaded log window.
 */
export interface TradeArtefact extends ArtefactRef {
  entryId: string;
  uploadedByUserId: string | null;
}

export interface TradeArtefactList {
  tradeId: string;
  /** Server-derived projectId — pass to {@link artefactDownloadUrl}. */
  projectId: string;
  artefacts: TradeArtefact[];
}

export interface LogEntryNode {
  id: string;
  authorUserId: string;
  onBehalfOfUserId: string | null;
  body: string;
  kind: 'ROOT' | 'PROPOSAL' | 'COMMENT' | 'REPLY';
  parentEntryId: string | null;
  proposedValue: string | null;
  status: 'DRAFT' | 'CONFIRMED';
  sourceRef: Record<string, unknown> | null;
  createdAt: string;
  children: LogEntryNode[];
  /** Handshake artefacts (MVP) bound to this entry — File/Image chips/thumbnails.
   * Read defensively: absent on older backends / flat-page rows. */
  artefacts?: ArtefactRef[];
  /** S4 (R7 W1) — server-authored flag: true for state-machine-authored entries
   * (override note, ruled close, reopen reason, invite, sign/countersign/withdraw/
   * push-back, bound FYI). Undefined/false for user-typed appendLog entries and
   * pre-existing rows from before the flag was added. */
  systemic?: boolean;
  /** trail-provenance — INJECTED rows (engine backfill) render red+bold and are
   * body-editable via {@link updateTradeLogEntry}. Read defensively: absent on
   * older backends (treat as REVIEWED / read-only). */
  reviewState?: 'INJECTED' | 'REVIEWED';
  /** trail-provenance badge — where this line came from (stamped at write time,
   * never derived on read). Null/absent = pre-column row, no badge. */
  sourceType?: EntrySourceType | null;
}

/** trail-provenance badge values (see backend EntrySourceType). */
export type EntrySourceType = 'MEETING' | 'EMAIL' | 'DIRECT' | 'IMPORT';

export interface OpenTurn {
  id: string;
  kind: string; // 'HANDSHAKE' | 'ACKNOWLEDGE' | ...
  forUserId: string;
  resolvedAt: string | null;
}

export interface BoundRef {
  id: string;
  refId: string | null;
  title: string | null;
  // Y6 (round 4) — MAY be present once the parallel backend parcel adds `version`
  // to trade-detail bounds entries. Read defensively (undefined on older
  // responses); a value > 1 means the requirement was edited after this trade
  // bound it, which the "· rev N" marker below surfaces.
  version?: number;
}
export interface PullRef {
  id: string;
  kind: string;
  refId: string | null;
  severity: number | null;
  title: string | null;
}
/**
 * xref-sections wave — the reverse of the Component page's decision list: a
 * ComponentDecision linked to THIS trade via DecisionTradeLink
 * (trade.service.ts getTradeDetail). Same status union as componentPage.ts's
 * DecisionStatus.
 */
export interface TradeDecisionLink {
  linkId: string;
  note: string | null;
  id: string;
  refId: string;
  title: string;
  status: 'DRAFT' | 'DECIDED' | 'SUPERSEDED';
  decidedAt: string | null;
  /** The owning component — used to build the "open in component page" door
   *  (?openDecision=<id>), the same drawer the Component page's own DECIDED
   *  section drills into. */
  componentId: string | null;
}

export interface GateDataRequest {
  refId: string;
  status: string; // 'OPEN' | 'ANSWERED' | ...
  ask: string;
  // Z11 — additive, read defensively (absent on backends before the DR-answer
  // parcel lands); trade detail is a raw pass-through (no adapter), so these
  // ride straight off the backend's serializer.
  answeredByUserId?: string | null;
  answeredByName?: string | null;
}

/** The stored `TradeRecord.ruling` Json (trade.service.ts `rule()`). Read defensively —
 * it's a Json column, not a typed relation. */
export interface RulingJson {
  arbiterUserId: string;
  chosenOption: string | null;
  rationale: string;
  riskAcceptance: { riskId?: string; residualBand?: string } | null;
  ruledAt: string;
  // W2-A: snapshot of the trade's options prose at ruling time — read defensively,
  // absent on rulings recorded before this field existed.
  optionsAtRuling?: string | null;
}

/** W2-A: one resolved ACKNOWLEDGE turn — a party who has acknowledged the ruling. */
export interface AcknowledgementRow {
  userId: string;
  resolvedAt: string;
}

export interface TradeDetail {
  id: string;
  refId: string;
  title: string;
  state: TradeState;
  demandNeed: string | null;
  supplyLimit: string | null;
  options: string | null;
  convergedValue: string | null;
  gateRef: string | null;
  ownerUserId: string | null;
  interfaceId: string;
  projectId: string;
  Interface?: {
    id: string;
    refId: string;
    register: string;
    spec: string | null;
    ownerUserId?: string | null;
    /** trail-provenance — counterparty name (e.g. "QCells") for the
     * "on behalf of <client>" badge. Read defensively. */
    externalCounterparty?: string | null;
  };
  /** trail-milestones — spawn timestamp (raw TradeRecord field, passed through
   * by the detail serializer). Read defensively on older backends. */
  createdAt?: string;
  /** trail-milestones — set when the trade converged; drives the "Settled"
   * milestone timestamp. Read defensively. */
  convergedAt?: string | null;
  // Model A handshake — the Crossing this trade negotiates (1:1), present when the
  // trade was raised from a crossing. Drives the Trade Detail crossing-context
  // header (refId · discipline · demand → supply → agreed). Read defensively —
  // absent/null for a plain interface-scoped trade or a backend before this parcel.
  Crossing?: {
    id: string;
    refId: string;
    discipline: CrossingDiscipline;
    demandText: string | null;
    supplyText: string | null;
    agreedText: string | null;
    status: CrossingStatus;
  } | null;
  // Model A handshake — anchor id (spread from the raw TradeRecord). Read
  // defensively; use Crossing above for display.
  crossingId?: string | null;
  handshakes: HandshakeRow[];
  stakeholders: StakeholderRow[];
  feedLinks: FeedLinkRow[];
  logTree: LogEntryNode[];
  openTurns: OpenTurn[];
  proposedValue: string | null;
  bounds: BoundRef[];
  pulls: PullRef[];
  /** xref-sections wave — read defensively (absent on backends before this
   *  parcel); raw pass-through same as everything else on this type. */
  decisions?: TradeDecisionLink[];
  gateDataRequests: GateDataRequest[];
  /** PERF1 (round 13) — read defensively: absent/undefined on backends that
   * still return the full log tree (Parcel A's cap ships separately). When
   * present and true, `logTree` is a truncated recent window and older
   * entries are fetchable via {@link fetchTradeLogBefore}. */
  hasMoreLog?: boolean;
  // §6.2b — MAY be present once the elevation parcel lands; read defensively.
  requiresCountersign?: boolean;
  elevationReasons?: ElevationReason[];
  // Set once an arbiter has ruled (state RESOLVED_BY_RULING); kept as history even
  // after a reopen — read defensively, it's a raw Json column.
  ruling?: RulingJson | null;
  // W2-A: resolved ACKNOWLEDGE turns — who has acknowledged the ruling and when.
  // Read defensively (absent on backends before this parcel).
  acknowledgements?: AcknowledgementRow[];
  // W2-A: trivial derived hint — true only when state is CONVERGED (the only state
  // close() accepts). Read defensively; fall back to the state check itself.
  canClose?: boolean;
}

/**
 * X1 — thrown by {@link requestJson} on a non-2xx response. Carries the HTTP status
 * + parsed error body (when JSON) so callers can distinguish e.g. a 409 ConflictError
 * (close() blocked on a missing countersign) from a generic failure, instead of
 * pattern-matching the message string alone.
 */
export class InterlockApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;
  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'InterlockApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** W8-A fix 3 — true for ANY 409 from {@link InterlockApiError}, regardless of which
 * specific conflict code it carries. The more specific `is*Conflict` helpers below
 * exist where a caller wants to react differently to a particular guard; this one
 * is for callers (rule()/close() stale-action handling) that just need to know
 * "the trade's state changed underneath this click, resync" without caring which
 * guard fired. */
export function isConflictError(e: unknown): e is InterlockApiError {
  return e instanceof InterlockApiError && e.status === 409;
}

/** True for a 409 whose body identifies it as the close() "requires a countersign"
 * ConflictError. Backend contract (parallel parcel) isn't final, so this checks
 * BOTH a structured code/error field and — defensively — the message text, so the
 * UI still recognizes the conflict even if the exact field name drifts. */
export function isCountersignConflict(e: unknown): e is InterlockApiError {
  if (!(e instanceof InterlockApiError) || e.status !== 409) return false;
  if (e.code === 'COUNTERSIGN_REQUIRED') return true; // stable machine code (backend)
  // Defensive fallback for older payloads only.
  const marker = `${e.code ?? ''} ${e.message ?? ''}`.toLowerCase();
  return marker.includes('countersign');
}

/** Z12 — true for a 409 whose body identifies close() as blocked on outstanding
 * ACKNOWLEDGE turns on a RESOLVED_BY_RULING trade (backend contract: code
 * `ACKNOWLEDGE_PENDING`). Same defensive fallback pattern as
 * {@link isCountersignConflict} since the backend half of this contract lands in a
 * parallel parcel. */
export function isAcknowledgePendingConflict(e: unknown): e is InterlockApiError {
  if (!(e instanceof InterlockApiError) || e.status !== 409) return false;
  if (e.code === 'ACKNOWLEDGE_PENDING') return true; // stable machine code (backend)
  const marker = `${e.code ?? ''} ${e.message ?? ''}`.toLowerCase();
  return marker.includes('acknowledge');
}

/** Fix-drule — true for a 409 whose body identifies rule() as blocked on zero
 * AGREED handshake parties (backend contract: code `NO_SIGNED_PARTIES`). The
 * ruling modal pre-empts this with its own warning + confirm before it ever
 * sends the request, but this lets a caller recognize the guard if it's ever
 * hit directly (defense in depth, same pattern as {@link isCountersignConflict}). */
export function isNoSignedPartiesConflict(e: unknown): e is InterlockApiError {
  if (!(e instanceof InterlockApiError) || e.status !== 409) return false;
  if (e.code === 'NO_SIGNED_PARTIES') return true; // stable machine code (backend)
  const marker = `${e.code ?? ''} ${e.message ?? ''}`.toLowerCase();
  return marker.includes('signed') && marker.includes('part');
}

/** K1 (R7 W2) — true for a 403 from the write-path actor gate (party/owner/admin
 * only). Lets the add-stakeholder UI show an honest "you're not authorized" message
 * instead of the generic "Action failed" toast when a non-authorized actor tries it. */
export function isForbidden(e: unknown): e is InterlockApiError {
  return e instanceof InterlockApiError && e.status === 403;
}

/** CC2-client (round 11) — true for a 409 whose body identifies agree() as
 * rejected because the proposal the caller signed against (`expectedProposalId`)
 * is no longer the latest CONFIRMED PROPOSAL on the trade (backend contract:
 * code `STALE_PROPOSAL`). Same defensive fallback pattern as
 * {@link isCountersignConflict}. */
export function isStaleProposal(e: unknown): e is InterlockApiError {
  if (!(e instanceof InterlockApiError) || e.status !== 409) return false;
  if (e.code === 'STALE_PROPOSAL') return true; // stable machine code (backend)
  const marker = `${e.code ?? ''} ${e.message ?? ''}`.toLowerCase();
  return marker.includes('stale') && marker.includes('propos');
}

/** FR1 (round 11) — on a 401, the generic mutation helper used to fall straight
 * into the `InterlockApiError` throw below and dead-end as a plain "Action
 * failed" toast (see TradeDetailPage's runMutation). That's a real dead end:
 * the session is gone, so every retry 401s again. The read fetchers already
 * route a 401 through {@link handleAuthExpiry} → {@link redirectToLogin};
 * mutations get the same treatment here so an expired session re-auths
 * instead of silently failing every subsequent click. */
async function requestJson<T>(path: string, init?: Parameters<typeof fetch>[1]): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${withActor(path)}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...init,
  });
  if (!res.ok) {
    let detail = '';
    let code: string | undefined;
    let parsedBody: unknown;
    try {
      const body = await res.json();
      parsedBody = body;
      code = body?.code ?? body?.error;
      detail = body?.error ?? body?.message ?? JSON.stringify(body);
    } catch {
      detail = res.statusText;
    }
    const err = new InterlockApiError(
      detail || `Interlock API ${path} → ${res.status}`,
      res.status,
      code,
      parsedBody
    );
    if (res.status === 401) {
      // Kicks off the SSO redirect (once, via the shared guard) and re-throws
      // — a mutation caller never mistakes this for a value it can act on.
      handleAuthExpiry(err);
    }
    throw err;
  }
  return (await res.json()) as T;
}

/**
 * GET a cursor-paginated list endpoint (PERF3) and follow `X-Next-Cursor` /
 * `X-Has-More` headers until the FULL set is assembled. The list endpoints cap
 * each response (default 200) so a single request is never unbounded, but
 * callers that need every row (refId resolution, nav search across all trades)
 * must page through — reading only the first page silently truncates to 200 and
 * makes trade #201+ resolve to "not found". Mirrors the backend mcpServer paging
 * loop. Bounded by a hard page cap so a misbehaving cursor can't spin forever.
 */
async function getAllPagedArray(path: string, pageLimit = 500): Promise<unknown[]> {
  const out: unknown[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 200; page++) {
    const sep = path.includes('?') ? '&' : '?';
    const qs = `${sep}limit=${pageLimit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
    const res = await fetch(`${API_BASE_URL}${withActor(`${path}${qs}`)}`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!res.ok) {
      let code: string | undefined;
      let body: unknown;
      try {
        body = await res.json();
        code = (body as Record<string, unknown> | undefined)?.code as string | undefined;
      } catch {
        /* non-JSON error body */
      }
      const err = new InterlockApiError(
        `Interlock API ${path} → ${res.status}`,
        res.status,
        code,
        body
      );
      if (res.status === 401) handleAuthExpiry(err);
      throw err;
    }
    out.push(...asArray(await res.json()));
    const hasMore = res.headers.get('X-Has-More') === 'true';
    cursor = res.headers.get('X-Next-Cursor') ?? undefined;
    if (!hasMore || !cursor) break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Global search (nav-rail jump-to-entity) — GET /api/projects/:id/search?q=
// ---------------------------------------------------------------------------

export type SearchKind =
  | 'requirement'
  | 'interface'
  | 'trade'
  | 'component'
  | 'function'
  | 'risk'
  | 'dataRequest'
  | 'failureMode'
  | 'decision';

export interface SearchHit {
  kind: SearchKind;
  id: string;
  refId: string | null;
  label: string;
  sublabel: string | null;
  /** In-app deep link to the entity (a React Router path). */
  url: string;
  score: number;
}

export interface ProjectSearchResult {
  query: string;
  results: SearchHit[];
  total: number;
}

/**
 * Project-scoped cross-entity search. Returns a flat, ranked hit list across
 * requirements / interfaces / trades / components / functions / risks. The
 * backend caps results per kind, so this is a single bounded request.
 */
export async function searchProjectEntities(
  query: string,
  projectId: string = PROJECT_ID
): Promise<ProjectSearchResult> {
  return requestJson<ProjectSearchResult>(
    `/projects/${encodeURIComponent(projectId)}/search?q=${encodeURIComponent(query)}`
  );
}

/** GET /api/projects/:id/trades → project-wide trade index (nav rail + refId resolution). */
export async function fetchProjectTrades(): Promise<TradeIndexRow[]> {
  const rows = await getAllPagedArray(`/projects/${PROJECT_ID}/trades`);
  return rows.map((raw) => {
    const o = raw as Record<string, unknown>;
    const iface = o.Interface as Record<string, unknown> | undefined;
    return {
      id: pick<string>(o, 'id') ?? '',
      refId: pick<string>(o, 'refId') ?? '',
      title: pick<string>(o, 'title') ?? '',
      state: (pick<string>(o, 'state') ?? 'OPEN') as TradeState,
      interfaceId: pick<string>(o, 'interfaceId') ?? '',
      interfaceRefId: iface ? (iface.refId as string) : undefined,
    };
  });
}

/** Resolve a route `:refId` (or raw id) to the trade's primary id via the index. */
export async function resolveTradeId(refIdOrId: string): Promise<TradeIndexRow | undefined> {
  const rows = await fetchProjectTrades();
  const needle = decodeURIComponent(refIdOrId);
  return (
    rows.find((t) => t.refId === needle) ??
    rows.find((t) => t.id === needle) ??
    // tolerate a stripped "REC/" style prefix or case drift
    rows.find((t) => t.refId.replace(/^[A-Z]+\//, '') === needle.replace(/^[A-Z]+\//, '')) ??
    rows.find((t) => t.refId.toUpperCase() === needle.toUpperCase())
  );
}

/** GET /api/trades/:id → full trade detail (by primary id). */
export async function fetchTradeDetail(tradeId: string): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}`);
}

/**
 * GET /api/trades/:id/artefacts → EVERY artefact bound to any negotiation-log
 * entry of the trade (newest-first), for the Trade Detail artefact gallery. The
 * per-comment chips only cover the loaded log window; this doesn't. `projectId` in
 * the response is server-derived — use it (not a client value) for download URLs.
 */
export async function fetchTradeArtefacts(tradeId: string): Promise<TradeArtefactList> {
  return requestJson<TradeArtefactList>(`/trades/${tradeId}/artefacts`);
}

/**
 * PERF1-frontend (round 13) — "Load earlier entries" for a windowed trade log.
 * Contract (coordinated with Parcel A, backend): `GET /trades/:id/log?before=<entryId>`
 * returns `{ items: LogEntryNode[], hasMore: boolean }`, older-than-cursor entries
 * in the same threaded-tree shape as `TradeDetail.logTree`.
 *
 * Parcel A's endpoint may not exist yet in this worktree/deploy — a 404 here is
 * expected and the caller (TradeDetailPage's CommitGraph) treats it as "no more
 * server-side history available" rather than a hard error. Flagged for a
 * post-merge re-check once both parcels land together.
 */
export async function fetchTradeLogBefore(
  tradeId: string,
  beforeEntryId: string
): Promise<{ items: LogEntryNode[]; hasMore: boolean }> {
  const res = await requestJson<{
    items?: LogEntryNode[];
    logTree?: LogEntryNode[];
    hasMore: boolean;
  }>(`/trades/${tradeId}/log?before=${encodeURIComponent(beforeEntryId)}`);
  // The backend returns BOTH a flat `items` list AND a threaded `logTree`. The
  // log renderer (GraphNode) walks `node.children`, so we must hand it the
  // threaded shape — the flat rows carry no `children` array and would crash the
  // renderer. Prefer `logTree`; if a backend only sends flat `items`, normalise
  // each row to a childless node so the renderer's contract still holds.
  const page: LogEntryNode[] =
    res.logTree ??
    (res.items ?? []).map((e) => ({ ...e, children: (e as LogEntryNode).children ?? [] }));
  // The backend pages ascending (oldest-first), but the log is rendered
  // newest-first (roots are reversed at the top). Reverse each fetched page to
  // newest-first so it slots in below the already-loaded window AND so the
  // caller's "oldest loaded" cursor (the LAST combined node) advances to the
  // page's oldest entry — otherwise the cursor sticks on the page's newest id
  // and re-fetches the same window forever (duplicate keys, runaway count).
  return { items: page.slice().reverse(), hasMore: res.hasMore };
}

/** POST /api/trades/:id/log — append a comment / reply (parentEntryId) / draft.
 *
 * `sourceRef` (artefacts MVP): pass `{ source:'MEETINGNOTES', pending:true }` to tag
 * the comment as a meeting-min extract (the quote goes in `body`). The backend
 * accepts this placeholder from any TRADE_PARTICIPATE author; a real
 * {recordingId,timestamp} provenance ref remains admin-only. */
export async function appendTradeLog(
  tradeId: string,
  input: {
    body: string;
    kind?: string;
    parentEntryId?: string | null;
    sourceRef?: {
      source?: 'MEETINGNOTES';
      pending?: boolean;
      recordingId?: string;
      timestamp?: string;
    };
  }
): Promise<unknown> {
  return requestJson(`/trades/${tradeId}/log`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// CrossingComment discussion log + provenance (reviewState) — data-backfill
// foundation. The comment log hangs off a Crossing (not a trade); reviewState
// 'INJECTED' rows render red+bold until marked reviewed.
// ---------------------------------------------------------------------------

export interface CrossingComment {
  id: string;
  crossingId: string;
  authorUserId: string;
  authorName: string;
  body: string;
  sourceLabel: string | null;
  reviewState: 'INJECTED' | 'REVIEWED';
  /** trail-provenance badge; null/absent = pre-column row, no badge. */
  sourceType?: EntrySourceType | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /api/crossings/:id/comments → chronological discussion log */
export async function listCrossingComments(crossingId: string): Promise<CrossingComment[]> {
  const body = await requestJson<{ comments?: CrossingComment[] }>(
    `/crossings/${encodeURIComponent(crossingId)}/comments`
  );
  return Array.isArray(body?.comments) ? body.comments : [];
}

/** POST /api/crossings/:id/comments. Back-dating params (authorUserId / createdAt /
 *  sourceLabel / reviewState) are for the backfill; normal UI create omits them. */
export async function createCrossingComment(
  crossingId: string,
  input: {
    body: string;
    authorUserId?: string | null;
    sourceLabel?: string | null;
    createdAt?: string | null;
    reviewState?: 'INJECTED' | 'REVIEWED' | null;
  }
): Promise<CrossingComment> {
  return requestJson(`/crossings/${encodeURIComponent(crossingId)}/comments`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** PATCH /api/crossing-comments/:id — edit body (the edit-comment feature). */
export async function updateCrossingComment(
  id: string,
  input: { body?: string; sourceLabel?: string | null }
): Promise<CrossingComment> {
  return requestJson(`/crossing-comments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * PATCH /api/trades/:id/log/:entryId — edit the BODY of an INJECTED
 * (engine-backfilled) negotiation-log entry. The backend 403s on any
 * non-INJECTED row: the paper trail is on the record and human entries are
 * append-only. Returns the updated raw entry (no children/artefacts) — callers
 * refetch the trade rather than merging it.
 */
export async function updateTradeLogEntry(
  tradeId: string,
  entryId: string,
  body: string
): Promise<LogEntryNode> {
  return requestJson(`/trades/${encodeURIComponent(tradeId)}/log/${encodeURIComponent(entryId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });
}

/** DELETE /api/crossing-comments/:id — soft-delete. */
export async function deleteCrossingComment(id: string): Promise<void> {
  await requestJson(`/crossing-comments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type ProvenanceEntity =
  | 'crossingComment'
  | 'crossing'
  | 'interface'
  | 'tradeRecord'
  | 'componentAttribute'
  | 'dataRequest';

/** POST /api/provenance/mark-reviewed — flip ONE record INJECTED→REVIEWED. */
export async function markReviewed(entity: ProvenanceEntity, id: string): Promise<void> {
  await requestJson(`/provenance/mark-reviewed`, {
    method: 'POST',
    body: JSON.stringify({ entity, id }),
  });
}

/** POST /api/interfaces/:id/mark-reviewed — bulk-clear an interface's injected data. */
export async function markInterfaceReviewed(interfaceId: string): Promise<Record<string, number>> {
  return requestJson(`/interfaces/${encodeURIComponent(interfaceId)}/mark-reviewed`, {
    method: 'POST',
  });
}

/** POST /api/crossings/:id/mark-reviewed — bulk-clear a crossing's injected data. */
export async function markCrossingReviewed(crossingId: string): Promise<Record<string, number>> {
  return requestJson(`/crossings/${encodeURIComponent(crossingId)}/mark-reviewed`, {
    method: 'POST',
  });
}

/** Direct URL the browser can GET to stream/download an artefact's bytes (image
 * <img src>, or a file download link). The /download route is unauthenticated in
 * dev; no actor needed. `projectId` is REQUIRED by the backend (cross-project
 * IDOR scoping) — pass the owning trade's projectId. */
export function artefactDownloadUrl(artefactId: string, projectId: string): string {
  return `${API_BASE_URL}/attachments/${encodeURIComponent(artefactId)}/download?projectId=${encodeURIComponent(projectId)}`;
}

/**
 * Upload a File/Image artefact and bind it to a negotiation-log entry
 * (entityType='NegotiationLogEntry'). Multipart — the browser sets the multipart
 * boundary, so we must NOT set Content-Type ourselves. projectId is derived
 * server-side from the entry's trade. Gated on TRADE_PARTICIPATE by the backend.
 */
export async function uploadTradeLogArtefact(
  entryId: string,
  file: File,
  description?: string
): Promise<ArtefactRef> {
  const form = new FormData();
  form.append('file', file);
  form.append('entityType', 'NegotiationLogEntry');
  form.append('entityId', entryId);
  if (description) form.append('description', description);
  const res = await fetch(`${API_BASE_URL}${withActor('/attachments')}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    let detail = '';
    let code: string | undefined;
    let parsedBody: unknown;
    try {
      const body = await res.json();
      parsedBody = body;
      code = body?.code ?? body?.error;
      detail = body?.error ?? body?.message ?? JSON.stringify(body);
    } catch {
      detail = res.statusText;
    }
    const err = new InterlockApiError(
      detail || `Attachment upload → ${res.status}`,
      res.status,
      code,
      parsedBody
    );
    if (res.status === 401) handleAuthExpiry(err);
    throw err;
  }
  const body = (await res.json()) as { attachment: ArtefactRef };
  return body.attachment;
}

/**
 * POST /api/attachments/entity/NegotiationLogEntry/:entryId/reconcile-body —
 * artefact-audit A1 (BLOCKER). Call once after a comment's upload loop
 * finishes with at least one failure: rewrites the entry's auto-generated
 * "📎 Attached N artefacts" body to the count that ACTUALLY landed, so the
 * append-only negotiation record never outlives the truth. No-op (still 200)
 * if the body was user-typed or already matches reality — safe to call
 * defensively. */
export async function reconcileTradeLogArtefactBody(
  entryId: string
): Promise<{ success: boolean; body?: string }> {
  return requestJson(
    `/attachments/entity/NegotiationLogEntry/${encodeURIComponent(entryId)}/reconcile-body`,
    {
      method: 'POST',
    }
  );
}

/** DELETE /api/attachments/:id — soft-delete a negotiation-log artefact
 * (TRADE_PARTICIPATE, entity-aware backend gate). `projectId` is REQUIRED by the
 * backend (cross-project IDOR scoping) — pass the owning trade's projectId. */
export async function deleteTradeLogArtefact(artefactId: string, projectId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}${withActor(
      `/attachments/${encodeURIComponent(artefactId)}?projectId=${encodeURIComponent(projectId)}`
    )}`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  );
  if (!res.ok) {
    const err = new InterlockApiError(`Attachment delete → ${res.status}`, res.status);
    if (res.status === 401) handleAuthExpiry(err);
    throw err;
  }
}

/** POST /api/trades/:id/log — confirm or discard an agent DRAFT entry. */
export async function resolveTradeDraft(
  tradeId: string,
  entryId: string,
  action: 'confirm' | 'discard'
): Promise<unknown> {
  return requestJson(`/trades/${tradeId}/log`, {
    method: 'POST',
    body: JSON.stringify({ entryId, action }),
  });
}

/** POST /api/trades/:id/propose — put a candidate value on the table. Returns full detail. */
export async function proposeTradeValue(tradeId: string, value: string): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/propose`, {
    method: 'POST',
    body: JSON.stringify({ value }),
  });
}

/** POST /api/trades/:id/agree — give (or retract) this actor's handshake. Returns full detail.
 * CC2-client (round 11) — `expectedProposalId` is the id of the latest CONFIRMED
 * PROPOSAL log entry the caller was shown when they clicked sign/countersign; the
 * backend (CC2) rejects with a 409 STALE_PROPOSAL when the value moved under them
 * between load and click. Optional/ignored by a backend that hasn't shipped CC2 yet. */
export async function agreeTrade(
  tradeId: string,
  // Z4 — `note` is required by the UI on a checker push-back (agree:false); the
  // backend treats it as optional and appends it as a second log entry when present.
  input: {
    agree?: boolean;
    side?: 'A' | 'B';
    role?: 'PARTY' | 'CHECKER';
    note?: string;
    expectedProposalId?: string;
  } = {}
): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/agree`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** POST /api/trades/:id/escalate — hand a stuck trade to the arbiter pool. A reason is
 * required (it becomes the "Escalated: <reason>" log entry server-side). */
export async function escalateTrade(tradeId: string, reason: string): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/escalate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface RuleInput {
  chosenOption?: string;
  rationale: string;
  riskAcceptance?: { riskId?: string; residualBand?: string } | null;
  // Fix-drule — explicit confirm that the arbiter is ruling with zero
  // AGREED handshake parties. Only set true after the UI's warning is
  // acknowledged; the backend 409s with NO_SIGNED_PARTIES otherwise.
  forceNoSignedParties?: boolean;
}

/** POST /api/trades/:id/rule — arbiter only (server-enforced via canArbitrate); body is
 * FLAT (chosenOption/rationale/riskAcceptance), matching RuleSchema in
 * interlockTrade.routes.ts — NOT wrapped in a `{ruling: ...}` envelope. The acting
 * arbiter rides via the existing ?actAs (withActor), not a body field. */
export async function ruleTrade(tradeId: string, input: RuleInput): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/rule`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** POST /api/trades/:id/acknowledge — a party acknowledges a ruling (resolves their
 * open ACKNOWLEDGE turn). */
export async function acknowledgeTrade(tradeId: string): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * POST /api/trades/:id/close — CONVERGED only; locks the value into Interface.spec.
 * X1/Y2: when the trade `requiresCountersign` and no CHECKER has signed, the backend
 * throws a ConflictError (409) instead of closing — see {@link isCountersignConflict}.
 * Y2 (round 4): the server no longer decides the override by role — an admin's first
 * call still gets the 409 same as anyone else. The ONLY way to close over a missing
 * countersign is an explicit `{override: true}` body, which the frontend sends solely
 * from the second-step confirm (never on the initial attempt) so the override is a
 * recorded, deliberate act rather than a silent role-based bypass.
 */
export async function closeTrade(
  tradeId: string,
  opts?: { override?: boolean }
): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/close`, {
    method: 'POST',
    body: JSON.stringify(opts?.override ? { override: true } : {}),
  });
}

/**
 * Y1 (round 4) — POST /api/trades/:id/checkers: the invite affordance the missing-key
 * audit finding demanded. Owner / party / interface-owner / ADMIN may invite (the
 * server enforces this — the frontend only gates the button's *visibility*, never
 * treats absence of the gate as permission). Creates a PENDING CHECKER handshake +
 * a COUNTERSIGN turn + a bell notification for the invitee. Returns full detail.
 */
export async function inviteChecker(
  tradeId: string,
  userId: string,
  side?: 'A' | 'B'
): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/checkers`, {
    method: 'POST',
    body: JSON.stringify(side ? { userId, side } : { userId }),
  });
}

/**
 * P1 — POST /api/trades/:id/parties: assign a PARTY signatory to an existing
 * trade (the "big gap" fix — a trade created with no parties could never
 * converge, notify, or gate, and the UI offered no repair). `side` is
 * REQUIRED — unlike inviteChecker's independent-verifier default, a party
 * genuinely belongs to demand (A) or supply (B). Owner / party / interface-
 * owner / ADMIN may assign (the server enforces this; the frontend only
 * gates the affordance's *visibility*). Creates a PENDING PARTY handshake +
 * a HANDSHAKE turn + fires the real "your turn" bell/email. Returns full detail.
 */
export async function addParty(
  tradeId: string,
  userId: string,
  side: 'A' | 'B'
): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/parties`, {
    method: 'POST',
    body: JSON.stringify({ userId, side }),
  });
}

/**
 * P1 — DELETE /api/trades/:id/parties: un-assign a PENDING party. The server
 * 409s if the party has already AGREED (reopen the trade first) — this is
 * never a way to erase a signed signature, only to fix a mis-assignment
 * before anyone has acted on it.
 */
export async function removeParty(tradeId: string, userId: string): Promise<TradeDetail> {
  return requestJson<TradeDetail>(
    `/trades/${tradeId}/parties?userId=${encodeURIComponent(userId)}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * K1 (R7 W2) — POST /api/trades/:id/stakeholders: add a non-signatory watcher
 * (party/owner/admin only — server enforces it via the actor gate; the frontend
 * only gates the affordance's *visibility*). Returns the created TradeStakeholder
 * row (NOT a full TradeDetail), so the caller should refetch the trade afterwards.
 */
export async function addStakeholder(
  tradeId: string,
  input: { userId: string; domain: string; reason?: string | null }
): Promise<StakeholderRow> {
  return requestJson<StakeholderRow>(`/trades/${tradeId}/stakeholders`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** POST /api/trades/:id/reopen — legal from CLOSED / RESOLVED_BY_RULING / ESCALATED
 * (and CONVERGED). `reason` is optional server-side but the UI requires it. */
export async function reopenTrade(tradeId: string, reason: string): Promise<TradeDetail> {
  return requestJson<TradeDetail>(`/trades/${tradeId}/reopen`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface CreateTradeInput {
  // Model A handshake — refId/title are optional when `crossingId` is set: the
  // "raise for negotiation" flow sends only the crossing and the backend derives
  // a refId (`T-<crossing.refId>`) and title from it.
  refId?: string;
  title?: string;
  // Model A handshake — anchor the trade to the Crossing it negotiates. When set,
  // the backend seeds demand/supply from the crossing and, on settle, writes the
  // converged value back to Crossing.agreedText (status → HANDSHAKED). If the
  // crossing already has a live trade, the backend returns that one (no dupe).
  crossingId?: string | null;
  demandNeed?: string | null;
  supplyLimit?: string | null;
  options?: string | null;
  gateRef?: string | null;
  // P1 — seed the initial PARTY signatories at creation time (mirrors
  // CreateTradeSchema.handshakes server-side). Without this a freshly-created
  // trade has zero signatories and can never converge/notify/gate until
  // someone finds the (previously nonexistent) affordance to add one.
  handshakes?: { userId: string; side: 'A' | 'B'; role?: 'PARTY' | 'CHECKER' }[];
}

/**
 * C1a (round 9 persona fix) — POST /api/interfaces/:id/trades: the create
 * affordance the audit found missing ("system of record you can't write to" —
 * every trade in the app had to be seeded, never authored). `interfaceId` is
 * the interface's DB id (see {@link InterfaceDetail.id}), NOT its refId — the
 * route only does `findFirst({ where: { id } })` server-side (unlike GET
 * /interfaces/:id, which resolves either). Body matches CreateTradeSchema in
 * interlockTrade.routes.ts. Returns the new TradeRecord on 201 — the caller
 * should refetch the interface detail (for the trades list) or route straight
 * to the new trade's page.
 */
export async function createTrade(
  interfaceId: string,
  input: CreateTradeInput
): Promise<{ id: string; refId: string }> {
  return requestJson<{ id: string; refId: string }>(`/interfaces/${interfaceId}/trades`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * S1 (surfaces parcel) — the register had a per-interface "+ New trade" but no way
 * to create the INTERFACE itself (the register was read-only for the entity it's
 * meant to be a working list of). Body matches CreateInterfaceSchema in
 * interlockInterface.routes.ts: `register`/`sideAId`/`sideBId` are REQUIRED — the
 * caller resolves side pickers against the same component list the rest of the app
 * uses (see `getComponents` in api/components.ts). Server-side this is admin-gated
 * (`isAdminActor`); a non-admin actor gets a 403 InterlockApiError the caller should
 * surface via `isForbidden()`, same idiom as every other write path here.
 */
export interface CreateInterfaceInput {
  refId: string;
  register: 'EXT' | 'INT';
  sideAId: string;
  sideBId: string;
  type: string;
  crosses?: string | null;
  spec?: string | null;
  neededBy?: string | null;
  ownerUserId?: string | null;
  ownerText?: string | null;
  externalCounterparty?: string | null;
  ownerDiscipline?: string | null;
  // R2-A2b (settle-control parcel) — mutable via PATCH only in practice (create
  // always defaults to OPEN server-side, see interface.service.ts); surfaced here
  // too since UpdateInterfaceInput derives from this via Omit/Partial below.
  status?: BackendInterfaceStatus;
}

export async function createInterface(
  input: CreateInterfaceInput
): Promise<{ id: string; refId: string }> {
  return requestJson<{ id: string; refId: string }>(`/projects/${PROJECT_ID}/interfaces`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============================================================================
// Data Requests (L1 — DrPanel, Wave 3). GET /api/projects/:id/data-requests
// (list; no `links` in the row — only `_count.links`) + the per-DR "what does
// this gate" derivation, which is NOT exposed as its own endpoint. Per the
// parcel brief: batch through the existing interface-detail endpoint (each
// InterfaceDetail already carries `links.dataRequests[].refId` — real field,
// see interface.service.ts) rather than inventing a reverse-lookup route.
// Dataset is small (tens of interfaces) so an N-way parallel fetch is cheap.
// ============================================================================

export type DataRequestStatus = 'OPEN' | 'ANSWERED' | 'CLOSED';

/** V8 — one entry of DataRequest.chaseLog (dataRequest.service#chase). */
export interface ChaseLogEntry {
  byUserId: string | null;
  at: string;
  note: string;
}

export interface DataRequestRow {
  id: string;
  refId: string;
  ask: string;
  status: DataRequestStatus;
  answerSummary: string | null;
  evidenceRef: string | null;
  answeredAt: string | null;
  createdAt: string;
  linkCount: number;
  /** V8 — chase trail, oldest-first. Empty array = never chased. */
  chaseLog: ChaseLogEntry[];
  // Z11 — additive; the backend half of this contract (parallel parcel B1) sets
  // answeredByUserId on answer() and resolves answeredByName in every DR
  // serializer. Read defensively — absent on backends that haven't shipped it.
  answeredByUserId?: string | null;
  answeredByName?: string | null;
}

function adaptChaseLog(raw: unknown): ChaseLogEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((e) => {
    const o = e as Record<string, unknown>;
    return {
      byUserId: pick<string>(o, 'byUserId') ?? null,
      at: pick<string>(o, 'at') ?? new Date().toISOString(),
      note: pick<string>(o, 'note') ?? '',
    };
  });
}

function adaptDataRequest(raw: unknown): DataRequestRow {
  const o = raw as Record<string, unknown>;
  const count = pick<Record<string, unknown>>(o, '_count');
  return {
    id: pick<string>(o, 'id') ?? '',
    refId: pick<string>(o, 'refId', 'ref') ?? '—',
    ask: pick<string>(o, 'ask') ?? '',
    status: ((pick<string>(o, 'status') ?? 'OPEN').toUpperCase() as DataRequestStatus) ?? 'OPEN',
    answerSummary: pick<string>(o, 'answerSummary') ?? null,
    evidenceRef: pick<string>(o, 'evidenceRef') ?? null,
    answeredAt: pick<string>(o, 'answeredAt') ?? null,
    createdAt: pick<string>(o, 'createdAt') ?? new Date().toISOString(),
    linkCount: (count ? pick<number>(count, 'links') : undefined) ?? 0,
    chaseLog: adaptChaseLog(pick<unknown>(o, 'chaseLog')),
    answeredByUserId: pick<string>(o, 'answeredByUserId') ?? null,
    answeredByName: pick<string>(o, 'answeredByName') ?? null,
  };
}

/** GET /api/projects/:id/data-requests → DataRequestRow[]. Never throws — caller
 * (DrPanel) decides how to render an empty/error result; there is no fixture for
 * DRs (this is a small, real, live-only surface). */
export async function fetchDataRequests(): Promise<DataRequestRow[]> {
  // Page through (PERF3) — the DR panel/home count need the full set, not the
  // first 200. See getAllPagedArray.
  const rows = await getAllPagedArray(`/projects/${PROJECT_ID}/data-requests`);
  return rows.map(adaptDataRequest);
}

/**
 * C1b (round 9 persona fix) — POST /api/projects/:id/data-requests: the create
 * affordance for raising a DR (previously only seedable). Body matches
 * CreateDrSchema in interlockDataRequest.routes.ts: `{refId, ask, status?,
 * evidenceRef?, interfaceId?}`.
 *
 * R24 follow-up (silent-DR-orphan, HIGH) — pass `interfaceId` when the DR is
 * being raised FROM an interface (RaiseDrControl on InterfaceDetailPage): the
 * backend link-on-creates the InterfaceDataRequestLink regardless of interface
 * ownership, so the DR always shows up under that interface's "DATA REQUESTS"
 * group. This replaced a separate client-side `linkDataRequestToInterface`
 * call that was gated by owner/admin `canLink` — a non-owner's DR was created
 * (201) but the follow-up link 403'd, so it silently orphaned.
 */
export async function createDataRequest(input: {
  refId: string;
  ask: string;
  evidenceRef?: string | null;
  interfaceId?: string | null;
}): Promise<DataRequestRow> {
  const body = await requestJson<unknown>(`/projects/${PROJECT_ID}/data-requests`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return adaptDataRequest(unwrap(body));
}

/**
 * R24 (DR-F2) — POST /api/interfaces/:id/links {kind:'DATA_REQUEST', targetId}.
 * Creates the InterfaceDataRequestLink that `createDataRequest` never wrote, so a
 * DR raised from an interface actually attaches to it (shows under the interface's
 * DATA REQUESTS group and participates in co-gating) instead of orphaning. The
 * route runs `assertInterfaceOwnerOrAdmin`, so the caller must be the interface
 * owner or an ADMIN — the raise-DR UI only chains this when that holds. `targetId`
 * is the DR's primary id (DataRequestRow.id), not its refId. `tradeId` is optional
 * (null = a bare interface↔DR link, not scoped to a specific trade). */
export async function linkDataRequestToInterface(
  interfaceId: string,
  dataRequestId: string,
  tradeId?: string | null
): Promise<void> {
  await requestJson<unknown>(`/interfaces/${interfaceId}/links`, {
    method: 'POST',
    body: JSON.stringify({
      kind: 'DATA_REQUEST',
      targetId: dataRequestId,
      tradeId: tradeId ?? null,
    }),
  });
}

/** POST /api/data-requests/:id/chase — V8: append a chase note; the backend also
 * echoes it into every open trade this DR gates (single call does both — see
 * dataRequest.service#chase). Returns the updated DR (fresh chaseLog). */
export async function chaseDataRequest(drId: string, note: string): Promise<DataRequestRow> {
  const body = await requestJson<unknown>(`/data-requests/${drId}/chase`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
  return adaptDataRequest(unwrap(body));
}

/** X7 — POST /api/data-requests/:id/answer. Body per interlockDataRequest.routes.ts
 * AnswerSchema: `{ summary, evidenceRef? }`. Actor is resolved server-side from the
 * request headers (`resolveActor`), same as chase — no actor field in the body.
 * Returns the updated DR (status flips to ANSWERED). */
export async function answerDataRequest(
  drId: string,
  summary: string,
  evidenceRef?: string | null
): Promise<DataRequestRow> {
  const body = await requestJson<unknown>(`/data-requests/${drId}/answer`, {
    method: 'POST',
    body: JSON.stringify({ summary, evidenceRef: evidenceRef ?? null }),
  });
  return adaptDataRequest(unwrap(body));
}

const OPEN_TRADE_STATES = new Set(['OPEN', 'NEGOTIATING', 'ESCALATED']);

export interface DrGatedTrade {
  id: string; // trade DB id — needed to append the chase-note log entry
  refId: string;
  title: string;
  state: string;
}

export interface DrGatedInterface {
  refId: string;
  label: string; // sideA ↔ sideB / crosses, for a friendly chip title
  openTrades: DrGatedTrade[];
}

/**
 * What does this DR gate? Batches `GET /api/interfaces/:id` across every project
 * interface (via the list endpoint for id/refId pairs) and keeps the ones whose
 * `links.dataRequests` includes this DR's refId — the same field
 * InterfaceDetailPage already renders. Each gated interface's `tradesSummary` is
 * filtered to OPEN|NEGOTIATING|ESCALATED for the "open trades" chase-note targets.
 */
export async function fetchDrGating(drRefId: string): Promise<DrGatedInterface[]> {
  const listBody = await getJson<unknown>(`/projects/${PROJECT_ID}/interfaces`);
  const rows = asArray(listBody) as Record<string, unknown>[];
  const results = await Promise.all(
    rows.map(async (r): Promise<DrGatedInterface | null> => {
      const id = pick<string>(r, 'id');
      const refId = pick<string>(r, 'refId', 'ref');
      if (!id || !refId) return null;
      try {
        const detailBody = await getJson<unknown>(`/interfaces/${id}`);
        const o = unwrap<Record<string, unknown>>(detailBody) ?? {};
        const links = pick<Record<string, unknown>>(o, 'links') ?? {};
        const drLinks = Array.isArray(links.dataRequests)
          ? (links.dataRequests as Record<string, unknown>[])
          : [];
        const matched = drLinks.some((l) => pick<string>(l, 'refId') === drRefId);
        if (!matched) return null;

        const rawTrades = pick<unknown>(o, 'tradesSummary', 'trades');
        const trades = (Array.isArray(rawTrades) ? rawTrades : []) as Record<string, unknown>[];
        const openTrades: DrGatedTrade[] = trades
          .filter((t) => OPEN_TRADE_STATES.has((pick<string>(t, 'state') ?? '').toUpperCase()))
          .map((t) => ({
            id: pick<string>(t, 'id') ?? '',
            refId: pick<string>(t, 'refId', 'ref') ?? '—',
            title: pick<string>(t, 'title', 'name') ?? '',
            state: (pick<string>(t, 'state') ?? '').toUpperCase(),
          }));

        const sideA = pick<Record<string, unknown>>(o, 'sideA');
        const sideB = pick<Record<string, unknown>>(o, 'sideB');
        const label =
          pick<string>(o, 'crosses') ??
          [
            sideA && pick<string>(sideA, 'componentName'),
            sideB && pick<string>(sideB, 'componentName'),
          ]
            .filter(Boolean)
            .join(' ↔ ') ??
          refId;
        return { refId, label: label || refId, openTrades };
      } catch {
        return null;
      }
    })
  );
  return results.filter((r): r is DrGatedInterface => r !== null);
}

/** W-F6 (round 9) — "1 interfaces" → "1 interface": a shared pluralize so every
 * count label in the app (workshop scope, sibling counts, ...) picks the noun
 * form off the actual count instead of hand-rolling `n === 1 ? '' : 's'` (or
 * forgetting to) at each call site. */
export function pluralize(n: number, singular: string, plural = `${singular}s`): string {
  return n === 1 ? singular : plural;
}

/** "asked 12d ago" / "asked 3h ago" / "asked just now" — shared so L2's turn-aging
 * rows ("waiting 3d") can reuse the same phrasing rules without re-deriving them. */
export function ageFromISO(iso: string, prefix = ''): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const ms = Date.now() - then;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return `${prefix}just now`;
  if (mins < 60) return `${prefix}${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${prefix}${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${prefix}${days}d ago`;
}

// ============================================================================
// Notifications (bell) — §9.5. Actor-scoped via the dev ?actAs hatch.
// ============================================================================

export interface InterlockNotification {
  id: string;
  title: string;
  message: string;
  entityType: string | null;
  entityId: string | null;
  actionUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationsResult {
  data: InterlockNotification[];
  unreadCount: number;
}

/** GET /api/me/notifications — the acting user's bell feed + unread count. */
export async function fetchNotifications(limit = 30): Promise<NotificationsResult> {
  const body = await requestJson<{ data: InterlockNotification[]; unreadCount?: number }>(
    `/me/notifications?limit=${limit}`
  );
  return { data: body.data ?? [], unreadCount: body.unreadCount ?? 0 };
}

/** PATCH /api/notifications/:id/read — mark one read. */
export async function markNotificationRead(id: string): Promise<void> {
  await requestJson(`/notifications/${id}/read`, { method: 'PATCH' });
}

/** PATCH /api/me/notifications/read-all — clear the acting user's unread count. */
export async function markAllNotificationsRead(): Promise<void> {
  await requestJson(`/me/notifications/read-all`, { method: 'PATCH' });
}

export { getActorId };

// ============================================================================
// Notification preferences (UX Wave A1 — profile popover). Backed by the
// legacy (pre-Interlock) `/api/notifications/preferences/:userId` router —
// keyed by an explicit userId in the path rather than the actAs-derived actor,
// so these calls pass the acting user's id straight through. Best-effort: the
// popover degrades to a plain note if either call fails (e.g. 404).
// ============================================================================

export interface NotificationPreferences {
  emailEnabled: boolean;
  inAppEnabled: boolean;
  categories: Record<string, boolean>;
}

/** GET /api/notifications/preferences/:userId */
export async function fetchNotificationPreferences(
  userId: string
): Promise<NotificationPreferences> {
  const body = await requestJson<{ success: boolean; data: NotificationPreferences }>(
    `/notifications/preferences/${encodeURIComponent(userId)}`
  );
  return body.data;
}

/**
 * PUT /api/notifications/preferences/:userId
 *
 * `categories` accepts a partial patch (e.g. `{ RPN_CHANGE: false }`) — the
 * backend merges it onto the existing category map (notification.service.ts's
 * updatePreferences: `{ ...existing.categories, ...updates.categories }`), it
 * does not replace the whole object. UX Wave 1 — the Settings page's category
 * toggles are the first UI to exercise this (the type only covered
 * emailEnabled/inAppEnabled before). NOTE: the backend's yup schema accepted
 * the `categories` KEY but — shapeless under stripUnknown:true — silently
 * discarded its contents until the PR #135 fix gave it a per-key boolean shape
 * (notification.routes.ts's categoriesPatchSchema); against an older backend a
 * category patch is a silent no-op, not an error.
 */
export async function updateNotificationPreferences(
  userId: string,
  updates: Partial<Pick<NotificationPreferences, 'emailEnabled' | 'inAppEnabled'>> & {
    categories?: Record<string, boolean>;
  }
): Promise<NotificationPreferences> {
  const body = await requestJson<{ success: boolean; data: NotificationPreferences }>(
    `/notifications/preferences/${encodeURIComponent(userId)}`,
    { method: 'PUT', body: JSON.stringify(updates) }
  );
  return body.data;
}

// ============================================================================
// Admin — user roster + role/lifecycle management (UX Wave A1 admin page).
// Backed by auth.routes.ts (admin-gated; local-admin/dev bypasses the guard).
// The roster opts into `?includeInactive=true` (the endpoint's default stays
// active-only for the dev switcher / assign pickers) so deactivated users
// stay visible and reactivatable here.
// ============================================================================

export interface AdminUserRow {
  id: string;
  email: string | null;
  name: string;
  role: 'ADMIN' | 'ENGINEER' | 'VIEWER' | 'MANAGER' | 'PROJECT_MANAGER' | 'GUEST';
  isActive: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  avatarUrl?: string | null; // uploaded profile photo → avatar in the admin roster
}

/** GET /api/auth/users?includeInactive=true (admin only) */
export async function fetchAdminUsers(): Promise<AdminUserRow[]> {
  const body = await requestJson<{ success: boolean; data: AdminUserRow[] }>(
    '/auth/users?includeInactive=true'
  );
  return body.data ?? [];
}

/** PATCH /api/auth/users/:userId/role (admin only) */
export async function updateAdminUserRole(
  userId: string,
  role: AdminUserRow['role']
): Promise<AdminUserRow> {
  const body = await requestJson<{ success: boolean; data: AdminUserRow }>(
    `/auth/users/${encodeURIComponent(userId)}/role`,
    { method: 'PATCH', body: JSON.stringify({ role }) }
  );
  return body.data;
}

/** DELETE /api/auth/users/:userId — soft-deactivate (admin only) */
export async function deactivateAdminUser(userId: string): Promise<void> {
  await requestJson(`/auth/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

/** POST /api/auth/users/:userId/reactivate (admin only) */
export async function reactivateAdminUser(userId: string): Promise<AdminUserRow> {
  const body = await requestJson<{ success: boolean; data: AdminUserRow }>(
    `/auth/users/${encodeURIComponent(userId)}/reactivate`,
    { method: 'POST' }
  );
  return body.data;
}

// ============================================================================
// Interface detail (datasheet C) — view-model + adapter + fetch.
// Backend: GET /api/interfaces/:id (keyed by DB id) returns the resolved detail
// (sides → component names, links with refs+titles, trades summarized). The route
// param the pages emit is the *refId* (EXT-09), so we resolve refId → id via the
// list endpoint first, then fetch the detail.
// ============================================================================

export interface DetailLink {
  refId: string | null;
  label: string | null;
  status?: string | null; // DR status (OPEN / ANSWERED / CLOSED)
  riskKind?: string; // EXTERNAL_RISK | FAILURE_MODE
  tradeId?: string | null; // set → this link came in via a trade (provenance)
  // Ontology O5 — the linked entity's DB id (backend `targetId`), needed for the
  // `/requirements?focus=<id>` deep link the trade page's bound chips use. The
  // trades-first interface page renders interface-scoped requirement links as
  // "inherited bounds" inside each trade card and mirrors that chip shape.
  targetId?: string | null;
  // Ontology O5 — requirement version (same "· rev N" marker as trade-page bounds).
  version?: number | null;
  // provenance — 'INJECTED' backfilled rows (e.g. data requests) render red+bold.
  reviewState?: 'INJECTED' | 'REVIEWED';
}

export interface DetailReference {
  kind: string; // SIBLING | ANNEX
  targetRef: string; // an interface refId (SIBLING) or external anchor (ANNEX)
}

/**
 * xref-sections wave — a failure mode captured directly against this
 * interface (CauseEffect.interfaceId, §7 D3/M1: "what could break THIS
 * agreement?"). Distinct from `risks` above (interface-scoped RISK pull-links,
 * which point at a risk that MAY be a FAILURE_MODE twin).
 */
export interface DetailFailureMode {
  id: string;
  refId: string | null;
  title: string | null;
  severity: number | null;
  occurrence: number | null;
  detection: number | null;
  rpn: number | null;
  scoreProvenance: string | null;
  category: string | null;
}

/** Matrix quick-view (ux/matrix-quickview) — one handshake row on a trade, as the
 * raw GET /interfaces/:id response already carries them (the response spreads the
 * Prisma include, so `trades[].handshakes` rides along next to `tradesSummary`;
 * the adapter used to drop it). PARTY rows drive the side-A/B avatars. */
export interface DetailTradeParty {
  userId: string;
  side: 'A' | 'B';
  role: 'PARTY' | 'CHECKER';
  state: 'PENDING' | 'AGREED' | 'DECLINED';
}

export interface DetailTrade {
  // Ontology O5 — the trade's DB id (tradesSummary carries it), needed to match
  // link rows' `tradeId` scoping (trade-scoped vs interface-scoped/inherited).
  id?: string;
  refId: string; // 'EXT-09-T1'
  title: string;
  state: string; // raw TradeState enum
  convergedValue: string | null;
  // C3-R5 (B1/C2) — true when the trade carries an arbiter ruling (settled by
  // ruling, not mutual convergence). Lets the register card say "ruled" honestly
  // even after a RESOLVED_BY_RULING trade closes to state CLOSED.
  ruled: boolean;
  pendingSignatures: number;
  logEntryCount: number;
  stakeholderCount: number;
  // Matrix quick-view enrichment — optional (absent on fixture rows and on any
  // older backend whose detail response doesn't carry the raw `trades` include).
  /** Handshake rows (parties + checkers) merged in from the raw `trades` include. */
  parties?: DetailTradeParty[];
  /** ISO timestamp of the trade row's last write — the cheap "latest activity" hint. */
  updatedAt?: string | null;
  // provenance — 'INJECTED' backfilled trades render red+bold until reviewed.
  reviewState?: 'INJECTED' | 'REVIEWED';
}

export interface InterfaceDetail {
  // R9 C1a — the DB id, needed to POST /interfaces/:id/trades (that route takes
  // the raw id only, unlike GET /interfaces/:id which also resolves a refId).
  // Optional: the fixture fallback (fixtureDetail below) has no real id, so a
  // caller gating a create affordance on this must treat undefined as "can't create".
  id?: string;
  // Optimistic-lock version (L9 audit) — resent on edit so the backend CAS can
  // 409 a stale write instead of silently clobbering a concurrent edit. Absent
  // on the fixture fallback.
  version?: number;
  refId: string;
  register: InterfaceRegister;
  // Bugfix (persona-audit Loop 2, #4) — the label the detail page shows
  // ("Internal interface" / "External interface") used to read `register`
  // directly, which is a value stored at interface-creation time (originally
  // org-based) and never re-derived when the systemClass wave shipped. Two
  // same-org endpoints where one is SUPPORT/EXTERNAL (e.g. Ring [SYSTEM] ↔
  // Sim-rig [SUPPORT]) still read `register: 'int'` even though the boundary
  // chip (interface matrix) correctly counts them as crossing. Server-computed
  // from the endpoints' live systemClass (interface.service.ts
  // getInterfaceDetail) — true whenever either side isn't SYSTEM. Optional:
  // absent from the register-list fixture fallback, which falls back to
  // `register` (see adaptInterfaceDetail / fixtureDetail below).
  crossesBoundary?: boolean;
  status: InterfaceStatus;
  // provenance — 'INJECTED' backfilled interface renders its title/spec red+bold
  // until reviewed. Optional (absent on the fixture fallback → treated REVIEWED).
  reviewState?: 'INJECTED' | 'REVIEWED';
  type: string;
  sideA: string; // component name
  sideB: string;
  // R2-A2 (r2b parcel) — the raw component ids, needed to prefill/re-submit the
  // side pickers on the new interface-edit form (PATCH sideAId/sideBId). The raw
  // API already selects `sideA.id`/`sideB.id` (see interface.service.ts's
  // getInterfaceDetail include) — these were just never surfaced by the adapter.
  // Optional: the fixture fallback has no real ids.
  sideAId?: string;
  sideBId?: string;
  crosses: string | null;
  spec: string | null;
  neededBy: string | null;
  owner: string | null; // display owner text ('QCells', 'Shijin/Alex') — INFORMATIONAL ONLY
  ownerInitials: string[]; // avatar initials, drive OwnerAvatar
  // OWN1 (round 13) — the single FK authz actually keys on (trade.service.ts
  // createTrade: `actorUserId === iface.ownerUserId`). Distinct from `owner`
  // above, which is free text (`ownerText`) that may name people who are NOT
  // this user and have no bearing on who the server lets start a trade.
  ownerUserId: string | null;
  externalCounterparty: string | null;
  ownerDiscipline: string | null;
  elevated: boolean;
  specTradeRef: string | null; // spec value converged from this trade (provenance)
  requirements: DetailLink[];
  risks: DetailLink[];
  tests: DetailLink[];
  dataRequests: DetailLink[];
  references: DetailReference[];
  trades: DetailTrade[];
  /** xref-sections wave — see DetailFailureMode. Empty (never undefined) on
   *  the fixture fallback — there is no fixture-scale FMEA data to fake here. */
  failureModes: DetailFailureMode[];
  // Model A — the interface's crossings (one demand/supply/agreed line item per
  // discipline), same shape the matrix cell payload returns. Empty (never
  // undefined) on the fixture fallback. Drives the crossing-centric detail page.
  crossings: MatrixCellCrossing[];
  // Model A — each side's real Component.systemClass (SYSTEM | SUPPORT |
  // EXTERNAL), when the backend detail payload carries it (sideA/sideB.systemClass).
  // Optional: absent on the fixture fallback (no systemClass data there).
  sideAClass?: SystemClassBand;
  sideBClass?: SystemClassBand;
  // Model A — true when BOTH endpoints are SYSTEM (delivered hardware), so this
  // is a hardware-vs-hardware interface where FMEA failure modes belong. Derived
  // from sideA/sideB.systemClass when present; otherwise from `crossesBoundary`
  // (server-derived, itself SYSTEM-on-both), which falls back to register 'int'
  // (INT ≈ hardware / EXT ≈ external — heuristic when systemClass is unavailable,
  // e.g. the register fixture). The inverse of "crosses the SYSTEM boundary".
  isHardwareInterface: boolean;
}

const NAME_INITIALS: Record<string, string> = {
  shijin: 'SH',
  alex: 'AL',
  james: 'JM',
  priya: 'PR',
  qcells: 'QC',
  qc: 'QC',
};

function ownerInitialsFrom(text?: string | null): string[] {
  if (!text) return [];
  return text
    .split(/[/,&]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((n) => NAME_INITIALS[n.toLowerCase()] ?? n.slice(0, 2).toUpperCase());
}

const CONVERGED_STATES = new Set(['CONVERGED', 'CLOSED', 'RESOLVED_BY_RULING']);

/** GET /api/interfaces/:id → InterfaceDetail */
export function adaptInterfaceDetail(body: unknown): InterfaceDetail | undefined {
  const o = unwrap<Record<string, unknown>>(body);
  if (!o || typeof o !== 'object') return undefined;
  const refId = pick<string>(o, 'refId', 'ref', 'id');
  if (!refId) return undefined;
  const register =
    (pick<string>(o, 'register', 'reg') ?? 'int').toLowerCase() === 'ext' ? 'ext' : 'int';
  const sideAObj = pick<Record<string, unknown>>(o, 'sideA');
  const sideBObj = pick<Record<string, unknown>>(o, 'sideB');
  const sideA =
    (sideAObj && pick<string>(sideAObj, 'componentName', 'name')) ??
    pick<string>(o, 'sideAName') ??
    '—';
  const sideB =
    (sideBObj && pick<string>(sideBObj, 'componentName', 'name')) ??
    pick<string>(o, 'sideBName') ??
    '—';
  // OWN1 — `owner` (the display strip) stays ownerText-first for backwards
  // compat with existing copy, but no longer falls back to the raw
  // `ownerUserId` uuid (that read like a person's name and wasn't one).
  // `ownerUserId` below is surfaced separately and resolved to a real name by
  // the page — see InterfaceDetailPage's OwnerStrip.
  const owner = pick<string>(o, 'ownerText') ?? pick<string>(o, 'externalCounterparty') ?? null;
  const ownerUserId = pick<string>(o, 'ownerUserId') ?? null;

  // Model A — each side's real systemClass (interface.service.ts getInterfaceDetail
  // selects sideA/sideB.systemClass). Optional: absent on older payloads.
  const asSystemClass = (v: string | undefined): SystemClassBand | undefined => {
    const u = (v ?? '').toUpperCase();
    return u === 'SYSTEM' || u === 'SUPPORT' || u === 'EXTERNAL'
      ? (u as SystemClassBand)
      : undefined;
  };
  const sideAClass = sideAObj ? asSystemClass(pick<string>(sideAObj, 'systemClass')) : undefined;
  const sideBClass = sideBObj ? asSystemClass(pick<string>(sideBObj, 'systemClass')) : undefined;
  // crossesBoundary is the server-derived "either side isn't SYSTEM" truth,
  // falling back to register === 'ext'. A hardware interface is the inverse:
  // both sides SYSTEM. Prefer the two real systemClasses when we have them.
  const crossesBoundary = pick<boolean>(o, 'crossesBoundary') ?? register === 'ext';
  const isHardwareInterface =
    sideAClass && sideBClass
      ? sideAClass === 'SYSTEM' && sideBClass === 'SYSTEM'
      : !crossesBoundary;

  const links = pick<Record<string, unknown>>(o, 'links') ?? {};
  const linkArr = (key: string): Record<string, unknown>[] => {
    const v = (links as Record<string, unknown>)[key];
    return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
  };

  const requirements: DetailLink[] = linkArr('requirements').map((l) => ({
    refId: pick<string>(l, 'refId') ?? null,
    label: pick<string>(l, 'label') ?? null,
    tradeId: pick<string>(l, 'tradeId') ?? null,
    targetId: pick<string>(l, 'targetId') ?? null,
    version: pick<number>(l, 'version') ?? null,
  }));
  const risks: DetailLink[] = linkArr('risks').map((l) => ({
    refId: pick<string>(l, 'refId') ?? null,
    label: pick<string>(l, 'label') ?? null,
    riskKind: pick<string>(l, 'riskKind'),
    tradeId: pick<string>(l, 'tradeId') ?? null,
  }));
  const tests: DetailLink[] = linkArr('tests').map((l) => ({
    refId: pick<string>(l, 'refId') ?? null,
    label: pick<string>(l, 'label') ?? null,
    tradeId: pick<string>(l, 'tradeId') ?? null,
  }));
  const dataRequests: DetailLink[] = linkArr('dataRequests').map((l) => ({
    refId: pick<string>(l, 'refId') ?? null,
    label: pick<string>(l, 'label') ?? null,
    status: pick<string>(l, 'status') ?? null,
    tradeId: pick<string>(l, 'tradeId') ?? null,
    reviewState: pick<string>(l, 'reviewState') === 'INJECTED' ? 'INJECTED' : 'REVIEWED',
  }));
  const references: DetailReference[] = linkArr('references').map((l) => ({
    kind: (pick<string>(l, 'kind') ?? 'SIBLING').toUpperCase(),
    targetRef: pick<string>(l, 'targetRef') ?? '—',
  }));

  // xref-sections wave — top-level `failureModes` (NOT under `links` — it's
  // not a link table, it's CauseEffect.interfaceId, a direct FK on the FM row).
  const rawFailureModes = pick<unknown>(o, 'failureModes');
  const failureModes: DetailFailureMode[] = (
    Array.isArray(rawFailureModes) ? (rawFailureModes as Record<string, unknown>[]) : []
  ).map((fm) => ({
    id: pick<string>(fm, 'id') ?? '',
    refId: pick<string>(fm, 'refId') ?? null,
    title: pick<string>(fm, 'title') ?? null,
    severity: pick<number>(fm, 'severity') ?? null,
    occurrence: pick<number>(fm, 'occurrence') ?? null,
    detection: pick<number>(fm, 'detection') ?? null,
    rpn: pick<number>(fm, 'rpn') ?? null,
    scoreProvenance: pick<string>(fm, 'scoreProvenance') ?? null,
    category: pick<string>(fm, 'category') ?? null,
  }));

  const rawTrades = pick<unknown>(o, 'tradesSummary', 'trades');
  // Matrix quick-view enrichment — the response ALSO carries the raw Prisma
  // `trades` include (handshakes + updatedAt + _count), which `tradesSummary`
  // above intentionally strips. Index it by refId so each summary row can be
  // enriched with parties/updatedAt without changing the summary contract.
  const rawFullTrades = pick<unknown>(o, 'trades');
  const fullByRef = new Map<string, Record<string, unknown>>(
    (Array.isArray(rawFullTrades) ? (rawFullTrades as Record<string, unknown>[]) : []).map((r) => [
      pick<string>(r, 'refId') ?? '',
      r,
    ])
  );
  const partiesFrom = (r: Record<string, unknown> | undefined): DetailTradeParty[] | undefined => {
    const hs = r ? pick<unknown>(r, 'handshakes') : undefined;
    if (!Array.isArray(hs)) return undefined;
    return (hs as Record<string, unknown>[]).map((h) => ({
      userId: pick<string>(h, 'userId') ?? '',
      side: (pick<string>(h, 'side') === 'B' ? 'B' : 'A') as 'A' | 'B',
      role: (pick<string>(h, 'role') === 'CHECKER' ? 'CHECKER' : 'PARTY') as 'PARTY' | 'CHECKER',
      state: ((): DetailTradeParty['state'] => {
        const s = (pick<string>(h, 'state') ?? 'PENDING').toUpperCase();
        return s === 'AGREED' || s === 'DECLINED' ? s : 'PENDING';
      })(),
    }));
  };
  const trades: DetailTrade[] = (Array.isArray(rawTrades) ? rawTrades : []).map((t) => {
    const r = t as Record<string, unknown>;
    const refId = pick<string>(r, 'refId', 'ref') ?? '—';
    // When rawTrades fell back to the raw `trades` array itself (no tradesSummary),
    // `r` already carries handshakes/updatedAt — read from the map hit or from r.
    const full = fullByRef.get(refId) ?? r;
    return {
      id: pick<string>(r, 'id') ?? pick<string>(full, 'id'),
      refId,
      title: pick<string>(r, 'title', 'name') ?? '—',
      state: (pick<string>(r, 'state') ?? 'OPEN').toUpperCase(),
      convergedValue: pick<string>(r, 'convergedValue') ?? null,
      ruled: pick<boolean>(r, 'ruled') ?? false,
      pendingSignatures: pick<number>(r, 'pendingSignatures') ?? 0,
      logEntryCount: pick<number>(r, 'logEntryCount') ?? 0,
      stakeholderCount: pick<number>(r, 'stakeholderCount') ?? 0,
      parties: partiesFrom(full),
      updatedAt: pick<string>(full, 'updatedAt') ?? null,
      reviewState:
        pick<string>(r, 'reviewState') === 'INJECTED' ||
        pick<string>(full, 'reviewState') === 'INJECTED'
          ? 'INJECTED'
          : 'REVIEWED',
    };
  });

  // Spec provenance: resolve the trade that actually wrote the current spec
  // value at close-and-lock (Interface.specSourceTradeId), not a heuristic
  // guess. No match (older interfaces closed before this field existed, or
  // the source trade isn't in the loaded set) → null, so the "via" badge
  // hides rather than guesses.
  const specSourceTradeId = pick<string>(o, 'specSourceTradeId') ?? null;
  const specTrade = specSourceTradeId ? trades.find((t) => t.id === specSourceTradeId) : undefined;

  return {
    id: pick<string>(o, 'id'),
    version: pick<number>(o, 'version'),
    refId,
    register,
    crossesBoundary,
    status: normStatus(pick<string>(o, 'status', 'st')),
    reviewState: pick<string>(o, 'reviewState') === 'INJECTED' ? 'INJECTED' : 'REVIEWED',
    type: pick<string>(o, 'type', 'kind') ?? '—',
    sideA,
    sideB,
    sideAId: (sideAObj && pick<string>(sideAObj, 'id')) ?? undefined,
    sideBId: (sideBObj && pick<string>(sideBObj, 'id')) ?? undefined,
    crosses: pick<string>(o, 'crosses') ?? null,
    spec: pick<string>(o, 'spec') ?? null,
    neededBy: pick<string>(o, 'neededBy', 'neededByGate', 'gate') ?? null,
    owner,
    ownerInitials: ownerInitialsFrom(owner),
    ownerUserId,
    externalCounterparty: pick<string>(o, 'externalCounterparty') ?? null,
    ownerDiscipline: pick<string>(o, 'ownerDiscipline') ?? null,
    elevated: Boolean(pick<boolean>(o, 'elevated')),
    specTradeRef: specTrade?.refId ?? null,
    requirements,
    risks,
    tests,
    dataRequests,
    references,
    trades,
    failureModes,
    crossings: adaptCrossingRows(pick<unknown>(o, 'crossings')) ?? [],
    sideAClass,
    sideBClass,
    isHardwareInterface,
  };
}

/** Build a coherent detail view from the register fixture row (fallback only). */
function fixtureDetail(refId: string): InterfaceDetail | undefined {
  const row = FIXTURE_INTERFACES.find((i) => i.refId === refId);
  if (!row) return undefined;
  const [a, b] = row.title.split(' ↔ ');
  return {
    refId: row.refId,
    register: row.register,
    status: row.status,
    type: row.type,
    sideA: a ?? row.title,
    sideB: b ?? '—',
    crosses: null,
    spec: null,
    neededBy: row.neededBy,
    owner: row.owners.join('/'),
    ownerInitials: row.owners,
    ownerUserId: null, // fixture rows carry no real owner FK
    externalCounterparty: row.register === 'ext' ? 'QCells' : null,
    ownerDiscipline: null,
    elevated: Boolean(row.elevated),
    specTradeRef: null,
    requirements: [],
    risks: row.riskRef ? [{ refId: row.riskRef, label: null, riskKind: 'EXTERNAL_RISK' }] : [],
    tests: [],
    dataRequests: row.gatedDr ? [{ refId: row.gatedDr, label: null, status: 'OPEN' }] : [],
    references: [],
    trades: [],
    failureModes: [],
    crossings: [],
    // No systemClass data in the register fixture — fall back to the register
    // heuristic (INT ≈ hardware / EXT ≈ external).
    isHardwareInterface: row.register === 'int',
  };
}

export async function fetchInterfaceDetail(
  refId: string
): Promise<Fetched<InterfaceDetail | undefined>> {
  try {
    // Resolve refId → DB id via the list endpoint (detail is keyed by id).
    const listBody = await getJson<unknown>(`/projects/${PROJECT_ID}/interfaces`);
    const match = asArray(listBody).find(
      (r) => (pick<string>(r as Record<string, unknown>, 'refId', 'ref') ?? '') === refId
    ) as Record<string, unknown> | undefined;
    const id = match ? pick<string>(match, 'id') : undefined;
    if (!id) throw new Error('refId not found in register');
    const body = await getJson<unknown>(`/interfaces/${id}`);
    const data = adaptInterfaceDetail(body);
    if (!data) throw new Error('empty detail');
    return { data, fromFixture: false };
  } catch (e) {
    // Bug fix (persona-audit finding, dangerous-fallback class) — this used
    // to fall back to `fixtureDetail(refId)` unconditionally, in EVERY
    // environment, on ANY failure (network/5xx/429 included), rendering a
    // fully-populated-looking datasheet (status, owner, elevated flag, risks,
    // data requests) with only a small FixtureBadge pill as warning. Split
    // "genuinely doesn't exist" (the list loaded fine, or the backend 404'd,
    // and this refId just isn't in it — real not-found content, always safe
    // to show as such) from "the request actually failed" (network/5xx/429),
    // which now routes through the same RS5 fixtureOrHonestFail idiom as
    // every other Interlock fetch: DEV still demos off the fixture, PROD
    // gets an honest `error` flag + no data instead of a confident fake page.
    const notFound =
      (e instanceof InterlockApiError && e.status === 404) ||
      (e instanceof Error &&
        (e.message === 'refId not found in register' || e.message === 'empty detail'));
    if (notFound) return { data: undefined, fromFixture: false };
    return fixtureOrHonestFail(e, fixtureDetail(refId), undefined);
  }
}

// ============================================================================
// Risk detail (Campaign-2 Theme-3 Wave-1) — GET /api/projects/:id/risks/:ref
// ============================================================================

export type RiskLevelBand = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface RiskAffectedInterface {
  interfaceRefId: string;
  interfaceTitle: string;
  elevated: boolean;
}

export interface RiskDetail {
  ref: string;
  kind: 'EXTERNAL_RISK' | 'FAILURE_MODE';
  name: string | null;
  level: RiskLevelBand;
  ratingNumeric: number | null;
  description: string | null;
  componentName: string | null;
  // P4 (clickable-crosslinks) — the raw component id backing `componentName`,
  // so the page can link the chip to `/interlock/components/:id` instead of
  // rendering inert text. Null when the risk has no component anchor.
  componentId: string | null;
  affectedInterfaces: RiskAffectedInterface[];
  // Campaign-2 Theme-3 Wave-2 (E2) — risk TYPE + ids for a path to the FMEA
  // authoring surface. `category` is the ExternalRisk risk-TYPE taxonomy value
  // (e.g. 'Failure', 'Safety'); null for a bare FAILURE_MODE result.
  category: string | null;
  externalRiskId: string | null;
  causeEffectId: string | null;
}

/**
 * Resolve an RSK-NN display ref to its risk detail + back-linked interfaces.
 * Returns `undefined` (not a fixture) when the backend 404s — the page renders
 * an honest "not found" state rather than fabricating placeholder risk data.
 */
export async function fetchRisk(
  projectId: string,
  ref: string
): Promise<Fetched<RiskDetail | undefined>> {
  try {
    const body = await getJson<unknown>(`/projects/${projectId}/risks/${encodeURIComponent(ref)}`);
    const o = unwrap<Record<string, unknown>>(body);
    const levelRaw = (pick<string>(o, 'level') ?? 'LOW').toUpperCase();
    const level: RiskLevelBand = (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).includes(
      levelRaw as RiskLevelBand
    )
      ? (levelRaw as RiskLevelBand)
      : 'LOW';
    const affectedRaw = pick<unknown>(o, 'affectedInterfaces');
    const affectedInterfaces: RiskAffectedInterface[] = Array.isArray(affectedRaw)
      ? affectedRaw.map((raw) => {
          const a = raw as Record<string, unknown>;
          return {
            interfaceRefId: pick<string>(a, 'interfaceRefId') ?? '—',
            interfaceTitle: pick<string>(a, 'interfaceTitle') ?? '—',
            elevated: Boolean(pick<boolean>(a, 'elevated')),
          };
        })
      : [];
    const data: RiskDetail = {
      ref: pick<string>(o, 'ref') ?? ref,
      kind: pick<string>(o, 'kind') === 'FAILURE_MODE' ? 'FAILURE_MODE' : 'EXTERNAL_RISK',
      name: pick<string>(o, 'name') ?? null,
      level,
      ratingNumeric: pick<number>(o, 'ratingNumeric') ?? null,
      description: pick<string>(o, 'description') ?? null,
      componentName: pick<string>(o, 'componentName') ?? null,
      componentId: pick<string>(o, 'componentId') ?? null,
      affectedInterfaces,
      category: pick<string>(o, 'category') ?? null,
      externalRiskId: pick<string>(o, 'externalRiskId') ?? null,
      causeEffectId: pick<string>(o, 'causeEffectId') ?? null,
    };
    return { data, fromFixture: false };
  } catch (err) {
    if (err instanceof InterlockApiError && err.status === 404) {
      return { data: undefined, fromFixture: false };
    }
    // RS5 — a real failure (not a clean 404): surface as an honest error state
    // rather than silently rendering "not found" for a transient outage.
    return { data: undefined, fromFixture: false, error: true };
  }
}

/**
 * R22 (Author) — PATCH an interface. The backend (`PATCH /api/interfaces/:id`,
 * `UpdateInterfaceSchema`, owner-or-admin gated) has always supported editing
 * interface fields incl. the governing `ownerUserId`, but NO frontend call ever
 * hit it — the datasheet was a read-only viewer that even printed "none assigned
 * — admin only" as dead text with no way to assign. This first exposed just the
 * owner-assignment slice (the highest-value gap: every EXT interface ships
 * owner-null, so no one can start a trade there until an admin sets an owner).
 *
 * R2-A2 (r2b parcel) — widened `patch` to the full `UpdateInterfaceSchema` shape
 * (`CreateInterfaceSchema.partial().omit({refId:true})`) so the new interface
 * DETAIL-page Edit form (owner-or-admin gated, same as this call already is) can
 * PATCH type/crosses/spec/neededBy/sides/etc., not just ownerUserId.
 *
 * `id` is the DB uuid (InterfaceDetail.id), NOT the refId — the PATCH route
 * keys on id only. Server enforces authz; passing `ownerUserId: null` unassigns.
 */
export type UpdateInterfaceInput = Partial<Omit<CreateInterfaceInput, 'refId'>>;

export async function updateInterface(id: string, patch: UpdateInterfaceInput): Promise<void> {
  await requestJson<unknown>(`/interfaces/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ============================================================================
// FIXTURES — taken verbatim from the design canvases:
//   dev/active/interface-pivot/design/icd-layer.dc.html (REG + IF)
//   dev/active/interface-pivot/design/system-view.dc.html (nodes/edges)
// Fallback only; every page shows a "fixture data" badge when these are used.
// ============================================================================

export const FIXTURE_INTERFACES: InterfaceRow[] = [
  {
    refId: 'INT-01',
    register: 'int',
    title: 'Cleaning carriage ↔ Rotator',
    type: 'Mech',
    owners: ['SH'],
    status: 'open',
    neededBy: 'Concept-lock',
  },
  {
    refId: 'INT-02',
    register: 'int',
    title: 'Rotator ↔ Ring chassis',
    type: 'Mech+Elec',
    owners: ['SH', 'AL'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'INT-03',
    register: 'int',
    title: 'Rotator ↔ JuiceBox + ARC',
    type: 'Elec+Data',
    owners: ['AL', 'JM'],
    status: 'open',
    neededBy: 'Design release',
    openTrades: 1,
  },
  {
    refId: 'INT-04',
    register: 'int',
    title: 'Ring chassis ↔ Tether',
    type: 'Mech',
    owners: ['SH'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'INT-05',
    register: 'int',
    title: 'Ring chassis ↔ Launch tube',
    type: 'Mech',
    owners: ['SH'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'INT-06',
    register: 'int',
    title: 'Ring chassis ↔ Sim rig',
    type: 'Mech+Env',
    owners: ['SH'],
    status: 'open',
    neededBy: 'Concept-lock',
  },
  {
    refId: 'INT-07',
    register: 'int',
    title: 'Tether ↔ JuiceBox',
    type: 'Elec+Data',
    owners: ['AL'],
    status: 'std',
    neededBy: '—',
  },
  {
    refId: 'INT-08',
    register: 'int',
    title: 'JuiceBox ↔ Operator',
    type: 'Human',
    owners: ['JM'],
    status: 'std',
    neededBy: '—',
  },
  {
    refId: 'INT-09',
    register: 'int',
    title: 'Tether ↔ Camera',
    type: 'Data+Elec',
    owners: ['AL'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'INT-10',
    register: 'int',
    title: 'Ring chassis ↔ Operator (recovery)',
    type: 'Mech+Ctrl',
    owners: ['SH', 'AL'],
    status: 'undef',
    neededBy: 'Design release',
    openTrades: 1,
  },
  {
    refId: 'EXT-01',
    register: 'ext',
    title: 'Ring chassis ↔ Pull chamber',
    type: 'Geometry',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
    gatedDr: 'DR-01',
  },
  {
    refId: 'EXT-02',
    register: 'ext',
    title: 'Ring chassis ↔ Seed chuck',
    type: 'Clearance',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Concept-lock',
    gatedDr: 'DR-02',
    riskRef: 'RSK-04',
    elevated: true,
  },
  {
    refId: 'EXT-03',
    register: 'ext',
    title: 'Launch tube ↔ QCells ops',
    type: 'Logistics',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'EXT-04',
    register: 'ext',
    title: 'Launch tube ↔ Bottom features',
    type: 'Mech',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'EXT-05',
    register: 'ext',
    title: 'Ring chassis ↔ Bottom features',
    type: 'Mech',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
    riskRef: 'RSK-03',
  },
  {
    refId: 'EXT-06',
    register: 'ext',
    title: 'Cleaning carriage ↔ Pull chamber',
    type: 'Process',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
    riskRef: 'RSK-05',
  },
  {
    refId: 'EXT-07',
    register: 'ext',
    title: 'Cleaning carriage ↔ QCells ops',
    type: 'Accept',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'EXT-08',
    register: 'ext',
    title: 'Cleaning carriage ↔ QCells ops',
    type: 'Process',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'EXT-09',
    register: 'ext',
    title: 'Ring chassis ↔ Pull chamber wall',
    type: 'Contact',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
    gatedDr: 'DR-13',
    openTrades: 2,
    riskRef: 'RSK-03',
  },
  {
    refId: 'EXT-10',
    register: 'ext',
    title: 'JuiceBox ↔ Site facilities',
    type: 'Elec',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'EXT-11',
    register: 'ext',
    title: 'Launch tube ↔ QCells ops',
    type: 'Logistics',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
  },
  {
    refId: 'EXT-12',
    register: 'ext',
    title: 'Ring chassis ↔ Site facilities',
    type: 'Env',
    owners: ['QC'],
    status: 'closed',
    neededBy: '—',
  },
  {
    refId: 'EXT-13',
    register: 'ext',
    title: 'Ring chassis ↔ Bottom features',
    type: 'Mech',
    owners: ['QC'],
    status: 'open',
    neededBy: 'Design release',
  },
];

// 14 elements — Nexxis 0–8, QCells 9–13 (order matters: the org boundary is at index 9).
// systemClass (INTERLOCK systemClass wave) is a SEPARATE axis from org: Operator
// (nx-owned) is EXTERNAL, Sim rig (nx-owned) is SUPPORT — see ICD_SYSTEM_CLASS_ORDER.
export const FIXTURE_MATRIX_ELEMENTS: MatrixElement[] = [
  { name: 'Cleaning\ncarriage', org: 'nx', systemClass: 'SYSTEM', index: 0 },
  { name: 'Rotator', org: 'nx', systemClass: 'SYSTEM', index: 1 },
  { name: 'Ring\nchassis', org: 'nx', systemClass: 'SYSTEM', index: 2 },
  { name: 'Tether', org: 'nx', systemClass: 'SYSTEM', index: 3 },
  { name: 'JuiceBox\n+ARC', org: 'nx', systemClass: 'SYSTEM', index: 4 },
  { name: 'Operator', org: 'nx', systemClass: 'EXTERNAL', index: 5 },
  { name: 'Launch\ntube', org: 'nx', systemClass: 'SYSTEM', index: 6 },
  { name: 'Sim rig', org: 'nx', systemClass: 'SUPPORT', index: 7 },
  { name: 'Camera', org: 'nx', systemClass: 'SYSTEM', index: 8 },
  { name: 'Pull\nchamber', org: 'qc', systemClass: 'EXTERNAL', index: 9 },
  { name: 'Seed\nchuck', org: 'qc', systemClass: 'EXTERNAL', index: 10 },
  { name: 'Bottom\nfeatures', org: 'qc', systemClass: 'EXTERNAL', index: 11 },
  { name: 'Site\nfacilities', org: 'qc', systemClass: 'EXTERNAL', index: 12 },
  { name: 'QCells\nops', org: 'qc', systemClass: 'EXTERNAL', index: 13 },
];

const FIXTURE_MATRIX_CELLS: Record<string, MatrixCellInterface[]> = {
  '0-1': [{ refId: 'INT-01', register: 'int', status: 'open' }],
  '1-2': [{ refId: 'INT-02', register: 'int', status: 'open' }],
  '1-4': [{ refId: 'INT-03', register: 'int', status: 'open', trade: true }],
  '2-3': [{ refId: 'INT-04', register: 'int', status: 'open' }],
  '2-6': [{ refId: 'INT-05', register: 'int', status: 'open' }],
  '2-7': [{ refId: 'INT-06', register: 'int', status: 'open' }],
  '3-4': [{ refId: 'INT-07', register: 'int', status: 'std' }],
  '4-5': [{ refId: 'INT-08', register: 'int', status: 'std' }],
  '3-8': [{ refId: 'INT-09', register: 'int', status: 'open' }],
  '2-5': [{ refId: 'INT-10', register: 'int', status: 'undef' }],
  '2-9': [
    { refId: 'EXT-01', register: 'ext', status: 'open', gated: true },
    { refId: 'EXT-09', register: 'ext', status: 'open', trade: true },
  ],
  '2-10': [{ refId: 'EXT-02', register: 'ext', status: 'open', elevated: true }],
  '6-13': [
    { refId: 'EXT-03', register: 'ext', status: 'open' },
    { refId: 'EXT-11', register: 'ext', status: 'open' },
  ],
  '6-11': [{ refId: 'EXT-04', register: 'ext', status: 'open' }],
  '2-11': [
    { refId: 'EXT-05', register: 'ext', status: 'open' },
    { refId: 'EXT-13', register: 'ext', status: 'open' },
  ],
  '0-9': [{ refId: 'EXT-06', register: 'ext', status: 'open' }],
  '0-13': [
    { refId: 'EXT-07', register: 'ext', status: 'open' },
    { refId: 'EXT-08', register: 'ext', status: 'open' },
  ],
  '4-12': [{ refId: 'EXT-10', register: 'ext', status: 'open' }],
  '2-12': [{ refId: 'EXT-12', register: 'ext', status: 'closed' }],
};

export const FIXTURE_MATRIX: MatrixModel = {
  elements: FIXTURE_MATRIX_ELEMENTS,
  cells: FIXTURE_MATRIX_CELLS,
};

export const FIXTURE_HEALTH: ProjectHealth = {
  projectId: PROJECT_ID,
  interfacesByStatus: { open: 19, std: 2, undef: 1, closed: 1 },
  tradesByState: { NEGOTIATING: 2, ESCALATED: 1 },
  openDataRequestCount: 3,
  oldestUnresolvedTurns: [
    {
      kind: 'HANDSHAKE',
      trade: { refId: 'EXT-09-T1', title: 'Ring chassis ↔ Pull chamber wall — clearance' },
      externalParty: null,
      forUserId: 'alex',
      forUserName: 'Alex Gravestock',
      ageDays: 3,
      createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    },
  ],
};

// Campaign-2 Theme-2 Wave-5 — a single curated escalation, DEV-fixture only (see
// fetchEscalations' fixtureOrHonestFail — PROD never fakes this).
export const FIXTURE_ESCALATIONS: EscalationRow[] = [
  {
    id: 'fixture-esc-1',
    refId: 'EXT-09-T1',
    title: 'Wheel contact force — traction vs non-marking',
    interfaceId: 'fixture-iface-ext-09',
    interfaceRefId: 'EXT-09',
    interfaceTitle: 'Ring chassis ↔ Pull chamber',
    reason: 'Contact force cannot be reconciled without a design change — kicked to an arbiter',
    escalatedByUserId: 'james',
    escalatedByName: 'James',
    escalatedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    ageMs: 4 * 86400000,
    awaitingRuling: true,
  },
];

// "Waiting on me" — curated from the design intent (US-201). Items where it is the
// current user's (Shijin) turn, grouped by kind on the home page.
export const FIXTURE_WAITING: WaitingItem[] = [
  {
    id: 'w1',
    kind: 'handshake',
    refId: 'INT-03-T1',
    title: 'Rotator ↔ JuiceBox — torque budget',
    subtitle: 'Priya submitted µ0.9 compound — your handshake to give',
    whoseTurn: 'your turn',
    href: '/interlock/trades/INT-03-T1',
  },
  {
    id: 'w2',
    kind: 'handshake',
    refId: 'INT-10-T4',
    title: 'Ring chassis ↔ Operator — recovery pull',
    subtitle: 'Alex countered at 3.2 kg — needs your sign-off',
    whoseTurn: 'your turn',
    href: '/interlock/trades/INT-10-T4',
  },
  {
    id: 'w3',
    kind: 'data-request',
    refId: 'DR-13',
    title: 'EXT-09 — wall finish spec',
    subtitle: 'QCells asked for the surface roughness bound',
    whoseTurn: 'awaiting your answer',
    href: '/interlock/interfaces/EXT-09',
  },
  {
    id: 'w4',
    kind: 'review',
    refId: 'EXT-02',
    title: 'Ring chassis ↔ Seed chuck — elevated',
    subtitle: 'RSK-04 sev 12 — review the clearance before Concept-lock',
    whoseTurn: 'flagged for you',
    href: '/interlock/interfaces/EXT-02',
  },
];

// "Client's court" — turns waiting on an external (non-user, e.g. QCells) party.
// Visibility-only; never actionable by the current user.
export const FIXTURE_WAITING_EXTERNAL: WaitingItem[] = [
  {
    id: 'ext-w1',
    kind: 'handshake',
    refId: 'EXT-09-T2',
    title: 'Ring chassis ↔ Pull chamber wall — clearance counter',
    subtitle: 'Waiting on QCells',
    href: '/interlock/trades/EXT-09-T2',
    externalParty: 'QCells',
  },
  {
    id: 'ext-w2',
    kind: 'data-request',
    refId: 'DR-02',
    title: 'Ring chassis ↔ Seed chuck — datum drift bound',
    subtitle: 'Waiting on QCells',
    href: '/interlock/interfaces/EXT-02',
    externalParty: 'QCells',
  },
];

// ============================================================================
// Mission Map (P2 — the consequence engine's showcase). DEVELOPMENT-PLAN §7 D2:
// derived + read-only; causes → failure modes → functions → mission, rendered
// entirely from the graph. Mitigated chains render CALM, unmitigated critical HOT.
//
// Backend contract (built in parallel — code EXACTLY to this, degrade gracefully):
//   GET /api/projects/:id/mission-map →
//     { nodes: [{ id, kind, label, refId?, band?, residualBand?, provenance?, elevated? }],
//       links: [{ source, target, value, heat }] }
// ============================================================================

export type MissionNodeKind = 'CAUSE' | 'FM' | 'FUNCTION' | 'MISSION';
export type MissionBand = 'crit' | 'high' | 'med' | 'low';
export type MissionHeat = 'HOT' | 'WARM' | 'CALM' | 'QUIET';
/** SOLO = one person's number, not yet converged → dashed/grey (§10.1).
 * CONVERGED_UNANIMOUS/CONVERGED_WITH_DISSENT = scoring round converged → solid
 * border; WITH_DISSENT additionally gets a † badge (§10.1). */
export type MissionProvenance =
  | 'SOLO'
  | 'CONVERGED'
  | 'CONVERGED_UNANIMOUS'
  | 'CONVERGED_WITH_DISSENT'
  | 'RULED';

export interface MissionNode {
  id: string;
  kind: MissionNodeKind;
  label: string;
  refId?: string; // 'RSK-04' / 'F3'
  band?: MissionBand; // FM/function consequence band
  residualBand?: MissionBand; // present when mitigated below the source band
  provenance?: MissionProvenance;
  elevated?: boolean;
  /** Optional function classification — read defensively (live backend may omit). */
  cls?: 'M' | 'S' | 'O'; // mission-critical / safety / operational
  /** D4 — the scoring campaign that produced this FM's converged value, so the
   * †/converged badge can link back to `/interlock/campaigns/<campaignRefId>`.
   * Read defensively — absent on backends before this parcel and on SOLO FMs. */
  campaignRefId?: string;
  /** D4 — driver-pick rationale snippet for a WITH_DISSENT FM, when present. */
  rationaleSnippet?: string;
}

export interface MissionLink {
  source: string; // node id
  target: string; // node id
  value: number; // ribbon weight
  heat: MissionHeat;
}

export interface MissionMap {
  nodes: MissionNode[];
  links: MissionLink[];
}

const MISSION_BANDS = new Set(['crit', 'high', 'med', 'low']);
function normBand(s: string | undefined): MissionBand | undefined {
  const v = (s ?? '').toLowerCase();
  if (v.startsWith('crit')) return 'crit';
  if (v.startsWith('high')) return 'high';
  if (v.startsWith('med')) return 'med';
  if (v.startsWith('low') || v === 'minor' || v === 'negligible') return 'low';
  return MISSION_BANDS.has(v) ? (v as MissionBand) : undefined;
}
const HEAT_SET = new Set(['HOT', 'WARM', 'CALM', 'QUIET']);
function normHeat(s: string | undefined): MissionHeat {
  const v = (s ?? '').toUpperCase();
  return (HEAT_SET.has(v) ? v : 'QUIET') as MissionHeat;
}
function normKind(s: string | undefined): MissionNodeKind {
  const v = (s ?? '').toUpperCase();
  if (v === 'CAUSE' || v === 'RC') return 'CAUSE';
  if (v === 'FUNCTION' || v === 'FN' || v === 'FUNC') return 'FUNCTION';
  if (v === 'MISSION') return 'MISSION';
  return 'FM';
}
function normProvenance(s: string | undefined): MissionProvenance | undefined {
  const v = (s ?? '').toUpperCase();
  if (v === 'SOLO') return 'SOLO';
  if (v === 'CONVERGED_UNANIMOUS') return 'CONVERGED_UNANIMOUS';
  if (v === 'CONVERGED_WITH_DISSENT') return 'CONVERGED_WITH_DISSENT';
  if (v === 'CONVERGED' || v === 'AGREED' || v === 'COUNTERSIGNED') return 'CONVERGED';
  if (v === 'RULED') return 'RULED';
  return undefined;
}

/** GET /api/projects/:id/mission-map → MissionMap */
export function adaptMissionMap(body: unknown): MissionMap {
  const d = unwrap<Record<string, unknown>>(body) ?? {};
  const rawNodes = (Array.isArray(d?.nodes) ? d.nodes : []) as Record<string, unknown>[];
  const rawLinks = (Array.isArray(d?.links) ? d.links : []) as Record<string, unknown>[];
  const nodes: MissionNode[] = rawNodes.map((o, i) => ({
    id: pick<string>(o, 'id', 'nodeId') ?? `n${i}`,
    kind: normKind(pick<string>(o, 'kind', 'type')),
    label: pick<string>(o, 'label', 'title', 'name') ?? '—',
    refId: pick<string>(o, 'refId', 'ref'),
    band: normBand(pick<string>(o, 'band')),
    residualBand: normBand(pick<string>(o, 'residualBand', 'residual')),
    provenance: normProvenance(pick<string>(o, 'provenance', 'standing')),
    elevated: Boolean(pick<boolean>(o, 'elevated')),
    cls: pick<'M' | 'S' | 'O'>(o, 'cls', 'class', 'functionClass'),
    campaignRefId: pick<string>(o, 'campaignRefId') ?? undefined,
    rationaleSnippet: pick<string>(o, 'rationaleSnippet') ?? undefined,
  }));
  const links: MissionLink[] = rawLinks
    .map((o) => ({
      source: pick<string>(o, 'source', 'from', 'src') ?? '',
      target: pick<string>(o, 'target', 'to', 'dst') ?? '',
      value: pick<number>(o, 'value', 'weight') ?? 1,
      heat: normHeat(pick<string>(o, 'heat')),
    }))
    .filter((l) => l.source && l.target);
  return { nodes, links };
}

export async function fetchMissionMap(): Promise<Fetched<MissionMap>> {
  try {
    const body = await getJson(`/projects/${PROJECT_ID}/mission-map`);
    const data = adaptMissionMap(body);
    // RS4 — no consequence graph yet is a real (fresh-project) state.
    return { data, fromFixture: false };
  } catch (e) {
    return fixtureOrHonestFail(e, FIXTURE_MISSION_MAP, { nodes: [], links: [] });
  }
}

// ============================================================================
// Elevation (§6.2b) — which interfaces/trades are on a critical path, and the
// receipts that explain WHY (badge → explain-path, one click away).
//
// Backend contract (parallel parcel):
//   GET /api/projects/:id/elevation →
//     { items: [{ kind: 'INTERFACE'|'TRADE', id, refId, requiresCountersign?,
//                 reasons: [{ explain, path }] }] }
// ============================================================================

export type ElevationTargetKind = 'INTERFACE' | 'TRADE';

export interface ElevationReason {
  explain: string; // human sentence: "elevated because RC-07 → FM RSK-04 CRITICAL → Ring chassis → EXT-02"
  path: string; // compact chain: "RC-07 → RSK-04 → Ring chassis → EXT-02"
}

export interface ElevationItem {
  kind: ElevationTargetKind;
  id: string;
  refId: string;
  requiresCountersign?: boolean;
  reasons: ElevationReason[];
}

export interface ElevationMap {
  items: ElevationItem[];
}

export function adaptElevation(body: unknown): ElevationMap {
  const items = asArray(
    (() => {
      const d = unwrap(body);
      // tolerate { items: [...] } or a bare array
      if (d && typeof d === 'object' && Array.isArray((d as Record<string, unknown>).items)) {
        return { items: (d as Record<string, unknown>).items };
      }
      return d;
    })()
  ).map((raw): ElevationItem => {
    const o = raw as Record<string, unknown>;
    const rawReasons = pick<unknown>(o, 'reasons', 'explanations');
    const reasons: ElevationReason[] = (Array.isArray(rawReasons) ? rawReasons : []).map((r) => {
      if (typeof r === 'string') return { explain: r, path: '' };
      const ro = r as Record<string, unknown>;
      // `path` is a compact chain STRING here (e.g. "RSK-01 → EXT-09-T1 → EXT-09").
      // The backend sends it as a refId ARRAY (ElevationReason.path: string[]), so
      // join it — rendering the raw array produced a separator-less run-on
      // ("RSK-01EXT-09-T1EXT-09"). (C2-T3 audit-out.) Still accept a pre-joined
      // string (fixtures use that form).
      const rawPath = pick<unknown>(ro, 'path', 'chain');
      const path = Array.isArray(rawPath)
        ? rawPath.filter((x): x is string => typeof x === 'string' && !!x).join(' → ')
        : typeof rawPath === 'string'
          ? rawPath
          : '';
      return {
        explain: pick<string>(ro, 'explain', 'reason', 'text') ?? '—',
        path,
      };
    });
    return {
      kind:
        (pick<string>(o, 'kind', 'type') ?? 'INTERFACE').toUpperCase() === 'TRADE'
          ? 'TRADE'
          : 'INTERFACE',
      id: pick<string>(o, 'id') ?? '',
      refId: pick<string>(o, 'refId', 'ref') ?? '',
      requiresCountersign: Boolean(pick<boolean>(o, 'requiresCountersign', 'countersign')),
      reasons,
    };
  });
  return { items };
}

// One shared in-flight promise so Register/Matrix/System/TradeDetail don't each
// hit the endpoint. Fixture fallback also cached; call resetElevationCache() in
// tests if needed.
let _elevationPromise: Promise<Fetched<ElevationMap>> | null = null;

export async function fetchElevation(): Promise<Fetched<ElevationMap>> {
  if (_elevationPromise) return _elevationPromise;
  _elevationPromise = (async () => {
    try {
      const body = await getJson(`/projects/${PROJECT_ID}/elevation`);
      const data = adaptElevation(body);
      // RS4 — nothing elevated yet is a real (good) state.
      return { data, fromFixture: false };
    } catch (e) {
      return fixtureOrHonestFail(e, FIXTURE_ELEVATION, { items: [] });
    }
  })();
  return _elevationPromise;
}

export function resetElevationCache(): void {
  _elevationPromise = null;
}

/** Index elevation items by refId (interface refIds + trade refIds). */
export function elevationByRef(map: ElevationMap): Map<string, ElevationItem> {
  const m = new Map<string, ElevationItem>();
  for (const it of map.items) if (it.refId) m.set(it.refId, it);
  return m;
}

// ============================================================================
// Mission-map + elevation FIXTURES — transliterated from
//   dev/active/interface-pivot/design/fmea-mission-map.dc.html
//   dev/active/interface-pivot/design/system-view.dc.html
// Fallback only; every consumer shows a "fixture data" badge when these are used.
// ============================================================================

// Compact source (mirrors the design canvas), expanded into the contract shape below.
const FM_HEAT: Record<string, MissionHeat> = {
  'RSK-01': 'HOT',
  'RSK-04': 'HOT',
  'RSK-02': 'HOT',
  'RSK-09': 'WARM',
  'RSK-05': 'WARM',
  'RSK-10': 'WARM',
  'RSK-08': 'CALM',
  'RSK-03': 'CALM',
  'RSK-06': 'WARM',
  'RSK-07': 'WARM',
  'RSK-11': 'QUIET',
};
const HEAT_VALUE: Record<MissionHeat, number> = { HOT: 3, WARM: 2, CALM: 1.6, QUIET: 1 };

function buildFixtureMissionMap(): MissionMap {
  const causes: [string, string, string][] = [
    // [causeId, label, → fmId]
    ['c1', 'excess wheel contact force', 'RSK-03'],
    ['c2', 'low-friction wheel compound', 'RSK-01'],
    ['c3', 'bore position tolerance stack', 'RSK-04'],
    ['c4', 'tether payout fault', 'RSK-02'],
    ['c5', 'deposit on chamber wall', 'RSK-05'],
    ['c6', 'seal degradation', 'RSK-08'],
    ['c7', 'comms / e-stop dropout', 'RSK-09'],
  ];
  const fms: {
    id: string;
    label: string;
    band: MissionBand;
    residualBand?: MissionBand;
    elevated?: boolean;
    provenance?: MissionProvenance;
    fns: string[];
  }[] = [
    { id: 'RSK-01', label: 'Loss of adhesion — slides', band: 'crit', fns: ['F2', 'F1'] },
    { id: 'RSK-04', label: 'Seed-chuck collision', band: 'crit', elevated: true, fns: ['F3'] },
    { id: 'RSK-02', label: 'Tether entanglement', band: 'crit', fns: ['F6', 'F1'] },
    { id: 'RSK-09', label: 'Comms / e-stop loss', band: 'high', fns: ['F10', 'F9'] },
    { id: 'RSK-05', label: 'Contamination / deposit', band: 'high', fns: ['F4', 'F5'] },
    { id: 'RSK-10', label: 'Actuator overtemp', band: 'high', fns: ['F2', 'F12'] },
    {
      id: 'RSK-08',
      label: 'Ingress → electronics',
      band: 'high',
      residualBand: 'low',
      fns: ['F11'],
    },
    {
      id: 'RSK-03',
      label: 'Wall scratch on traverse',
      band: 'high',
      residualBand: 'low',
      provenance: 'SOLO',
      elevated: true,
      fns: ['F5'],
    },
    { id: 'RSK-06', label: 'Imaging dropout', band: 'high', fns: ['F7'] },
    { id: 'RSK-07', label: 'Encoder drift', band: 'high', provenance: 'SOLO', fns: ['F8'] },
    { id: 'RSK-11', label: 'Launch misalignment', band: 'med', fns: ['F1'] },
  ];
  const fns: { id: string; label: string; cls: 'M' | 'S' | 'O' }[] = [
    { id: 'F1', label: 'Deploy & recover', cls: 'M' },
    { id: 'F2', label: 'Traverse & hold', cls: 'M' },
    { id: 'F3', label: 'Seed-chuck clearance', cls: 'M' },
    { id: 'F4', label: 'Clean to compliance', cls: 'M' },
    { id: 'F5', label: 'Preserve surface integrity', cls: 'S' },
    { id: 'F6', label: 'Manage 30m tether', cls: 'M' },
    { id: 'F9', label: 'Operator control', cls: 'M' },
    { id: 'F10', label: 'E-stop & safe recovery', cls: 'S' },
    { id: 'F11', label: 'Withstand environment', cls: 'M' },
    { id: 'F7', label: 'Chamber imaging', cls: 'O' },
    { id: 'F8', label: 'Measure travel', cls: 'O' },
    { id: 'F12', label: 'Module service', cls: 'O' },
  ];

  const nodes: MissionNode[] = [
    ...causes.map(([id, label]) => ({ id, kind: 'CAUSE' as const, label })),
    ...fms.map((f) => ({
      id: f.id,
      kind: 'FM' as const,
      label: f.label,
      refId: f.id,
      band: f.band,
      residualBand: f.residualBand,
      provenance: f.provenance,
      elevated: f.elevated,
    })),
    ...fns.map((f) => ({
      id: f.id,
      kind: 'FUNCTION' as const,
      label: f.label,
      refId: f.id,
      cls: f.cls,
    })),
    { id: 'MISSION', kind: 'MISSION', label: 'Mission integrity' },
  ];

  const links: MissionLink[] = [];
  // cause → fm
  for (const [cid, , fmId] of causes) {
    const heat = FM_HEAT[fmId] ?? 'QUIET';
    links.push({ source: cid, target: fmId, value: HEAT_VALUE[heat], heat });
  }
  // fm → function
  const fnCls = new Map(fns.map((f) => [f.id, f.cls]));
  const rank: Record<MissionHeat, number> = { HOT: 3, WARM: 2, CALM: 1, QUIET: 0 };
  const fnIncoming = new Map<string, MissionHeat>();
  for (const fm of fms) {
    const heat = FM_HEAT[fm.id] ?? 'QUIET';
    for (const fnId of fm.fns) {
      links.push({ source: fm.id, target: fnId, value: HEAT_VALUE[heat], heat });
      const cur = fnIncoming.get(fnId) ?? 'QUIET';
      if (rank[heat] > rank[cur]) fnIncoming.set(fnId, heat);
    }
  }
  // function → mission (operational functions stay QUIET regardless of inflow)
  for (const f of fns) {
    const heat: MissionHeat = f.cls === 'O' ? 'QUIET' : (fnIncoming.get(f.id) ?? 'QUIET');
    links.push({ source: f.id, target: 'MISSION', value: HEAT_VALUE[heat], heat });
  }
  return { nodes, links };
}

export const FIXTURE_MISSION_MAP: MissionMap = buildFixtureMissionMap();

// Elevated interfaces/trades + their receipts (design intent; matches the
// system-view fixture's elevated edges and EXT-02's RSK-04 path).
export const FIXTURE_ELEVATION: ElevationMap = {
  items: [
    {
      kind: 'INTERFACE',
      id: 'EXT-02',
      refId: 'EXT-02',
      requiresCountersign: true,
      reasons: [
        {
          explain:
            'Elevated because RC-03 bore tolerance stack → FM RSK-04 Seed-chuck collision (CRITICAL) → Ring chassis → EXT-02',
          path: 'RC-03 → RSK-04 → Ring chassis → EXT-02',
        },
      ],
    },
    {
      kind: 'INTERFACE',
      id: 'EXT-09',
      refId: 'EXT-09',
      requiresCountersign: true,
      reasons: [
        {
          explain:
            'Elevated because RC-01 excess wheel contact force → FM RSK-03 Wall scratch on traverse (HIGH) → Ring chassis → EXT-09',
          path: 'RC-01 → RSK-03 → Ring chassis → EXT-09',
        },
      ],
    },
    {
      kind: 'INTERFACE',
      id: 'EXT-01',
      refId: 'EXT-01',
      reasons: [
        {
          explain:
            'Elevated via the Ring chassis ↔ Pull chamber geometry contract (shares EXT-09 contact path)',
          path: 'RSK-03 → Ring chassis → EXT-01',
        },
      ],
    },
    {
      kind: 'INTERFACE',
      id: 'EXT-05',
      refId: 'EXT-05',
      reasons: [
        {
          explain:
            'Elevated because RSK-03 Wall scratch traverse path also loads the Ring chassis ↔ Bottom features fit',
          path: 'RSK-03 → Ring chassis → EXT-05',
        },
      ],
    },
    {
      kind: 'TRADE',
      id: 'EXT-09-T1',
      refId: 'EXT-09-T1',
      requiresCountersign: true,
      reasons: [
        {
          explain:
            'Requires countersign because its interface EXT-09 sits on the RSK-03 critical-contact path (CRITICAL-band source)',
          path: 'RSK-03 → Ring chassis → EXT-09 → EXT-09-T1',
        },
      ],
    },
  ],
};

// ============================================================================
// Gate screen (§10.3 utility #1) — Design-Release gate.
// Backend (this parcel): interlockGate.routes.ts.
// Shapes are owned here (both ends built together) so no defensive adapter needed.
// ============================================================================

export interface GatePredicateResult {
  key: string;
  label: string;
  pass: boolean;
  detail: string;
  link?: string;
  // W2-A / V5: structured (tradeRefId, interfaceRefId) pairs — today only
  // elevated_trades_countersigned. Read defensively; absent on older backends.
  refs?: { tradeRefId: string; interfaceRefId: string }[];
  // R6 — ruled trades with unresolved ACKNOWLEDGE turns, gate-scoped the same
  // way as `refs` (elevated_trades_countersigned predicate only). The
  // predicate FAILS whenever this is non-empty, same as `refs`. Read
  // defensively; absent on backends before this parcel.
  pendingAckRefs?: { tradeRefId: string; interfaceRefId: string }[];
  // MAJOR fix (gate/requirement UX audit) — full requirementRefId list for a
  // failing requirements_design_linked / requirements_fmea_covered predicate.
  // Read defensively; absent on backends before this parcel (falls back to the
  // bare `link`, same as any other unrecognised predicate key).
  offenderRefIds?: string[];
  // C2-T2-W1: "not applicable / not tracked" (e.g. zero interfaces configured).
  // `pass` stays true (non-blocking) but this row must render NEUTRAL, not green,
  // and must not be counted in the GREEN tally. Absent on older backends.
  notApplicable?: boolean;
}

export interface GateEvaluation {
  name: string;
  slug: string;
  pass: boolean;
  // L7 (gate-empty-not-ready, BLOCKER) — true when EVERY predicate came back
  // notApplicable, i.e. the project has no agreement-state data at all yet
  // (no interfaces/trades/DRs/requirements). `pass` alone can't tell
  // "verified clean" apart from "nothing to verify" — the frontend must check
  // THIS before rendering the green "Ready for design release" banner or
  // enabling "Declare pass". Absent on older backends; treat as false (old
  // behaviour) rather than assuming not-started.
  notStarted?: boolean;
  status: 'PASSED' | 'FAILED';
  predicates: GatePredicateResult[];
  // passCount EXCLUDES notApplicable rows from the numerator — see gate.service.ts.
  passCount: number;
  total: number;
  // How many of `total` are notApplicable (folded out of passCount). Absent on
  // older backends; treat as 0.
  naCount?: number;
  snapshotFingerprint: string;
  fingerprintShort: string;
  evaluatedAt: string;
}

export interface GateReviewRow {
  id: string;
  projectId: string;
  name: string;
  predicates: GatePredicateResult[];
  status: 'OPEN' | 'PASSED' | 'FAILED';
  verdict: string | null;
  declaredById: string | null;
  declaredByName: string | null;
  declaredAt: string | null;
  snapshotFingerprint: string | null;
  createdAt: string;
}

/** GET /api/projects/:id/gates/design-release — live evaluation. */
export async function fetchDesignReleaseGate(): Promise<GateEvaluation> {
  return getJson<GateEvaluation>(`/projects/${PROJECT_ID}/gates/design-release`);
}

/** POST /api/projects/:id/gates/design-release/declare — records verdict + fingerprint. */
export async function declareDesignReleaseGate(verdict?: string): Promise<GateReviewRow> {
  return requestJson<GateReviewRow>(`/projects/${PROJECT_ID}/gates/design-release/declare`, {
    method: 'POST',
    body: JSON.stringify({ verdict: verdict ?? null }),
  });
}

/** GET /api/projects/:id/gates — declared-review history (newest first).
 *
 * NOTE (P5 review): this deliberately omits the route's optional `?name=`
 * filter because only one gate definition ("Design release") exists today.
 * If a second gate ever ships, this list will interleave BOTH gates'
 * reviews — GatePage's baseline lettering and the baseline-diff panel would
 * then compare across gates. At that point pass `?name=` here. */
export async function fetchGateReviews(): Promise<GateReviewRow[]> {
  const body = await getJson<GateReviewRow[]>(`/projects/${PROJECT_ID}/gates`);
  return Array.isArray(body) ? body : [];
}

// ============================================================================
// Scoring campaigns (§10.1) — "a risk score becomes a trade".
// Blind round → per-dimension reveal (S/O/D dot-strips, NEVER an RPN spread) →
// tiered convergence → driver proposes finals → one handshake per SHEET.
//
// Backend contract (parallel parcel interlock/p3-campaigns — code EXACTLY to this,
// degrade gracefully to the fixtures below + a FixtureBadge):
//   GET  /api/projects/:id/campaigns → CampaignSummary[]
//   GET  /api/campaigns/:id          → CampaignDetail
//   POST /api/campaigns/:id/votes    { votes: [...] }
//   POST /api/campaigns/:id/confirm  (sheet-level handshake)
//   POST /api/campaigns/:id/:action  action ∈ open|reveal|propose|close (driver-only)
// ============================================================================

export type CampaignStatus = 'DRAFT' | 'OPEN' | 'REVEAL' | 'CONVERGING' | 'IN_REVIEW' | 'CLOSED';
// S/O/D are the vote-time dims; L (Likelihood) only appears in round-diff
// cells for LxS projects (see computeRoundDiff in campaign.service.ts) — it
// is never a votable dim, so keep it out of anywhere that assumes S/O/D
// exhaustiveness (e.g. DIM_FULL in CampaignDetailPage.tsx handles it
// separately).
export type ScoreDim = 'S' | 'O' | 'D' | 'L';
/** scopeKind — read as a free string; known values get a friendly label. */
export type CampaignScopeKind =
  | 'INTERFACE_SWEEP'
  | 'FUNCTION_SWEEP'
  | 'FM_SET'
  | 'ELEVATED_ONLY'
  | string;
export type SheetOutcome =
  | 'CONVERGED_UNANIMOUS'
  | 'CONVERGED_WITH_DISSENT'
  | 'RULED'
  | 'NOT_SCORED';

export interface CampaignSummary {
  id: string;
  refId: string;
  status: CampaignStatus;
  scopeKind: CampaignScopeKind;
  driverUserId: string;
  participants: string[];
  deadline?: string;
  fmCount: number;
  /** derived if the backend surfaces it (N of M submitted) — else computed on detail. */
  submittedCount?: number;
  /** The frozen FM id set (raw `listCampaigns` echoes the full row) — used by the
   * create-campaign picker to disable FMs already claimed by another open round,
   * mirroring the server-side "<=1 non-CLOSED campaign per FM" guard. Absent on
   * fixture data (harmless — the picker just can't pre-disable anything then). */
  fmIds?: string[];
}

export interface CampaignCause {
  causeLinkId: string;
  rootCauseId?: string;
  label: string;
}

export interface CampaignFm {
  fmId: string;
  refId?: string;
  title: string;
  causes: CampaignCause[];
}

export interface MyVote {
  fmId: string;
  causeLinkId?: string;
  dim: ScoreDim;
  value?: number;
  abstain: boolean;
  rationale?: string;
}

export interface Submission {
  userId: string;
  submitted: boolean;
}

export interface RevealVote {
  userId: string;
  value?: number;
  abstain: boolean;
  rationale?: string;
}

export interface RevealCell {
  fmId: string;
  causeLinkId?: string;
  dim: ScoreDim;
  votes: RevealVote[];
  spread: number;
  proposal?: number;
  method?: string; // AUTO_MEDIAN | DRIVER_PICK | RULED | MAX
}

export interface SheetDissent {
  userId: string;
  dim: ScoreDim;
  causeLinkId?: string;
  value?: number;
  rationale?: string;
}

export interface SheetFinal {
  dim: ScoreDim;
  causeLinkId?: string;
  value?: number;
}

/** X5 — one dim's method label ("MAX" / "MEDIAN" / "DRIVER_PICK" / "RULED"), same
 * {S?, causes:{[causeId]:{O?,D?}}} nesting as `finals`/`liveSpreads`. */
export interface SheetMethod {
  dim: ScoreDim;
  causeLinkId?: string;
  method?: string;
}

/** Z14 — one dim's driver-pick rationale, same nesting as SheetMethod. Only
 * present where a driver actually gave one (most DRIVER_PICK dims have none). */
export interface SheetRationale {
  dim: ScoreDim;
  causeLinkId?: string;
  rationale?: string;
}

/** P8 — one dim's round-over-round movement (finals as of `fromRound` → `toRound`). */
export interface RoundDiffCell {
  dim: ScoreDim;
  causeLinkId?: string;
  from?: number;
  to?: number;
}

/** P8 — campaign-wide multi-round diff. Absent (undefined) for single-round
 * campaigns (the common case) and while the campaign is still blind (backend
 * sends `roundDiff: null` pre-reveal) — the page hides the section entirely. */
export interface CampaignRoundDiff {
  fromRound: number;
  toRound: number;
  byFm: Record<string, RoundDiffCell[]>;
}

export interface CampaignSheet {
  fmId: string;
  finals: SheetFinal[];
  outcome: SheetOutcome;
  dissents: SheetDissent[];
  confirmedBy: string[];
  /** X5 — per-dim method, sourced from `sheets[].methods`. Empty when the backend
   * hasn't shipped per-dim methods yet — callers fall back to `legacyMethod`. */
  methods: SheetMethod[];
  /** Old flat sheet-level method string (pre-X5 backends) — last-resort fallback
   * ONLY, so an untouched dim never inherits a sibling's driver-pick override. */
  legacyMethod?: string;
  /** Z14 — per-dim driver-pick rationale, read defensively (absent on backends
   * before this parcel, and on any dim the driver didn't annotate). */
  rationales: SheetRationale[];
}

export interface CampaignDetail {
  id: string;
  refId: string;
  status: CampaignStatus;
  scoreDims: ScoreDim[];
  fms: CampaignFm[];
  myVotes: MyVote[];
  submissions: Submission[];
  reveal: RevealCell[];
  sheets: CampaignSheet[];
  // Present on the summary; MAY be echoed on detail. Read defensively so the
  // detail page knows the driver / scope / deadline without a second call.
  driverUserId?: string;
  participants?: string[];
  deadline?: string;
  scopeKind?: CampaignScopeKind;
  // V1 — MAY arrive from the parallel backend parcel (W1-A: listCampaigns/
  // getCampaignDetail attribution). Read defensively; the page falls back to
  // resolving driverUserId via userById() and to counting `submissions` itself.
  driverName?: string;
  submittedCount?: number;
  /** P8 — round-over-round diff; undefined for single-round campaigns/while blind. */
  roundDiff?: CampaignRoundDiff;
  /** R-O4-01 — the quorum actually enforced (scoring-capable-based). */
  effectiveQuorum?: number;
  /** R-O4-01 — count of CAMPAIGN_SCORE-capable participants (max possible confirmers). */
  capableParticipantCount?: number;
  /** R-O4-01 — false when the effective quorum can never be met; at CONVERGING
   * this means the campaign is stuck and close() is the only path out. */
  quorumReachable?: boolean;
}

const CAMPAIGN_STATUSES = new Set<CampaignStatus>([
  'DRAFT',
  'OPEN',
  'REVEAL',
  'CONVERGING',
  'IN_REVIEW',
  'CLOSED',
]);
function normCampaignStatus(s: string | undefined): CampaignStatus {
  const v = (s ?? '').toUpperCase() as CampaignStatus;
  return CAMPAIGN_STATUSES.has(v) ? v : 'OPEN';
}
function normDim(s: string | undefined): ScoreDim {
  const v = (s ?? '').toUpperCase();
  return v === 'S' || v === 'O' || v === 'D' || v === 'L' ? v : 'S';
}
const SHEET_OUTCOMES = new Set<SheetOutcome>([
  'CONVERGED_UNANIMOUS',
  'CONVERGED_WITH_DISSENT',
  'RULED',
  'NOT_SCORED',
]);
function normOutcome(s: string | undefined): SheetOutcome {
  const v = (s ?? '').toUpperCase() as SheetOutcome;
  return SHEET_OUTCOMES.has(v) ? v : 'NOT_SCORED';
}
function toNum(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}
function strList(v: unknown): string[] {
  if (Array.isArray(v))
    return v
      .map((x) =>
        typeof x === 'string'
          ? x
          : String(
              (x as Record<string, unknown>)?.userId ?? (x as Record<string, unknown>)?.id ?? x
            )
      )
      .filter(Boolean);
  return [];
}

/** GET /api/projects/:id/campaigns → CampaignSummary[] */
export function adaptCampaigns(body: unknown): CampaignSummary[] {
  return asArray(body).map((raw, i) => {
    const o = raw as Record<string, unknown>;
    return {
      id: pick<string>(o, 'id') ?? `campaign-${i}`,
      refId: pick<string>(o, 'refId', 'ref') ?? `RRC-${i + 1}`,
      status: normCampaignStatus(pick<string>(o, 'status')),
      scopeKind: pick<string>(o, 'scopeKind', 'scope') ?? 'FM_SET',
      driverUserId: pick<string>(o, 'driverUserId', 'driver') ?? '',
      participants: strList(pick<unknown>(o, 'participants', 'participantIds')),
      deadline: pick<string>(o, 'deadline', 'dueAt') ?? undefined,
      fmCount: toNum(pick<unknown>(o, 'fmCount', 'fmeCount')) ?? 0,
      submittedCount: toNum(pick<unknown>(o, 'submittedCount', 'submitted')),
      fmIds: strList(pick<unknown>(o, 'fmIds')),
    };
  });
}

function adaptCauses(v: unknown): CampaignCause[] {
  if (!Array.isArray(v)) return [];
  return v.map((raw, i) => {
    const o = raw as Record<string, unknown>;
    return {
      causeLinkId: pick<string>(o, 'causeLinkId', 'id') ?? `cause-${i}`,
      rootCauseId: pick<string>(o, 'rootCauseId', 'causeId'),
      label: pick<string>(o, 'label', 'title', 'name') ?? '—',
    };
  });
}

/**
 * Read a possibly-nested {S?, causes:{[causeId]:{O?,D?}}} holder (finals/spreads/methods
 * all share this shape) for one dim (+ optional causeId). Returns the raw value —
 * callers narrow to number/string as needed.
 */
function nestedRaw(obj: unknown, causeId: string | undefined, dim: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  const root = obj as Record<string, unknown>;
  const holder = causeId
    ? ((root.causes as Record<string, unknown> | undefined)?.[causeId] as
        | Record<string, unknown>
        | undefined)
    : root;
  return holder?.[dim];
}
function nestedNum(obj: unknown, causeId: string | undefined, dim: string): number | undefined {
  const v = nestedRaw(obj, causeId, dim);
  return typeof v === 'number' ? v : undefined;
}
function nestedStr(obj: unknown, causeId: string | undefined, dim: string): string | undefined {
  const v = nestedRaw(obj, causeId, dim);
  return typeof v === 'string' ? v : undefined;
}

/**
 * X4a — client-side spread fallback: max−min of scored (non-abstain) votes. Used
 * whenever the backend hasn't (yet) supplied a spread for a cell — NEVER default a
 * disagreement to 0/calm just because the field was absent.
 */
function spreadFromVotes(votes: unknown): number {
  if (!Array.isArray(votes)) return 0;
  const scored = (votes as Record<string, unknown>[])
    .filter((v) => !v.abstain && typeof v.value === 'number')
    .map((v) => v.value as number);
  if (scored.length < 2) return 0;
  return Math.max(...scored) - Math.min(...scored);
}

/**
 * Normalize the LIVE campaign backend shape (votes/submissionStatus/dotStrips/viewer,
 * finals/spreads as {S?, causes:{[causeId]:{O?,D?}}}) into the design-era field names
 * the adapters below consume (myVotes/submissions/reveal). No-op when the design-era
 * fields are already present (fixtures).
 *
 * X4a/X5 (R3-W1B): the parallel backend parcel adds `liveSpreads` (same {S?,
 * causes:{...}} nesting as finals) on the campaign detail from REVEAL onward, and
 * `methods` (per-dim, same nesting) inside each sheet. Both are read defensively —
 * `liveSpreads` takes precedence over the older `sheet.spreads`, and either falls
 * back to a client-computed spread from the actual votes rather than the previous
 * "field absent → 0" default (which silently hid genuine disagreement). Likewise
 * per-dim `methods` takes precedence over the old flat sheet-level `method` string,
 * so overriding one dim's method (e.g. a driver pick) never bleeds onto a sibling
 * dim that was untouched.
 */
function normalizeCampaignDetailBody(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...o };
  const viewer = (o.viewer ?? {}) as Record<string, unknown>;
  const viewerId = typeof viewer.userId === 'string' ? viewer.userId : undefined;

  // myVotes ← votes (blind: own-only rows, possibly without userId; else filter to viewer)
  if (!Array.isArray(out.myVotes) && Array.isArray(o.votes)) {
    out.myVotes = (o.votes as Record<string, unknown>[]).filter(
      (v) => v.userId === undefined || v.userId === null || v.userId === viewerId
    );
  }
  // submissions ← submissionStatus [{userId, hasVoted}]
  if (!Array.isArray(out.submissions) && Array.isArray(o.submissionStatus)) {
    out.submissions = (o.submissionStatus as Record<string, unknown>[]).map((s) => ({
      userId: s.userId,
      submitted: Boolean(s.hasVoted ?? s.submitted),
    }));
  }
  // reveal ← dotStrips (+ liveSpreads/sheets for spread/proposal/method)
  if (!Array.isArray(out.reveal) && o.dotStrips && typeof o.dotStrips === 'object') {
    const sheetsByFm = new Map<string, Record<string, unknown>>();
    for (const s of Array.isArray(o.sheets) ? (o.sheets as Record<string, unknown>[]) : []) {
      if (typeof s.fmId === 'string') sheetsByFm.set(s.fmId, s);
    }
    const liveSpreadsRoot = (
      o.liveSpreads && typeof o.liveSpreads === 'object' ? o.liveSpreads : {}
    ) as Record<string, unknown>;
    // X4b: the live `dotStrips` per-vote entries have been observed WITHOUT a
    // `rationale` field (it's only echoed on the flat top-level `votes` array) —
    // build a lookup so a dot-strip vote missing rationale can recover it from the
    // matching raw vote, rather than silently losing every dissent's "why".
    const rationaleByKey = new Map<string, string>();
    if (Array.isArray(o.votes)) {
      for (const v of o.votes as Record<string, unknown>[]) {
        const r = typeof v.rationale === 'string' ? v.rationale : undefined;
        if (!r) continue;
        const key = [v.fmId, v.causeLinkId ?? '', v.dim, v.userId].join('|');
        rationaleByKey.set(key, r);
      }
    }
    const cells: Record<string, unknown>[] = [];
    for (const [fmId, stripRaw] of Object.entries(o.dotStrips as Record<string, unknown>)) {
      const strip = (stripRaw ?? {}) as Record<string, unknown>;
      const sheet = sheetsByFm.get(fmId);
      const liveSpreadForFm = liveSpreadsRoot[fmId];
      const pushCell = (dim: string, causeId: string | undefined, votesRaw: unknown) => {
        if (!Array.isArray(votesRaw)) return;
        const votes = (votesRaw as Record<string, unknown>[]).map((v) => {
          if (typeof v.rationale === 'string' && v.rationale) return v;
          const found = rationaleByKey.get([fmId, causeId ?? '', dim, v.userId].join('|'));
          return found ? { ...v, rationale: found } : v;
        });
        const spread =
          nestedNum(liveSpreadForFm, causeId, dim) ??
          nestedNum(sheet?.spreads, causeId, dim) ??
          spreadFromVotes(votes);
        // X5: prefer the documented `sheet.methods` sibling; some already-persisted
        // rows on the shared dev DB instead nest per-dim methods inside
        // `sheet.finals.methods` — check that before falling back to the old flat
        // sheet-level method string.
        const method =
          nestedStr(sheet?.methods, causeId, dim) ??
          nestedStr(
            (sheet?.finals as Record<string, unknown> | undefined)?.methods,
            causeId,
            dim
          ) ??
          (typeof sheet?.method === 'string' ? (sheet.method as string) : undefined);
        cells.push({
          fmId,
          causeLinkId: causeId,
          dim,
          votes,
          spread,
          proposal: nestedNum(sheet?.finals, causeId, dim),
          method,
        });
      };
      for (const dim of ['S', 'L']) if (strip[dim]) pushCell(dim, undefined, strip[dim]);
      const causes = (strip.causes ?? {}) as Record<string, unknown>;
      for (const [causeId, dimsRaw] of Object.entries(causes)) {
        const dims = (dimsRaw ?? {}) as Record<string, unknown>;
        for (const dim of ['O', 'D', 'L']) if (dims[dim]) pushCell(dim, causeId, dims[dim]);
      }
    }
    out.reveal = cells;
  }
  // fms[].causes ← fms[].causeIds (live shape has ids only)
  if (Array.isArray(out.fms)) {
    out.fms = (out.fms as Record<string, unknown>[]).map((f) => {
      if (Array.isArray(f.causes) || !Array.isArray(f.causeIds)) return f;
      return {
        ...f,
        causes: (f.causeIds as string[]).map((cid, i) => ({
          causeLinkId: cid,
          rootCauseId: cid,
          label: `Cause ${i + 1}`,
        })),
      };
    });
  }
  return out;
}

/** GET /api/campaigns/:id → CampaignDetail */
export function adaptCampaignDetail(body: unknown): CampaignDetail | undefined {
  const raw = unwrap<Record<string, unknown>>(body);
  if (!raw || typeof raw !== 'object') return undefined;
  const o = normalizeCampaignDetailBody(raw);
  const id = pick<string>(o, 'id');
  if (!id) return undefined;

  const dimsRaw = pick<unknown>(o, 'scoreDims', 'dims');
  const scoreDims: ScoreDim[] =
    Array.isArray(dimsRaw) && dimsRaw.length
      ? dimsRaw.map((d) => normDim(String(d)))
      : ['S', 'O', 'D'];

  const fms: CampaignFm[] = (Array.isArray(o.fms) ? o.fms : []).map((raw, i) => {
    const f = raw as Record<string, unknown>;
    return {
      fmId: pick<string>(f, 'fmId', 'id') ?? `fm-${i}`,
      refId: pick<string>(f, 'refId', 'ref'),
      title: pick<string>(f, 'title', 'name', 'label') ?? '—',
      causes: adaptCauses(pick<unknown>(f, 'causes')),
    };
  });

  const myVotes: MyVote[] = (Array.isArray(o.myVotes) ? o.myVotes : []).map((raw) => {
    const m = raw as Record<string, unknown>;
    return {
      fmId: pick<string>(m, 'fmId') ?? '',
      causeLinkId: pick<string>(m, 'causeLinkId') ?? undefined,
      dim: normDim(pick<string>(m, 'dim')),
      value: toNum(pick<unknown>(m, 'value')),
      abstain: Boolean(pick<boolean>(m, 'abstain')),
      rationale: pick<string>(m, 'rationale') ?? undefined,
    };
  });

  const submissions: Submission[] = (Array.isArray(o.submissions) ? o.submissions : []).map(
    (raw) => {
      const s = raw as Record<string, unknown>;
      return {
        userId: pick<string>(s, 'userId', 'id') ?? '',
        submitted: Boolean(pick<boolean>(s, 'submitted')),
      };
    }
  );

  const reveal: RevealCell[] = (Array.isArray(o.reveal) ? o.reveal : []).map((raw) => {
    const r = raw as Record<string, unknown>;
    const votes: RevealVote[] = (Array.isArray(r.votes) ? r.votes : []).map((vr) => {
      const v = vr as Record<string, unknown>;
      return {
        userId: pick<string>(v, 'userId', 'id') ?? '',
        value: toNum(pick<unknown>(v, 'value')),
        abstain: Boolean(pick<boolean>(v, 'abstain')),
        rationale: pick<string>(v, 'rationale') ?? undefined,
      };
    });
    // X4a safety net: whatever shape `reveal` cells arrived in, never let an absent
    // spread field default to 0/calm when the votes actually disagree — recompute
    // from the votes themselves. `normalizeCampaignDetailBody` already does this for
    // the live dotStrips shape; this covers any other shape reveal cells arrive in
    // (fixtures, future backend revisions) with the same guarantee.
    const rawSpread = toNum(pick<unknown>(r, 'spread'));
    return {
      fmId: pick<string>(r, 'fmId') ?? '',
      causeLinkId: pick<string>(r, 'causeLinkId') ?? undefined,
      dim: normDim(pick<string>(r, 'dim')),
      votes,
      spread: rawSpread ?? spreadFromVotes(r.votes),
      proposal: toNum(pick<unknown>(r, 'proposal')),
      method: pick<string>(r, 'method') ?? undefined,
    };
  });

  const adaptFinals = (v: unknown): SheetFinal[] => {
    if (Array.isArray(v)) {
      return v.map((raw) => {
        const f = raw as Record<string, unknown>;
        return {
          dim: normDim(pick<string>(f, 'dim')),
          causeLinkId: pick<string>(f, 'causeLinkId') ?? undefined,
          value: toNum(pick<unknown>(f, 'value')),
        };
      });
    }
    // tolerate an object map: FM-level dims { S: 8 } + live nested { causes: { [causeId]: { O, D } } }
    if (v && typeof v === 'object') {
      const root = v as Record<string, unknown>;
      const out: SheetFinal[] = Object.entries(root)
        .filter(([k]) => ['S', 'O', 'D', 'L'].includes(k.toUpperCase()))
        .map(([k, val]) => ({ dim: normDim(k), value: toNum(val) }));
      if (root.causes && typeof root.causes === 'object') {
        for (const [causeId, dimsRaw] of Object.entries(root.causes as Record<string, unknown>)) {
          const dims = (dimsRaw ?? {}) as Record<string, unknown>;
          for (const [k, val] of Object.entries(dims)) {
            if (['O', 'D', 'L'].includes(k.toUpperCase()))
              out.push({ dim: normDim(k), causeLinkId: causeId, value: toNum(val) });
          }
        }
      }
      return out;
    }
    return [];
  };

  // X5 — per-dim method labels: same {S?, causes:{[causeId]:{O?,D?}}} nesting as
  // adaptFinals, but the leaf value is a method STRING ('MAX' | 'MEDIAN' |
  // 'DRIVER_PICK' | 'RULED'), not a score.
  const adaptMethods = (v: unknown): SheetMethod[] => {
    if (Array.isArray(v)) {
      return v.map((raw) => {
        const f = raw as Record<string, unknown>;
        return {
          dim: normDim(pick<string>(f, 'dim')),
          causeLinkId: pick<string>(f, 'causeLinkId') ?? undefined,
          method: pick<string>(f, 'method', 'value') ?? undefined,
        };
      });
    }
    if (v && typeof v === 'object') {
      const root = v as Record<string, unknown>;
      const out: SheetMethod[] = Object.entries(root)
        .filter(([k]) => ['S', 'O', 'D', 'L'].includes(k.toUpperCase()))
        .map(([k, val]) => ({
          dim: normDim(k),
          method: typeof val === 'string' ? val : undefined,
        }));
      if (root.causes && typeof root.causes === 'object') {
        for (const [causeId, dimsRaw] of Object.entries(root.causes as Record<string, unknown>)) {
          const dims = (dimsRaw ?? {}) as Record<string, unknown>;
          for (const [k, val] of Object.entries(dims)) {
            if (['O', 'D', 'L'].includes(k.toUpperCase()))
              out.push({
                dim: normDim(k),
                causeLinkId: causeId,
                method: typeof val === 'string' ? val : undefined,
              });
          }
        }
      }
      return out;
    }
    return [];
  };

  // Z14 — per-dim driver-pick rationale: only ever lives nested inside
  // `finals.rationales` (never a documented sibling like methods was pre-X5 —
  // this field is new, so there's no legacy shape to also read).
  const adaptRationales = (v: unknown): SheetRationale[] => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const root = v as Record<string, unknown>;
      const out: SheetRationale[] = [];
      if (typeof root.S === 'string') out.push({ dim: 'S', rationale: root.S });
      if (root.causes && typeof root.causes === 'object') {
        for (const [causeId, dimsRaw] of Object.entries(root.causes as Record<string, unknown>)) {
          const dims = (dimsRaw ?? {}) as Record<string, unknown>;
          for (const [k, val] of Object.entries(dims)) {
            if (['O', 'D', 'L'].includes(k.toUpperCase()) && typeof val === 'string')
              out.push({ dim: normDim(k), causeLinkId: causeId, rationale: val });
          }
        }
      }
      return out;
    }
    return [];
  };

  const sheets: CampaignSheet[] = (Array.isArray(o.sheets) ? o.sheets : []).map((raw) => {
    const s = raw as Record<string, unknown>;
    const dissents: SheetDissent[] = (Array.isArray(s.dissents) ? s.dissents : []).map((dr) => {
      const d = dr as Record<string, unknown>;
      return {
        userId: pick<string>(d, 'userId', 'id') ?? '',
        dim: normDim(pick<string>(d, 'dim')),
        causeLinkId: pick<string>(d, 'causeLinkId') ?? undefined,
        value: toNum(pick<unknown>(d, 'value')),
        rationale: pick<string>(d, 'rationale') ?? undefined,
      };
    });
    // X5 — per-dim methods have been observed live nested INSIDE `finals.methods`
    // (some already-persisted campaign rows on the shared dev DB predate the
    // documented `sheets.methods` sibling contract) as well as the documented
    // sibling location — read both, sibling taking precedence.
    const finalsRaw = pick<Record<string, unknown>>(s, 'finals');
    const methods = adaptMethods(pick<unknown>(s, 'methods'));
    const nestedMethods = methods.length ? [] : adaptMethods(finalsRaw?.methods);
    return {
      fmId: pick<string>(s, 'fmId') ?? '',
      finals: adaptFinals(pick<unknown>(s, 'finals')),
      outcome: normOutcome(pick<string>(s, 'outcome')),
      dissents,
      confirmedBy: strList(pick<unknown>(s, 'confirmedBy')),
      methods: methods.length ? methods : nestedMethods,
      legacyMethod: pick<string>(s, 'method') ?? undefined,
      rationales: adaptRationales(finalsRaw?.rationales),
    };
  });

  // P8 — round-over-round diff. Backend sends `roundDiff: null` for single-round
  // campaigns and while blind (see campaign.service.ts::computeRoundDiff) — read
  // defensively and leave the field undefined so the page can hide the section
  // on a simple truthiness check, same pattern as `dotStrips`/`liveSpreads`.
  const roundDiffRaw = pick<unknown>(o, 'roundDiff');
  let roundDiff: CampaignRoundDiff | undefined;
  if (roundDiffRaw && typeof roundDiffRaw === 'object') {
    const rd = roundDiffRaw as Record<string, unknown>;
    const fromRound = toNum(pick<unknown>(rd, 'fromRound'));
    const toRound = toNum(pick<unknown>(rd, 'toRound'));
    const byFmRaw = (rd.byFm ?? {}) as Record<string, unknown>;
    if (fromRound !== undefined && toRound !== undefined) {
      const byFm: Record<string, RoundDiffCell[]> = {};
      for (const [fmId, fmEntryRaw] of Object.entries(byFmRaw)) {
        const fmEntry = (fmEntryRaw ?? {}) as Record<string, unknown>;
        const cells: RoundDiffCell[] = [];
        const sVal = fmEntry.S as Record<string, unknown> | undefined;
        if (sVal && typeof sVal === 'object') {
          cells.push({ dim: 'S', from: toNum(sVal.from), to: toNum(sVal.to) });
        }
        const causes = (fmEntry.causes ?? {}) as Record<string, unknown>;
        for (const [causeId, dimsRaw] of Object.entries(causes)) {
          const dims = (dimsRaw ?? {}) as Record<string, unknown>;
          for (const dim of ['O', 'D', 'L']) {
            const cell = dims[dim] as Record<string, unknown> | undefined;
            if (cell && typeof cell === 'object') {
              cells.push({
                dim: normDim(dim),
                causeLinkId: causeId,
                from: toNum(cell.from),
                to: toNum(cell.to),
              });
            }
          }
        }
        if (cells.length) byFm[fmId] = cells;
      }
      roundDiff = { fromRound, toRound, byFm };
    }
  }

  return {
    id,
    refId: pick<string>(o, 'refId', 'ref') ?? id,
    status: normCampaignStatus(pick<string>(o, 'status')),
    scoreDims,
    fms,
    myVotes,
    submissions,
    reveal,
    sheets,
    driverUserId: pick<string>(o, 'driverUserId', 'driver') ?? undefined,
    participants: strList(pick<unknown>(o, 'participants')),
    deadline: pick<string>(o, 'deadline', 'dueAt') ?? undefined,
    scopeKind: pick<string>(o, 'scopeKind', 'scope') ?? undefined,
    driverName: pick<string>(o, 'driverName') ?? undefined,
    submittedCount:
      toNum(pick<unknown>(o, 'submittedCount', 'submitted')) ??
      (submissions.length ? submissions.filter((s) => s.submitted).length : undefined),
    roundDiff,
    effectiveQuorum: toNum(pick<unknown>(o, 'effectiveQuorum')),
    capableParticipantCount: toNum(pick<unknown>(o, 'capableParticipantCount')),
    quorumReachable:
      pick<unknown>(o, 'quorumReachable') === undefined
        ? undefined
        : Boolean(pick<boolean>(o, 'quorumReachable')),
  };
}

export async function fetchCampaigns(): Promise<Fetched<CampaignSummary[]>> {
  try {
    const body = await requestJson<unknown>(`/projects/${PROJECT_ID}/campaigns`);
    // An empty list is a real (fresh-project) state — only fetch FAILURE falls to fixture.
    return { data: adaptCampaigns(body), fromFixture: false };
  } catch (e) {
    // Bug fix (persona-audit finding, dangerous-fallback class) — this used to
    // fall back to FIXTURE_CAMPAIGNS unconditionally, in EVERY environment,
    // on ANY fetch failure including a 429/5xx. FIXTURE_CAMPAIGNS is a fully
    // realistic-looking, actionable list (real-looking refIds, driver names,
    // OPEN/REVEAL/IN_REVIEW statuses, submitted-of-N counts, deadlines) with
    // per-card "Reveal spread" / "Propose finals" / "Close campaign" driver
    // buttons — the only tell was a small FixtureBadge pill in the header. A
    // manager glancing at this in PROD on a transient API failure could
    // mistake it for the live scoring-campaign queue. Route through the same
    // RS5 fixtureOrHonestFail idiom every other Interlock surface uses: DEV
    // still demos off the fixture, PROD gets an honest empty list + `error`
    // so the page can render "couldn't load — retry" instead.
    return fixtureOrHonestFail(e, FIXTURE_CAMPAIGNS, []);
  }
}

/**
 * POST /api/projects/:id/campaigns — create a new blind-scoring round.
 *
 * The backend route (interlockCampaign.routes.ts) has always accepted this —
 * `requireCapability('CAMPAIGN_RUN')` was wired from day one — but nothing in
 * the frontend ever called it, so `/interlock/campaigns` could only ever show
 * its empty state ("Elevated FMs seed the first one" — aspirational copy
 * that never had a real seeding mechanism behind it). `driverUserId` is
 * intentionally omitted here — the
 * service defaults it to the acting user (campaign.service.ts's
 * `createCampaign`: "naming yourself, or leaving driverUserId unset ... needs
 * no extra check — that's just 'I am starting a campaign'"), which is the
 * sensible default for a manual create flow: whoever clicks "+ New campaign"
 * drives it.
 */
export interface CreateCampaignInput {
  scopeKind: CampaignScopeKind;
  fmIds: string[];
  participants: string[];
  quorum?: number;
  deadline?: string; // ISO date string
}

export async function createCampaign(
  input: CreateCampaignInput
): Promise<{ id: string; refId: string }> {
  const body = await requestJson<Record<string, unknown>>(`/projects/${PROJECT_ID}/campaigns`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return {
    id: String(pick<string>(body, 'id') ?? ''),
    refId: String(pick<string>(body, 'refId') ?? ''),
  };
}

/**
 * Result of fetching ONE campaign's detail. Deliberately NOT `Fetched<T>` — the
 * campaign LIST page may fall back to a fixture (a fresh/empty project is a real
 * state), but the DETAIL route never fakes a specific campaign: a fetch/parse
 * failure here means "this campaign is not really available", so the page must
 * show an explicit not-found/unreachable state instead of silently rendering
 * fixture data as if it were real.
 */
export interface CampaignDetailResult {
  data: CampaignDetail | undefined;
  /** Set (data is always undefined alongside it) when the fetch or parse failed. */
  error?: string;
}

export async function fetchCampaignDetail(id: string): Promise<CampaignDetailResult> {
  try {
    const body = await requestJson<unknown>(`/campaigns/${id}`);
    const data = adaptCampaignDetail(body);
    if (!data) return { data: undefined, error: 'Campaign response could not be parsed.' };
    return { data };
  } catch (e) {
    return {
      data: undefined,
      error: e instanceof Error ? e.message : 'Backend unreachable.',
    };
  }
}

export interface VoteInput {
  fmId: string;
  causeLinkId?: string;
  dim: ScoreDim;
  value?: number;
  abstain?: boolean;
  rationale?: string;
}

/** POST /api/campaigns/:id/votes — submit this actor's sheet (blind round). */
export async function submitCampaignVotes(id: string, votes: VoteInput[]): Promise<unknown> {
  return requestJson(`/campaigns/${id}/votes`, {
    method: 'POST',
    body: JSON.stringify({ votes }),
  });
}

/** POST /api/campaigns/:id/confirm — this actor's sheet-level handshake. */
export async function confirmCampaignSheets(id: string, fmIds?: string[]): Promise<unknown> {
  return requestJson(`/campaigns/${id}/confirm`, {
    method: 'POST',
    body: JSON.stringify(fmIds ? { fmIds } : {}),
  });
}

export type CampaignDriverAction = 'open' | 'reveal' | 'propose' | 'close';

/** Z14 — a driver-pick cell: a bare number (back-compat) or {value, rationale}
 * when the driver recorded why they overrode the crowd on this cell. */
export type DriverPickCell = number | { value: number; rationale?: string };

/**
 * POST /api/campaigns/:id/:action — driver-only lifecycle move. `propose` MAY carry
 * per-FM driver picks (spread 2–3 tier), nested `{fmId, S?, causes?}` — same shape
 * the backend's DriverPickSchema expects; sent defensively so a picks-unaware
 * backend still transitions to CONVERGING.
 */
export async function campaignDriverAction(
  id: string,
  action: CampaignDriverAction,
  picks?: {
    fmId: string;
    S?: DriverPickCell;
    causes?: Record<string, Partial<Record<'O' | 'D' | 'L', DriverPickCell>>>;
  }[]
): Promise<unknown> {
  return requestJson(`/campaigns/${id}/${action}`, {
    method: 'POST',
    body: JSON.stringify(picks && picks.length ? { picks } : {}),
  });
}

// Friendly labels + participant count helper shared by the pages.
export const SCOPE_LABEL: Record<string, string> = {
  INTERFACE_SWEEP: 'Interface sweep',
  FUNCTION_SWEEP: 'Function sweep',
  FM_SET: 'FM set',
  ELEVATED_ONLY: 'Elevated only',
};

// ============================================================================
// Campaign FIXTURES — design-intent stand-ins so every phase of the mechanic is
// visible before the backend parcel lands. Fallback only (FixtureBadge shows).
// Actors: shijin (driver) · alex · james — matches utils/interlockActor.ts.
// ============================================================================

export const FIXTURE_CAMPAIGNS: CampaignSummary[] = [
  {
    id: 'rrc-01',
    refId: 'RRC-01',
    status: 'OPEN',
    scopeKind: 'ELEVATED_ONLY',
    driverUserId: 'shijin',
    participants: ['shijin', 'alex', 'james'],
    deadline: '2026-07-14',
    fmCount: 3,
    submittedCount: 1,
  },
  {
    id: 'rrc-02',
    refId: 'RRC-02',
    status: 'REVEAL',
    scopeKind: 'INTERFACE_SWEEP',
    driverUserId: 'shijin',
    participants: ['shijin', 'alex', 'james'],
    deadline: '2026-07-12',
    fmCount: 2,
    submittedCount: 3,
  },
  {
    id: 'rrc-03',
    refId: 'RRC-03',
    status: 'IN_REVIEW',
    scopeKind: 'FM_SET',
    driverUserId: 'shijin',
    participants: ['shijin', 'alex', 'james'],
    deadline: '2026-07-10',
    fmCount: 2,
    submittedCount: 3,
  },
  {
    id: 'rrc-04',
    refId: 'RRC-04',
    status: 'CLOSED',
    scopeKind: 'FUNCTION_SWEEP',
    driverUserId: 'alex',
    participants: ['shijin', 'alex', 'james'],
    fmCount: 2,
    submittedCount: 3,
  },
];

// --- shared cause/fm scaffolds ------------------------------------------------
const FX_FMS_OPEN: CampaignFm[] = [
  {
    fmId: 'rsk-04',
    refId: 'RSK-04',
    title: 'Seed-chuck collision on insert',
    causes: [
      { causeLinkId: 'cl-04a', rootCauseId: 'rc-03', label: 'Bore position tolerance stack' },
      {
        causeLinkId: 'cl-04b',
        rootCauseId: 'rc-08',
        label: 'Chuck datum drift after thermal cycle',
      },
    ],
  },
  {
    fmId: 'rsk-03',
    refId: 'RSK-03',
    title: 'Wall scratch on traverse',
    causes: [{ causeLinkId: 'cl-03a', rootCauseId: 'rc-01', label: 'Excess wheel contact force' }],
  },
  {
    fmId: 'rsk-05',
    refId: 'RSK-05',
    title: 'Contamination / deposit on chamber wall',
    causes: [
      { causeLinkId: 'cl-05a', rootCauseId: 'rc-05', label: 'Deposit not fully removed' },
      { causeLinkId: 'cl-05b', rootCauseId: 'rc-12', label: 'Cleaning solution carry-over' },
    ],
  },
];

function fxVote(
  fmId: string,
  causeLinkId: string | undefined,
  dim: ScoreDim,
  value: number | undefined,
  abstain = false,
  rationale?: string
): MyVote {
  return { fmId, causeLinkId, dim, value, abstain, rationale };
}

export const FIXTURE_CAMPAIGN_DETAILS: Record<string, CampaignDetail> = {
  // ---- OPEN (blind) — RRC-01, shijin has scored one FM so far -----------------
  'rrc-01': {
    id: 'rrc-01',
    refId: 'RRC-01',
    status: 'OPEN',
    scoreDims: ['S', 'O', 'D'],
    scopeKind: 'ELEVATED_ONLY',
    driverUserId: 'shijin',
    participants: ['shijin', 'alex', 'james'],
    deadline: '2026-07-14',
    fms: FX_FMS_OPEN,
    myVotes: [
      fxVote(
        'rsk-04',
        undefined,
        'S',
        9,
        false,
        'Chuck collision damages the seed — mission-ending on that unit.'
      ),
      fxVote('rsk-04', 'cl-04a', 'O', 5),
      fxVote('rsk-04', 'cl-04a', 'D', 4),
      fxVote('rsk-04', 'cl-04b', 'O', undefined, true),
      fxVote('rsk-04', 'cl-04b', 'D', 6),
    ],
    submissions: [
      { userId: 'shijin', submitted: false },
      { userId: 'alex', submitted: true },
      { userId: 'james', submitted: false },
    ],
    reveal: [],
    sheets: [],
  },

  // ---- REVEAL — RRC-02, dot-strips + proposals across the tiers ---------------
  'rrc-02': {
    id: 'rrc-02',
    refId: 'RRC-02',
    status: 'REVEAL',
    scoreDims: ['S', 'O', 'D'],
    scopeKind: 'INTERFACE_SWEEP',
    driverUserId: 'shijin',
    participants: ['shijin', 'alex', 'james'],
    deadline: '2026-07-12',
    fms: [
      {
        fmId: 'rsk-04',
        refId: 'RSK-04',
        title: 'Seed-chuck collision on insert',
        causes: [
          { causeLinkId: 'cl-04a', rootCauseId: 'rc-03', label: 'Bore position tolerance stack' },
        ],
      },
      {
        fmId: 'rsk-02',
        refId: 'RSK-02',
        title: 'Tether entanglement in chamber',
        causes: [
          { causeLinkId: 'cl-02a', rootCauseId: 'rc-04', label: 'Tether payout servo fault' },
        ],
      },
    ],
    myVotes: [],
    submissions: [
      { userId: 'shijin', submitted: true },
      { userId: 'alex', submitted: true },
      { userId: 'james', submitted: true },
    ],
    reveal: [
      // RSK-04 S — tight (calm), S proposes MAX = worst credible voice
      {
        fmId: 'rsk-04',
        dim: 'S',
        spread: 1,
        proposal: 9,
        method: 'MAX',
        votes: [
          { userId: 'shijin', value: 9, abstain: false },
          { userId: 'alex', value: 8, abstain: false },
          { userId: 'james', value: 9, abstain: false },
        ],
      },
      // RSK-04 O — divergent (≥4), thread hint
      {
        fmId: 'rsk-04',
        causeLinkId: 'cl-04a',
        dim: 'O',
        spread: 5,
        proposal: 5,
        method: 'AUTO_MEDIAN',
        votes: [
          { userId: 'shijin', value: 5, abstain: false },
          {
            userId: 'alex',
            value: 3,
            abstain: false,
            rationale: 'Tolerance stack is tighter after the Rev-2 jig fix.',
          },
          {
            userId: 'james',
            value: 8,
            abstain: false,
            rationale: 'Saw this fail twice on PRO-271 — occurrence is real.',
          },
        ],
      },
      // RSK-04 D — mid spread (2–3), driver picks in range
      {
        fmId: 'rsk-04',
        causeLinkId: 'cl-04a',
        dim: 'D',
        spread: 2,
        proposal: 4,
        method: 'AUTO_MEDIAN',
        votes: [
          { userId: 'shijin', value: 4, abstain: false },
          { userId: 'alex', value: 5, abstain: false },
          { userId: 'james', value: undefined, abstain: true },
        ],
      },
      // RSK-02 S — calm
      {
        fmId: 'rsk-02',
        dim: 'S',
        spread: 1,
        proposal: 8,
        method: 'MAX',
        votes: [
          { userId: 'shijin', value: 8, abstain: false },
          { userId: 'alex', value: 7, abstain: false },
          { userId: 'james', value: 8, abstain: false },
        ],
      },
      {
        fmId: 'rsk-02',
        causeLinkId: 'cl-02a',
        dim: 'O',
        spread: 3,
        proposal: 4,
        method: 'AUTO_MEDIAN',
        votes: [
          { userId: 'shijin', value: 4, abstain: false },
          { userId: 'alex', value: 6, abstain: false },
          { userId: 'james', value: 3, abstain: false },
        ],
      },
      {
        fmId: 'rsk-02',
        causeLinkId: 'cl-02a',
        dim: 'D',
        spread: 1,
        proposal: 6,
        method: 'AUTO_MEDIAN',
        votes: [
          { userId: 'shijin', value: 6, abstain: false },
          { userId: 'alex', value: 6, abstain: false },
          { userId: 'james', value: 7, abstain: false },
        ],
      },
    ],
    sheets: [],
  },

  // ---- IN_REVIEW — RRC-03, sheet summary + confirm, dissent preserved ---------
  'rrc-03': {
    id: 'rrc-03',
    refId: 'RRC-03',
    status: 'IN_REVIEW',
    scoreDims: ['S', 'O', 'D'],
    scopeKind: 'FM_SET',
    driverUserId: 'shijin',
    participants: ['shijin', 'alex', 'james'],
    deadline: '2026-07-10',
    fms: [
      {
        fmId: 'rsk-04',
        refId: 'RSK-04',
        title: 'Seed-chuck collision on insert',
        causes: [
          { causeLinkId: 'cl-04a', rootCauseId: 'rc-03', label: 'Bore position tolerance stack' },
        ],
      },
      {
        fmId: 'rsk-05',
        refId: 'RSK-05',
        title: 'Contamination / deposit on chamber wall',
        causes: [
          { causeLinkId: 'cl-05a', rootCauseId: 'rc-05', label: 'Deposit not fully removed' },
        ],
      },
    ],
    myVotes: [],
    submissions: [
      { userId: 'shijin', submitted: true },
      { userId: 'alex', submitted: true },
      { userId: 'james', submitted: true },
    ],
    reveal: [],
    sheets: [
      {
        fmId: 'rsk-04',
        outcome: 'CONVERGED_WITH_DISSENT',
        confirmedBy: ['alex'],
        finals: [
          { dim: 'S', value: 9 },
          { dim: 'O', causeLinkId: 'cl-04a', value: 5 },
          { dim: 'D', causeLinkId: 'cl-04a', value: 4 },
        ],
        dissents: [
          {
            userId: 'james',
            dim: 'O',
            causeLinkId: 'cl-04a',
            value: 8,
            rationale: 'Saw this fail twice on PRO-271 — occurrence is a 7 or 8, not 5.',
          },
        ],
        methods: [],
        rationales: [],
      },
      {
        fmId: 'rsk-05',
        outcome: 'CONVERGED_UNANIMOUS',
        confirmedBy: ['alex', 'james'],
        finals: [
          { dim: 'S', value: 6 },
          { dim: 'O', causeLinkId: 'cl-05a', value: 4 },
          { dim: 'D', causeLinkId: 'cl-05a', value: 3 },
        ],
        dissents: [],
        methods: [],
        rationales: [],
      },
    ],
  },

  // ---- CLOSED — RRC-04, finals + outcome chips --------------------------------
  'rrc-04': {
    id: 'rrc-04',
    refId: 'RRC-04',
    status: 'CLOSED',
    scoreDims: ['S', 'O', 'D'],
    scopeKind: 'FUNCTION_SWEEP',
    driverUserId: 'alex',
    participants: ['shijin', 'alex', 'james'],
    fms: [
      {
        fmId: 'rsk-09',
        refId: 'RSK-09',
        title: 'Comms / e-stop dropout',
        causes: [
          { causeLinkId: 'cl-09a', rootCauseId: 'rc-07', label: 'Wireless link congestion' },
        ],
      },
      {
        fmId: 'rsk-08',
        refId: 'RSK-08',
        title: 'Ingress → electronics',
        causes: [
          { causeLinkId: 'cl-08a', rootCauseId: 'rc-06', label: 'Seal degradation over cycles' },
        ],
      },
    ],
    myVotes: [],
    submissions: [
      { userId: 'shijin', submitted: true },
      { userId: 'alex', submitted: true },
      { userId: 'james', submitted: true },
    ],
    reveal: [],
    sheets: [
      {
        fmId: 'rsk-09',
        outcome: 'CONVERGED_WITH_DISSENT',
        confirmedBy: ['shijin', 'alex', 'james'],
        finals: [
          { dim: 'S', value: 8 },
          { dim: 'O', causeLinkId: 'cl-09a', value: 5 },
          { dim: 'D', causeLinkId: 'cl-09a', value: 4 },
        ],
        dissents: [
          {
            userId: 'shijin',
            dim: 'D',
            causeLinkId: 'cl-09a',
            value: 6,
            rationale:
              'FAT will not reliably catch intermittent congestion — detection is weaker than 4.',
          },
        ],
        methods: [],
        rationales: [],
      },
      {
        fmId: 'rsk-08',
        outcome: 'CONVERGED_UNANIMOUS',
        confirmedBy: ['shijin', 'alex', 'james'],
        finals: [
          { dim: 'S', value: 7 },
          { dim: 'O', causeLinkId: 'cl-08a', value: 3 },
          { dim: 'D', causeLinkId: 'cl-08a', value: 3 },
        ],
        dissents: [],
        methods: [],
        rationales: [],
      },
    ],
  },
};
// ============================================================================
// System View (SV parcel) — the read-only React Flow nested canvas that replaces
// the SVG topology map. Backend built in a parallel parcel (SV-A); code EXACTLY
// against this contract and degrade to a fixture when it 404s.
//
//   GET /api/projects/:id/system-view →
//     { nodes: [{ id, name, type: 'SYSTEM'|'SUBSYSTEM'|'COMPONENT'|'PART', parentId, x, y,
//                 discipline, fmRollup: { count, worstBand }, elevated }],
//       connections: [{ id, sourceId, targetId, connectionType, label, customLabel, edgeColor,
//                        sourceHandle, targetHandle,
//                        interface: null | { refId, register, status, elevated, openTrades,
//                                            whoseTurn, gatedDrRefs: string[] } }] }
// ============================================================================

export type SystemViewNodeType = 'SYSTEM' | 'SUBSYSTEM' | 'COMPONENT' | 'PART';
export type SystemViewWorstBand = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
/** Top-level discipline bucket — matches block-diagram's ConnectionType categories. */
export type SystemViewDiscipline = 'mechanical' | 'electrical' | 'software' | null;
/** INTERLOCK systemClass wave — Component.systemClass. SYSTEM (the delivered
 * robot) | SUPPORT (test/verification tooling, e.g. the sim rig) | EXTERNAL
 * (client-side counterparty systems, e.g. QCells). The System View renders
 * SYSTEM blocks only; SUPPORT/EXTERNAL neighbours surface as a boundary chip
 * on their SYSTEM-side counterpart instead of disappearing silently. */
export type SystemViewSystemClass = 'SYSTEM' | 'SUPPORT' | 'EXTERNAL';

export interface SystemViewFmRollup {
  count: number;
  worstBand: SystemViewWorstBand;
}

export interface SystemViewNode {
  id: string;
  name: string;
  type: SystemViewNodeType;
  parentId: string | null;
  x: number;
  y: number;
  discipline: SystemViewDiscipline;
  fmRollup: SystemViewFmRollup;
  elevated: boolean;
  /** sysview-wave3 — manually-curated top-level flag (Component.isMajorComponent).
   * The root-level System View filters to these; curation is manual by design. */
  isMajorComponent: boolean;
  /** INTERLOCK systemClass wave — real backend classification (Component.
   * systemClass), NOT the old name-matching `org` guess. Defaults to SYSTEM
   * so an older backend without the field renders exactly as before. */
  systemClass: SystemViewSystemClass;
  /** Component-thumbnails parcel — curated thumbnail download URL (legacy
   * Component.thumbnailId → Attachment pipeline). Optional so an older backend
   * without the field simply renders no thumbnail. */
  thumbnailUrl?: string | null;
}

export interface SystemViewInterfaceRef {
  refId: string;
  register: InterfaceRegister;
  status: InterfaceStatus;
  elevated: boolean;
  openTrades: number;
  whoseTurn: string | null;
  gatedDrRefs: string[];
  // Campaign-2 Theme-2 Wave-3 — same signal/derivation as InterfaceRow.hasRejection;
  // optional so an older backend without the field just doesn't render the badge.
  hasRejection?: boolean;
  // S5 (surfaces parcel) — true if any of this interface's trades is ESCALATED.
  // Optional so an older backend without the field just doesn't paint the branch.
  escalated?: boolean;
}

export interface SystemViewConnection {
  id: string;
  sourceId: string;
  targetId: string;
  connectionType: string; // 'mechanical' | 'electrical' | 'software' (discipline bucket)
  label: string | null;
  customLabel: string | null;
  edgeColor: string | null;
  sourceHandle: string | null;
  targetHandle: string | null;
  interface: SystemViewInterfaceRef | null;
}

export interface SystemViewModel {
  nodes: SystemViewNode[];
  connections: SystemViewConnection[];
  // Campaign-2 Theme-2 Wave-4 — interfaces the diagram silently hides (null/dangling
  // diagramConnectionId) and an honest count of the inverse drift (edges with no
  // claiming interface). Defaulted so an older backend without the fields still works.
  offDiagramInterfaces: SystemViewInterfaceRef[];
  interfacelessConnectionCount: number;
}

function normWorstBand(s: string | undefined | null): SystemViewWorstBand {
  const v = (s ?? '').toUpperCase();
  if (v === 'CRITICAL' || v === 'HIGH' || v === 'MEDIUM' || v === 'LOW') return v;
  return null;
}

function normNodeType(s: string | undefined): SystemViewNodeType {
  const v = (s ?? '').toUpperCase();
  if (v === 'SYSTEM' || v === 'SUBSYSTEM' || v === 'COMPONENT' || v === 'PART') return v;
  return 'COMPONENT';
}

/** INTERLOCK systemClass wave — defaults to SYSTEM so a pre-wave backend (or
 * fixture row) that omits the field renders exactly as before this change. */
function normSystemClass(s: string | undefined): SystemViewSystemClass {
  const v = (s ?? '').toUpperCase();
  if (v === 'SUPPORT' || v === 'EXTERNAL') return v;
  return 'SYSTEM';
}

function adaptSystemViewInterfaceRef(ifaceRaw: Record<string, unknown>): SystemViewInterfaceRef {
  return {
    refId: pick<string>(ifaceRaw, 'refId', 'ref') ?? '—',
    register: (pick<string>(ifaceRaw, 'register') ?? 'int').toLowerCase() === 'ext' ? 'ext' : 'int',
    status: normStatus(pick<string>(ifaceRaw, 'status')),
    elevated: Boolean(pick<boolean>(ifaceRaw, 'elevated')),
    openTrades: pick<number>(ifaceRaw, 'openTrades') ?? 0,
    whoseTurn: pick<string>(ifaceRaw, 'whoseTurn') ?? null,
    gatedDrRefs: (() => {
      const g = pick<unknown>(ifaceRaw, 'gatedDrRefs');
      return Array.isArray(g) ? g.filter((x): x is string => typeof x === 'string') : [];
    })(),
    hasRejection: Boolean(pick<boolean>(ifaceRaw, 'hasRejection')),
    escalated: Boolean(pick<boolean>(ifaceRaw, 'escalated')),
  };
}

/** GET /api/projects/:id/system-view → SystemViewModel */
export function adaptSystemView(body: unknown): SystemViewModel {
  const d = unwrap<Record<string, unknown>>(body) ?? {};
  const rawNodes = (Array.isArray(d?.nodes) ? d.nodes : []) as Record<string, unknown>[];
  const rawConns = (Array.isArray(d?.connections) ? d.connections : []) as Record<
    string,
    unknown
  >[];
  const rawOffDiagram = (
    Array.isArray(d?.offDiagramInterfaces) ? d.offDiagramInterfaces : []
  ) as Record<string, unknown>[];

  const nodes: SystemViewNode[] = rawNodes.map((o, i) => {
    const rollupRaw = pick<Record<string, unknown>>(o, 'fmRollup', 'fmSummary');
    return {
      id: pick<string>(o, 'id') ?? `n${i}`,
      name: pick<string>(o, 'name', 'componentName') ?? `Node ${i}`,
      type: normNodeType(pick<string>(o, 'type', 'componentType')),
      parentId: pick<string>(o, 'parentId', 'parent') ?? null,
      x: pick<number>(o, 'x') ?? 0,
      y: pick<number>(o, 'y') ?? 0,
      discipline: (pick<string>(o, 'discipline') as SystemViewDiscipline) ?? null,
      fmRollup: {
        count: pick<number>(rollupRaw ?? {}, 'count') ?? 0,
        worstBand: normWorstBand(pick<string>(rollupRaw ?? {}, 'worstBand')),
      },
      elevated: Boolean(pick<boolean>(o, 'elevated')),
      isMajorComponent: Boolean(pick<boolean>(o, 'isMajorComponent')),
      systemClass: normSystemClass(pick<string>(o, 'systemClass')),
      // Component-thumbnails parcel — absent on older backends → null (no thumb).
      thumbnailUrl: pick<string>(o, 'thumbnailUrl') ?? null,
    };
  });

  const connections: SystemViewConnection[] = rawConns.map((o, i) => {
    const ifaceRaw = pick<Record<string, unknown>>(o, 'interface');
    const iface: SystemViewInterfaceRef | null = ifaceRaw
      ? adaptSystemViewInterfaceRef(ifaceRaw)
      : null;
    return {
      id: pick<string>(o, 'id') ?? `c${i}`,
      sourceId: pick<string>(o, 'sourceId', 'source') ?? '',
      targetId: pick<string>(o, 'targetId', 'target') ?? '',
      connectionType: pick<string>(o, 'connectionType', 'type') ?? 'mechanical',
      label: pick<string>(o, 'label') ?? null,
      customLabel: pick<string>(o, 'customLabel') ?? null,
      edgeColor: pick<string>(o, 'edgeColor') ?? null,
      sourceHandle: pick<string>(o, 'sourceHandle') ?? null,
      targetHandle: pick<string>(o, 'targetHandle') ?? null,
      interface: iface,
    };
  });

  const offDiagramInterfaces: SystemViewInterfaceRef[] = rawOffDiagram.map(
    adaptSystemViewInterfaceRef
  );
  const interfacelessConnectionCount = pick<number>(d, 'interfacelessConnectionCount') ?? 0;

  return { nodes, connections, offDiagramInterfaces, interfacelessConnectionCount };
}

export async function fetchSystemView(): Promise<Fetched<SystemViewModel>> {
  try {
    const body = await getJson(`/projects/${PROJECT_ID}/system-view`);
    const data = adaptSystemView(body);
    // RS4 — no nodes yet is a real (fresh-project) state.
    return { data, fromFixture: false };
  } catch (e) {
    return fixtureOrHonestFail(e, FIXTURE_SYSTEM_VIEW, {
      nodes: [],
      connections: [],
      offDiagramInterfaces: [],
      interfacelessConnectionCount: 0,
    });
  }
}

/**
 * ~8-node fixture: one SYSTEM root, two SUBSYSTEM containers with children, a handful
 * of connections including interface chips (agreement/consequence/trade flavours) so the
 * canvas demos meaningfully before the SV-A backend parcel merges.
 */
export const FIXTURE_SYSTEM_VIEW: SystemViewModel = {
  nodes: [
    {
      id: 'sys-robot',
      name: 'Pull-chamber robot',
      type: 'SYSTEM',
      parentId: null,
      x: 40,
      y: 40,
      discipline: null,
      fmRollup: { count: 0, worstBand: null },
      elevated: false,
      isMajorComponent: false,
      systemClass: 'SYSTEM',
    },
    {
      id: 'sub-ring',
      name: 'Ring chassis',
      type: 'SUBSYSTEM',
      parentId: 'sys-robot',
      x: 80,
      y: 100,
      discipline: 'mechanical',
      fmRollup: { count: 6, worstBand: 'CRITICAL' },
      elevated: true,
      isMajorComponent: false,
      systemClass: 'SYSTEM',
    },
    {
      id: 'cmp-rotator',
      name: 'Rotator drive',
      type: 'COMPONENT',
      parentId: 'sub-ring',
      x: 40,
      y: 60,
      discipline: 'mechanical',
      fmRollup: { count: 2, worstBand: 'HIGH' },
      elevated: false,
      isMajorComponent: false,
      systemClass: 'SYSTEM',
    },
    {
      id: 'cmp-tether',
      name: 'Tether reel',
      type: 'COMPONENT',
      parentId: 'sub-ring',
      x: 260,
      y: 60,
      discipline: 'mechanical',
      fmRollup: { count: 1, worstBand: 'MEDIUM' },
      elevated: false,
      isMajorComponent: false,
      systemClass: 'SYSTEM',
    },
    {
      id: 'sub-juice',
      name: 'JuiceBox + ARC',
      type: 'SUBSYSTEM',
      parentId: 'sys-robot',
      x: 80,
      y: 400,
      discipline: 'electrical',
      fmRollup: { count: 3, worstBand: 'HIGH' },
      elevated: false,
      isMajorComponent: false,
      systemClass: 'SYSTEM',
    },
    {
      id: 'part-arc',
      name: 'ARC board',
      type: 'PART',
      parentId: 'sub-juice',
      x: 40,
      y: 60,
      discipline: 'electrical',
      fmRollup: { count: 1, worstBand: 'MEDIUM' },
      elevated: false,
      isMajorComponent: false,
      systemClass: 'SYSTEM',
    },
    {
      id: 'cmp-chamber',
      name: 'Pull chamber',
      type: 'COMPONENT',
      parentId: null,
      x: 700,
      y: 140,
      discipline: 'mechanical',
      fmRollup: { count: 4, worstBand: 'CRITICAL' },
      elevated: true,
      isMajorComponent: false,
      systemClass: 'EXTERNAL',
    },
    {
      id: 'cmp-seed',
      name: 'Seed chuck',
      type: 'COMPONENT',
      parentId: null,
      x: 700,
      y: 420,
      discipline: null,
      fmRollup: { count: 0, worstBand: 'LOW' },
      elevated: false,
      isMajorComponent: false,
      systemClass: 'EXTERNAL',
    },
  ],
  connections: [
    {
      id: 'conn-1',
      sourceId: 'cmp-rotator',
      targetId: 'sub-juice',
      connectionType: 'electrical',
      label: '24V DC',
      customLabel: null,
      edgeColor: null,
      sourceHandle: null,
      targetHandle: null,
      interface: {
        refId: 'INT-03',
        register: 'int',
        status: 'open',
        elevated: false,
        openTrades: 1,
        whoseTurn: 'Priya',
        gatedDrRefs: [],
      },
    },
    {
      id: 'conn-2',
      sourceId: 'sub-ring',
      targetId: 'cmp-chamber',
      connectionType: 'mechanical',
      label: 'Contact',
      customLabel: 'EXT-09 — Mat/mech',
      edgeColor: null,
      sourceHandle: null,
      targetHandle: null,
      interface: {
        refId: 'EXT-09',
        register: 'ext',
        status: 'open',
        elevated: true,
        openTrades: 2,
        whoseTurn: "Alex's turn",
        gatedDrRefs: ['DR-13'],
      },
    },
    {
      id: 'conn-3',
      sourceId: 'sub-ring',
      targetId: 'cmp-seed',
      connectionType: 'mechanical',
      label: 'Clearance',
      customLabel: 'EXT-02',
      edgeColor: null,
      sourceHandle: null,
      targetHandle: null,
      interface: {
        refId: 'EXT-02',
        register: 'ext',
        status: 'open',
        elevated: true,
        openTrades: 0,
        whoseTurn: null,
        gatedDrRefs: ['DR-02'],
      },
    },
    {
      id: 'conn-4',
      sourceId: 'cmp-rotator',
      targetId: 'cmp-tether',
      connectionType: 'mechanical',
      label: null,
      customLabel: null,
      edgeColor: null,
      sourceHandle: null,
      targetHandle: null,
      interface: null,
    },
    {
      id: 'conn-5',
      sourceId: 'sub-juice',
      targetId: 'part-arc',
      connectionType: 'software',
      label: 'RS485',
      customLabel: null,
      edgeColor: null,
      sourceHandle: null,
      targetHandle: null,
      interface: {
        refId: 'INT-07',
        register: 'int',
        status: 'std',
        elevated: false,
        openTrades: 0,
        whoseTurn: null,
        gatedDrRefs: [],
      },
    },
  ],
  // Campaign-2 Theme-2 Wave-4 — a demo off-diagram interface so the fixture path
  // exercises the new panel too.
  offDiagramInterfaces: [
    {
      refId: 'EXT-13',
      register: 'ext',
      status: 'open',
      elevated: false,
      openTrades: 0,
      whoseTurn: null,
      gatedDrRefs: [],
      hasRejection: false,
    },
  ],
  interfacelessConnectionCount: 0,
};

// ============================================================================
// L2 additions (interlock/fix-l2 — Wave 3 lead views). Kept in one block at the
// end of the file per the file-ownership contract (L1 also appends here — do not
// interleave, this section is L2's only).
// ============================================================================

/**
 * Extract candidate refIds (EXT-09, INT-05, DR-01, EXT-09-T1, RSK-03, SR-04 …) from a
 * gate predicate's prose `detail` string. gate.service.ts predicates are consistently
 * formatted ("… not converged: EXT-09-T1 (NEGOTIATING)." / "… gating live trades:
 * DR-01, DR-13.") so a plain word-boundary scan is reliable; a trade refId that carries
 * a "REC/" prefix (e.g. "REC/INT-10-T1") is matched from "INT-10-T1" onward, which is
 * fine — resolveTradeId() already tolerates a stripped REC/ prefix. Dedupes, preserves
 * first-seen order. Used by GatePage (deep links) and nowhere else (v1).
 */
export function extractRefIds(detail: string): string[] {
  const matches = detail.match(/\b(?:EXT|INT|DR|RSK|SR)-\d+(?:-T\d+)?\b/g) ?? [];
  return [...new Set(matches)];
}

/** Comma-join for building `?highlight=A,B,C` query values. */
export function refIdsToHighlightParam(refIds: string[]): string {
  return refIds.join(',');
}

// ============================================================================
// Y6 (round 4) — SPA-nav unsaved-changes guard.
//
// CampaignDetailPage already guards `beforeunload` (tab close/refresh) and the
// browser back button (popstate) for its blind-round drafts (see the X9d effects
// there) — but neither of those fires for an in-app `navigate()` / `<Link>` click,
// which is exactly how someone leaves via the NavRail while mid-sheet. Rather than
// having NavRail import CampaignDetailPage's local state, a tiny module-level
// registry: any page with unsaved work registers a guard function while dirty;
// NavRail (or any other SPA-nav trigger) asks the registry before navigating.
//
// Not a React hook by itself (this module has no React dependency and other
// consumers here are plain fetch helpers) — `useDirtyGuard` below is the thin
// hook wrapper a page calls; it just registers/unregisters a callback here.
// ============================================================================

type DirtyGuardFn = () => string | null; // a confirm() message, or null when not dirty

const dirtyGuards = new Map<string, DirtyGuardFn>();

/** Register (or replace) a named dirty-check. Returns an unregister fn. Safe to call
 * every render — re-registering under the same key just replaces the callback. */
export function registerDirtyGuard(key: string, fn: DirtyGuardFn): () => void {
  dirtyGuards.set(key, fn);
  return () => {
    // Only clear if this registration is still the current one (a fast unmount/
    // remount pair shouldn't clobber a newer registration under the same key).
    if (dirtyGuards.get(key) === fn) dirtyGuards.delete(key);
  };
}

/**
 * Ask every registered guard whether an in-app navigation is safe right now.
 * Returns true (proceed) when nothing is dirty, or the user confirms the first
 * dirty guard's prompt; false (stay put) if they cancel. Deliberately checks only
 * the first dirty guard found — in practice at most one page is dirty at a time.
 */
export function confirmSpaNavigation(): boolean {
  for (const fn of dirtyGuards.values()) {
    const msg = fn();
    if (msg) return window.confirm(msg);
  }
  return true;
}

// ============================================================================
// Phase 3 — cross-interface blast radius (read-only). Backend: blastRadius.
// service.ts / GET /api/blast-radius?projectId=&kind=&id=&riskKind=&maxHops=.
// NOT wrapped in the `{data}` envelope (raw top-level JSON) — fetched directly
// rather than through getJson/unwrap.
//
// `id` semantics per kind (mirrors resolveStart() in the backend service):
//   INTERFACE   -> the interface's DB id (InterfaceDetail.id / InterfaceRow.id)
//   TRADE       -> the trade's DB id (TradeDetail.id) — NOT the refId
//   RISK        -> RiskDetail.externalRiskId (riskKind EXTERNAL_RISK) or
//                  RiskDetail.causeEffectId (riskKind FAILURE_MODE)
//   REQUIREMENT -> the requirement's DB id (Requirement.id); hop-1 = the
//                  interfaces its InterfaceRequirementLinks touch. Backend
//                  parcel addition (persona-audit fix wave) — response shape
//                  is otherwise unchanged.
// ============================================================================

export type BlastRadiusStartKind = 'INTERFACE' | 'TRADE' | 'RISK' | 'REQUIREMENT';

export interface BlastRadiusReason {
  explain: string;
  path: string[];
}

export interface BlastRadiusTradeSummary {
  id: string;
  refId: string;
  state: string;
}

/** Persona-audit fix wave — one "why is this elevated" receipt, mirroring
 * {@link ElevationReason} elsewhere (ElevationBadge's popover). Present on an
 * affected item only when `elevated` is true; a backend that hasn't shipped
 * this field yet simply omits it, so `?? []` keeps the badge honest instead
 * of rendering a receipts popover with nothing in it. */
export interface BlastRadiusElevationReason {
  explain: string;
  path: string[];
}

export interface BlastRadiusAffectedItem {
  kind: 'INTERFACE' | 'TRADE';
  id: string;
  refId: string;
  label: string;
  hopDistance: number;
  viaComponents: string[];
  reasons: BlastRadiusReason[];
  trades?: BlastRadiusTradeSummary[];
  elevated?: boolean;
  elevationReasons?: BlastRadiusElevationReason[];
}

export interface BlastRadiusResult {
  start: { kind: BlastRadiusStartKind; id: string; refId: string; label: string };
  hops: number;
  affected: BlastRadiusAffectedItem[];
  truncated?: boolean;
}

/**
 * GET /api/blast-radius. Read-only derivation — no adapter needed, the backend
 * shape IS the view-model (see blastRadius.service.ts's exported types, mirrored
 * 1:1 above). Throws InterlockApiError on a non-2xx response (same contract as
 * requestJson) so callers (useBlastRadius) can distinguish "not found" / "bad
 * params" from a genuine outage rather than silently swallowing it.
 */
export async function fetchBlastRadius(opts: {
  kind: BlastRadiusStartKind;
  id: string;
  riskKind?: 'EXTERNAL_RISK' | 'FAILURE_MODE';
  maxHops?: number;
  projectId?: string;
  /** REQUIREMENT-kind only — W2-F: mirrors `/:id/verifications`'s L8 remedy so
   * a retired requirement's "bound to trades" chip resolves instead of
   * 404ing. No-op for every other kind (see resolveStart in blastRadius
   * .service.ts). */
  includeRetired?: boolean;
}): Promise<BlastRadiusResult> {
  const params = new URLSearchParams({
    projectId: opts.projectId ?? PROJECT_ID,
    kind: opts.kind,
    id: opts.id,
  });
  if (opts.riskKind) params.set('riskKind', opts.riskKind);
  if (opts.maxHops) params.set('maxHops', String(opts.maxHops));
  if (opts.includeRetired) params.set('includeRetired', '1');
  return requestJson<BlastRadiusResult>(`/blast-radius?${params.toString()}`, { method: 'GET' });
}
